import React, { useEffect, useState, useRef } from 'react';
import { AlertCircle, Download, Send, Plus, Check, X, RotateCcw, Upload, ChevronLeft, ChevronRight, CopyPlus, Save } from 'lucide-react';
import * as productLineHelpers from './productLineHelpers';
import { createEmptySpeciesState, createSpeciesHelpers, isDetailedSpeciesMode } from './speciesHelpers';
import { buildSubmitPayload as buildSubmitPayloadHelper } from './submitPayloadHelpers';
import {
  createSampleInfoContext,
  buildSampleInfoPreviewModel,
  getAnalysisSampleCountConsistencyError,
  getAnalysisSampleSheetLiteQuantity,
  getChIPseqSubmittedSampleCount,
  getServiceQuantityUnit,
  isAnalysisOnlyOrder as isAnalysisOnlySampleOrder,
  isGeneralNoSampleOrder as isGeneralNoSampleOrderCheck,
  normalizeNoSampleOrderData,
  requiresAnalysisSampleSheetLite
} from './sampleInfoHelpers';
import {
  createDefaultOrderFormData,
  normalizeRestoredOrderData
} from './orderRestoreHelpers';
import { hasMeaningfulServiceItems } from './serviceItemModeHelpers';
import {
  CHIPSEQ_ROW_ROLE_LABELS,
  CHIPSEQ_SAMPLE_SHEET_HINTS,
  CHIPSEQ_SAMPLE_SOURCE_MAX_LENGTH,
  sanitizeChIPseqSampleSource,
  CHIPSEQ_SPECIES_NOTICE,
  getChIPseqPipelineInfo,
  getChIPseqRowRole,
  getChIPseqSampleGroupNo,
  isChIPseqSampleNameRequired,
  isChIPseqServiceConfig
} from './chipSeqHelpers';
import {
  ANALYSIS_PARAMETER_CUSTOM_OPTION_VALUE,
  applyAnalysisParameterChange,
  buildAnalysisParameterSummary,
  deriveAnalysisParameterCustomModes,
  getAnalysisParameterDefinitions,
  getAnalysisParameterErrors,
  getAnalysisParameterSelectValue,
  getAnalysisParameterSuggestionWarnings,
  resolveAnalysisParameterFormValues
} from './analysisParameterHelpers';
import { getRNAseqPipelineInfo } from './rnaSeqPipelineHelpers';
import { getScRNAseqPipelineInfo } from './ScRnaSeqPipelineHelpers';
import {
  buildScRnaExperimentOptions,
  formatScRnaToolVersionText,
  getScRnaExperimentCatalog,
  getScRnaExperimentNotice,
  getScRnaExperimentSampleSheetRule,
  getScRnaPipelineToolVersions,
  formatVdjLibraryTypeLabel,
  getScRnaSampleSheetRowErrors,
  normalizeVdjLibraryType,
  resolveScRnaPlatformLabel,
  sanitizeScRnaSampleSheetRow,
  vdjLibraryTypeIncludes,
  VDJ_LIBRARY_FASTQ_FIELDS,
  VDJ_LIBRARY_TYPE_OPTIONS
} from './scRnaExperimentHelpers';
import tgiaLogo from './images/TGIA_logo_rgb.png';
import tgiaLogoFull from './images/TGIA_logo_rgb_full.png';
import tgiaLogo_dark from './images/TGIA_logo_dark_rgb.png';
import tgiaLogoFull_dark from './images/TGIA_logo_dark_rgb_full.png';

import {
  ActionBar,
  InfoPanel,
  SectionCard,
  apPackageToneClasses,
  buttonSizeClasses,
  buttonToneClasses,
  badgeToneClasses,
  dropdownToneClasses,
  inputToneClasses,
  segmentedTabToneClasses,
  pageToneClasses,
  infoToneClasses
} from './formLayoutComponents';

const compactInputToneClasses = {
  text: inputToneClasses.compactText,
  select: inputToneClasses.compactSelect,
  textarea: inputToneClasses.compactTextarea
};

const {
  filterPublicProductLineCatalog,
  sanitizeServiceItemsForPublicCatalog,
  findProductLineServiceConfig,
  filterExtractionServicesForSequencing
} = productLineHelpers;

// 讀取設定
const APP_TITLE = process.env.REACT_APP_TITLE || '台基盟生技股份有限公司';
const DEFAULT_SALES_CODE = process.env.REACT_APP_DEFAULT_SALES_CODE || '';
const DEFAULT_ORG = process.env.REACT_APP_DEFAULT_ORG || '';
const DEFAULT_PI = process.env.REACT_APP_DEFAULT_PI || '';
const DEFAULT_CONTACT = process.env.REACT_APP_DEFAULT_CONTACT || '';
const DEFAULT_PHONE = process.env.REACT_APP_DEFAULT_PHONE || '';
const DEFAULT_EMAIL = process.env.REACT_APP_DEFAULT_EMAIL || '';
const THEME_STORAGE_KEY = 'tgia-order-theme';

const createFormSessionId = () => {
  if (typeof window !== 'undefined' && window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const formatWaitTimeMs = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return '';
  if (numeric < 1000) return `${Math.max(1, Math.round(numeric))} ms`;
  if (numeric < 60000) return `${(numeric / 1000).toFixed(numeric < 10000 ? 1 : 0)} 秒`;
  const totalSeconds = Math.round(numeric / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return seconds ? `${minutes} 分 ${seconds} 秒` : `${minutes} 分`;
};

const sendThemeUsage = ({ mode, eventType, sessionId }) => {
  const payload = JSON.stringify({ mode, eventType, sessionId });

  if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function' && typeof Blob !== 'undefined') {
    const sent = navigator.sendBeacon('/api/usage/theme', new Blob([payload], { type: 'application/json' }));
    if (sent) return;
  }

  if (typeof fetch === 'function') {
    fetch('/api/usage/theme', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: payload,
      keepalive: true
    }).catch(() => {});
  }
};

const sendPresenceHeartbeat = ({ sessionId, authState, currentStep, salesCode }) => {
  const payload = JSON.stringify({ sessionId, authState, currentStep, salesCode });

  if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function' && typeof Blob !== 'undefined') {
    const sent = navigator.sendBeacon('/api/usage/presence', new Blob([payload], { type: 'application/json' }));
    if (sent) return;
  }

  if (typeof fetch === 'function') {
    fetch('/api/usage/presence', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: payload,
      keepalive: true
    }).catch(() => {});
  }
};

const isDocumentHidden = () => (
  typeof document !== 'undefined'
  && document.visibilityState === 'hidden'
);

const PRESENCE_HEARTBEAT_INTERVAL_MS = 2000;
const FORCE_LOGOUT_TIMEOUT_MS = 60 * 60 * 1000;

const ANALYSIS_DOCUMENT_TEMPLATE_GROUPS = [
  { key: 'isRNAseqService', serviceFlag: 'isRNAseq' },
  { key: 'isScRNAseqService', serviceFlag: 'isScRNAseq' },
  { key: 'isPacBioService', serviceFlag: 'isPacBio' },
  { key: 'isWGSWESGermlineAnnotationService', serviceFlag: 'isWGSWESGermlineAnnotation' },
  { key: 'isShotgunMetagenomicsService', serviceFlag: 'isShotgunMetagenomics' },
  { key: 'isDRAGENService', serviceFlag: 'isDRAGEN' },
  { key: 'isPCGRService', serviceFlag: 'isPCGR' },
  { key: 'isTGSafeService', serviceFlag: 'isTGSafe' }
];

const normalizeAnalysisTemplateDocuments = (templateKey, templateConfig) => {
  if (!templateConfig || typeof templateConfig !== 'object' || Array.isArray(templateConfig)) return [];

  const documentConfig = Array.isArray(templateConfig.documents) ? templateConfig.documents : [];

  return documentConfig.flatMap((entry, index) => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return [];

    const label = typeof entry.label === 'string' ? entry.label.trim() : '';
    const fileName = typeof entry.fileName === 'string' ? entry.fileName.trim() : '';

    if (!label || !fileName) return [];

    return [{
      id: `${templateKey}__${index}`,
      label,
      fileName,
      description: typeof entry.description === 'string' ? entry.description.trim() : ''
    }];
  });
};

const getAnalysisDocumentTemplateKeysForOrder = (orderData = {}, serviceCatalog = {}) => {
  const templateKeys = new Set();

  if (!Array.isArray(orderData.serviceItems)) return [];

  orderData.serviceItems.forEach(item => {
    if (!Array.isArray(item?.services)) return;

    item.services.forEach(serviceItem => {
      const serviceConfig = findProductLineServiceConfig(serviceCatalog, serviceItem?.service);
      if (!serviceConfig || serviceConfig.with_docs !== true) return;

      if (typeof serviceConfig.analysisTemplateKey === 'string' && serviceConfig.analysisTemplateKey.trim()) {
        templateKeys.add(serviceConfig.analysisTemplateKey.trim());
      }

      ANALYSIS_DOCUMENT_TEMPLATE_GROUPS.forEach(group => {
        if (serviceConfig[group.serviceFlag] === true) {
          templateKeys.add(group.key);
        }
      });
    });
  });

  return [...templateKeys];
};

const collectAnalysisDocumentsForOrder = (orderData = {}, {
  serviceCatalog = {},
  analysisTemplatesByServiceType = {}
} = {}) => {
  const seen = new Set();
  const documents = [];

  getAnalysisDocumentTemplateKeysForOrder(orderData, serviceCatalog).forEach(templateKey => {
    normalizeAnalysisTemplateDocuments(templateKey, analysisTemplatesByServiceType[templateKey]).forEach(document => {
      const dedupeKey = `${document.label}/${document.fileName}`;
      if (seen.has(dedupeKey)) return;
      seen.add(dedupeKey);
      documents.push({
        ...document,
        downloadUrl: `/api/analysis-documents/${encodeURIComponent(document.id)}`
      });
    });
  });

  return documents;
};


// ========== 小功能 ==========

const COMMON_LIBRARY_KITS = [
  {
    category: "DNA", kits: [
      "Illumina DNA PCR-Free Prep",
      "Illumina DNA Prep",
      "Illumina DNA Prep with Enrichment+xGen Exome enrichment panel V2",
      "KAPA HyperExome Plus Kit",
      "Lotus DNA Library Prep Kit (IDT WES)",
      "TruSeq DNA PCR-Free",
      "Twist Human Core Exome Kit",
      "KAPA HyperPrep Kit (PCR program)",
      "KAPA HyperPrep Kit",
      "TruePrep DNA Library Prep Kit V2 for Illumina® (TD501,TD502,TD503)",
      "CeleroTM DNA-Seq",
      "QIAseq Targeted DNA Panels",
      "QIAseqTM cfDNA All-in-One Kit"
    ]
  },
  {
    category: "RNA", kits: [
      "Illumina Stranded mRNA Prep",
      "KAPA mRNA HyperPrep Kit",
      "TruSeq Stranded mRNA",
      "SMART-Seq® Stranded Kit (Takara 634442,634443,634444)",
      "KAPA stranded mRNA",
      "KAPA RNA HyperPrep Kit with RiboErase (HMR)",
      "Illumina Stranded Total RNA Prep with Ribo-Zero Plus",
      "Illumina RNA prep with enrichment+IDT probe",
      "Universal PlusTM mRNA-Seq with NuQuant®"
    ]
  },
  {
    category: "Single Cell", kits: [
      "BD RhapsodyTM System",
      "Chromium Next GEM Single Cell 3ʹ Reagent Kits v3.1",
      "Chromium Next GEM Single Cell 3ʹ Reagent Kits v3.1 (Dual Index)",
      "Chromium Next GEM Single Cell 5' Reagent Kits v2 (Dual Index)",
      "Visium Spatial Gene Expression Reagent Kits -Tissue Optimization"
    ]
  },
  {
    category: "Targeted/Panel", kits: [
      "TruSight Oncology 500 High Throughput",
      "TruSight Oncology 500",
      "TruSight® Myeloid Sequencing Panel"
    ]
  },
  {
    category: "Other", kits: [
      "ATAC-seq",
      "QIASeq miRNA Library kit"
    ]
  }
];

const COMMON_INDEX_KITS = [
  "IDT for Illumina DNA/RNA UD Indexes Set A, B, C, D (384 Indexes, 384 Samples)",
  "Illumina DNA/RNA UD Indexes Set A (96 Indexes, 96 Samples)",
  "IDT for Illumina DNA/RNA UD Indexes Ligation (i7) only",
  "TruSeq DNA Single Indexes Set A (12 Indexes, 12 Samples)",
  "TruSeq DNA Single Indexes Set B (12 Indexes, 12 Samples)",
  "Nextera DNA CD Indexes (96 Indexes, 96 Samples)",
  "TruSeq RNA Single Indexes Set A (12 Indexes, 12 Samples)",
  "TruSeq RNA Single Indexes Set B (12 Indexes, 12 Samples)",
  "IDT for Illumina TruSeq DNA UD Indexes (96 Indexes, 96 Samples)",
  "IDT for Illumina TruSeq RNA UD Indexes (96 Indexes, 96 Samples)"
];

const filterScRNAExperimentOptions = (options = [], allowedServiceNames = []) => {
  const normalizedOptions = Array.isArray(options) ? options : [];
  const normalizedAllowedNames = Array.isArray(allowedServiceNames)
    ? allowedServiceNames.map(name => String(name).trim()).filter(Boolean)
    : [];

  if (normalizedAllowedNames.length === 0) {
    return normalizedOptions;
  }

  const allowedSet = new Set(normalizedAllowedNames);
  return normalizedOptions.filter(item => allowedSet.has(String(item?.ServiceName || '').trim()));
};

const ORDER_CODE_PREFIX = 'TGIA-';

const getOrderCodeSuffixInputValue = (value = '') => {
  const trimmed = String(value || '').trim().replace(/\.json$/i, '');
  if (!trimmed) return '';

  const normalized = trimmed.toUpperCase();
  return normalized.startsWith(ORDER_CODE_PREFIX)
    ? normalized.slice(ORDER_CODE_PREFIX.length)
    : normalized;
};

const normalizeOrderCodeInput = (value = '') => {
  const suffix = getOrderCodeSuffixInputValue(value);
  if (!suffix) return '';
  if (!/^[A-Z0-9_-]+$/.test(suffix)) return '';

  return `${ORDER_CODE_PREFIX}${suffix}`;
};

//// Email 驗證 函數（多個 Email，以逗號或分號分隔都可以） 
const validateEmail = (emailString) => {
  if (!emailString || !emailString.trim()) return false;
  const emails = emailString.split(/[,;]/).map(e => e.trim()).filter(e => e);
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emails.length > 0 && emails.every(email => emailRegex.test(email));
};

const isWarningMessage = (text = '') => {
  const normalized = String(text || '').trim();
  if (!normalized) return false;

  return (
    normalized.startsWith('❌') ||
    normalized.startsWith('⚠️') ||
    normalized.startsWith('請') ||
    normalized.includes('失敗') ||
    normalized.includes('錯誤') ||
    normalized.includes('不存在') ||
    normalized.includes('不足') ||
    normalized.includes('超過') ||
    normalized.includes('無法') ||
    normalized.includes('不正確') ||
    normalized.includes('未找到')
  );
};

//// 步驟指示器 => 網頁上方那排
const StepIndicator = ({ currentStep, steps, isLocked }) => {
  return (
    <div className="mb-6 rounded-[28px] border border-[var(--tgia-color-border)] [background:var(--tgia-step-indicator-bg)] px-4 py-4 shadow-[var(--tgia-shadow-card)] sm:mb-8 sm:px-5 sm:py-5">
      <div className="flex items-start justify-between gap-0 overflow-x-auto sm:gap-3">
        {steps.map((step, index) => {
          const isCompleted = index < currentStep;
          const isCurrent = index === currentStep;
          const isFinished = isLocked || isCompleted;
          const nodeClass = isFinished
            ? 'bg-[var(--tgia-color-step-complete)] text-[var(--tgia-color-step-complete-text)] shadow-[var(--tgia-shadow-card)]'
            : isCurrent
              ? 'bg-[var(--tgia-color-step-current)] text-[var(--tgia-color-step-current-text)] shadow-[var(--tgia-shadow-card)]'
              : 'bg-[var(--tgia-color-step-idle)] text-[var(--tgia-color-step-idle-text)]';
          const labelClass = isFinished
            ? 'text-[var(--tgia-color-step-complete-label)] font-semibold'
            : isCurrent
              ? 'text-[var(--tgia-color-step-current)] font-semibold'
              : 'text-[var(--tgia-color-step-idle-label)]';
          return (
            <React.Fragment key={index}>
              <div className="flex min-w-0 flex-none flex-col items-center sm:flex-1">
                <div
                  className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold transition sm:h-10 sm:w-10 ${nodeClass}`}
                  aria-current={isCurrent ? 'step' : undefined}
                >
                  {isFinished ? <Check size={18} /> : index + 1}
                </div>
                {/* Phones: per-node labels are hidden (they would stack letter-by-letter); the current label is shown below the row. */}
                <p
                  className={`mt-2 hidden text-center text-xs leading-5 sm:block ${labelClass}`}
                >
                  {step}
                </p>
              </div>

              {index < steps.length - 1 && (
                <div
                  className={`mx-1 mt-4 h-1 flex-1 rounded-full transition sm:mx-2 sm:mt-5 ${isLocked || index < currentStep
                    ? 'bg-[var(--tgia-color-step-connector-active)]'
                    : 'bg-[var(--tgia-color-step-connector)]'
                    }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
      <p className="mt-3 text-center text-sm font-semibold leading-5 text-[var(--tgia-color-step-current)] sm:hidden">
        {isLocked ? steps[steps.length - 1] : steps[currentStep]}
      </p>
    </div>
  );
};


// ========== 主要表單組件 ==========
const TGIAOrderForm = () => {
  const canAddServiceForCategory = (category) => category === '分析服務 (A)';
  const [currentStep, setCurrentStep] = useState(0);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(THEME_STORAGE_KEY) === 'dark';
  });
  const [showOrgSuggestions, setShowOrgSuggestions] = useState(false);
  const [filteredOrgs, setFilteredOrgs] = useState([]);
  const [exportReady, setExportReady] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);  // 🆕 提交載入狀態
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isExporting, setIsExporting] = useState(false);    // 🆕 下載載入狀態
  const [needsAnalysisRequest, setNeedsAnalysisRequest] = useState(false);  // 🆕 是否需要下載分析需求單
  const [organizationOptions, setOrganizationOptions] = useState([]);
  const [availableSpecies, setAvailableSpecies] = useState({});
  const [experimentInfo, setExperimentInfo] = useState({});
  const [serviceOptionsByCategory, setServiceOptionsByCategory] = useState({});
  const [commonPackages, setCommonPackages] = useState([]);
  const [analysisTemplatesByServiceType, setAnalysisTemplatesByServiceType] = useState({});
  const [analysisDocumentLatencyEstimate, setAnalysisDocumentLatencyEstimate] = useState(null);
  const [isCatalogConfigLoading, setIsCatalogConfigLoading] = useState(true);
  const [catalogConfigLoadError, setCatalogConfigLoadError] = useState('');
  const safeCommonPackages = Array.isArray(commonPackages) ? commonPackages : [];
  const formTimingRef = useRef({
    sessionId: createFormSessionId(),
    startedAtMs: Date.now(),
    startedAt: new Date().toISOString()
  });
  const presenceSessionIdRef = useRef(createFormSessionId());
  const authSessionStartedAtRef = useRef(null);
  const forcedLogoutTimerRef = useRef(null);
  const forcedLogoutInProgressRef = useRef(false);
  const forcedLogoutHandlerRef = useRef(null);
  const latestAutoLogoutSnapshotRef = useRef({
    formData: null,
    resolvedSampleFormData: null,
    currentStep: 0,
    isLocked: false,
    isSubmitting: false,
    isSavingDraft: false,
    submitted: false,
    orderId: '',
    draftOrderCode: '',
    editingOrderCode: '',
    editingOrderSource: '',
    hasDraftContent: false
  });
  const skipNextSampleSheetSyncRef = useRef(false);
  const themeUsageRef = useRef({
    reportedInitial: false,
    lastMode: null
  });

  const buildFormTimingMetadata = () => {
    const submittedAtMs = Date.now();
    return {
      sessionId: formTimingRef.current.sessionId,
      startedAt: formTimingRef.current.startedAt,
      submittedAt: new Date(submittedAtMs).toISOString()
    };
  };

  const buildForcedLogoutTimingMetadata = () => {
    const submittedAtMs = Date.now();
    const startedAtMs = authSessionStartedAtRef.current || formTimingRef.current.startedAtMs || submittedAtMs;
    return {
      sessionId: formTimingRef.current.sessionId,
      startedAt: new Date(startedAtMs).toISOString(),
      submittedAt: new Date(submittedAtMs).toISOString()
    };
  };

  useEffect(() => {
    const theme = isDarkMode ? 'dark' : 'light';
    document.documentElement.dataset.theme = theme;
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);

    if (themeUsageRef.current.lastMode === theme && themeUsageRef.current.reportedInitial) {
      return;
    }

    sendThemeUsage({
      mode: theme,
      eventType: themeUsageRef.current.reportedInitial ? 'toggle' : 'initial',
      sessionId: formTimingRef.current.sessionId
    });
    themeUsageRef.current.reportedInitial = true;
    themeUsageRef.current.lastMode = theme;
  }, [isDarkMode]);

  // 表單小工具
  // 🆕 判斷是否為 Bulk RNA-seq 性質的服務（對應後端 isBulkRNAseqService）
  const isBulkRNAseqService = (serviceName) => {
    if (!serviceName) return false;
    // 從服務 catalog 中尋找匹配的服務並檢查 isRNAseq 標記
    for (const category in serviceOptionsByCategory) {
      const service = serviceOptionsByCategory[category].find(s => s.value === serviceName);
      if (service) {
        return service.isRNAseq === true;
      }
    }
    return false;
  };

  // 🆕 判斷是否為 single cell RNA-seq 性質的服務（對應後端 isScRNAseqService）
  const isScRNAseqService = (serviceName) => {
    if (!serviceName) return false;
    // 從服務 catalog 中尋找匹配的服務並檢查 isScRNAseq 標記
    for (const category in serviceOptionsByCategory) {
      const service = serviceOptionsByCategory[category].find(s => s.value === serviceName);
      if (service) {
        return service.isScRNAseq === true;
      }
    }
    return false;
  };

  const isVisiumHDService = (serviceName) => {
    if (!serviceName) return false;
    for (const category in serviceOptionsByCategory) {
      const service = serviceOptionsByCategory[category].find(s => s.value === serviceName);
      if (service) {
        return service.isVisiumHD === true || String(service.value || '').includes('VisiumHD-10X');
      }
    }
    return false;
  };

  // 🆕 判斷是否為 ChIP-seq 服務（對應後端 isChIPseqService）
  const isChIPseqService = (serviceName) => {
    if (!serviceName) return false;
    for (const category in serviceOptionsByCategory) {
      const service = serviceOptionsByCategory[category].find(s => s.value === serviceName);
      if (service) {
        return isChIPseqServiceConfig(service, serviceName);
      }
    }
    return false;
  };

  // ChIP-seq 的 Sample Sheet 列數由使用者決定，樣品數量則跟著步驟三填的樣本數走，
  // 所以列數、數量、驗證與表格都要知道這張單是不是 ChIP-seq。
  const isChIPseqOrder = (data = formData) => Boolean(getChIPseqPipelineInfo({
    serviceItems: data?.serviceItems || [],
    serviceCatalog: serviceOptionsByCategory
  }));

  // 🆕 判斷是否為 Singleron 服務，並兼容舊的 CeleScope 命名
  const isSingleronService = (serviceName) => {
    if (!serviceName) return false;
    const normalized = String(serviceName).toLowerCase();
    return normalized.includes('singleron') || normalized.includes('celescope');
  };

  const {
    getSpeciesMode,
    getSpeciesOptionsForMode,
    canSelectOtherSpecies,
    normalizeSpeciesFields,
    getSpeciesDisplayInfo,
    getSpeciesOptionLabel,
    getSpeciesSelectionKey,
    findSpeciesOptionBySelection
  } = createSpeciesHelpers({
    isBulkRNAseqService,
    isScRNAseqService,
    isVisiumHDService,
    isChIPseqService,
    isSingleronService,
    availableSpecies,
    serviceCatalog: serviceOptionsByCategory
  });

  // 下拉選單用：不支援的實驗類型仍會出現在清單中，但帶著 disabled 旗標。
  const getScRNAExperimentOptions = (platformLabel = '', apPackageInfo = null, analysisServiceValue = '') => (
    filterScRNAExperimentOptions(
      buildScRnaExperimentOptions({ experimentInfo, platformLabel, analysisServiceValue }),
      apPackageInfo?.binding?.scRNAExperimentServiceNames
    )
  );

  // 查詢用：以 ServiceName 精準比對，不限平台，這樣舊訂單載入時也找得到說明。
  const findScRNAExperimentOption = (platformLabel = '', serviceName = '', apPackageInfo = null) => (
    filterScRNAExperimentOptions(
      getScRnaExperimentCatalog(experimentInfo),
      apPackageInfo?.binding?.scRNAExperimentServiceNames
    ).find(item => item.ServiceName === serviceName) || null
  );

  // 是否真的可以選：要出現在這個品項的選單中，而且沒有被標記為不支援。
  const isScRNAExperimentSelectable = (platformLabel = '', serviceName = '', apPackageInfo = null, analysisServiceValue = '') => {
    if (!serviceName) return false;

    const option = getScRNAExperimentOptions(platformLabel, apPackageInfo, analysisServiceValue)
      .find(item => item.ServiceName === serviceName);

    return Boolean(option) && !option.disabled;
  };

  // 準備輸出到 JSON 中的資訊
  const buildSubmitPayload = (data = {}) => {
    return buildSubmitPayloadHelper({
      data,
      normalizeSpeciesFields,
      getScRNAseqPipelineInfo,
      getRNAseqPipelineInfo,
      getChIPseqPipelineInfo,
      getAnalysisParameterDefinitions,
      findScRNAExperimentOption,
      serviceCatalog: serviceOptionsByCategory
    });
  };

  //// Sample_Name 清理函數：只保留英文、數字、_、,、- => 強制SampleName命名方式
  const sanitizeSampleName = (name) => {
    if (!name) return '';
    return String(name).replace(/[^a-zA-Z0-9_,-]/g, '');
  };
  // 🆕 驗證姓名/單位格式（不能以數字、符號、空格開頭）
  const validateNameFormat = (value, fieldName) => {
    if (!value || value.trim() === '') {
      return `${fieldName}為必填欄位`;
    }

    const validPattern = /^[a-zA-Z\u4e00-\u9fa5]/;
    if (!validPattern.test(value)) {
      return `${fieldName}不能以數字、符號或空格開頭`;
    }
    return null;
  };

  // 加入AP(Package產品)系列判定
  const getAPPackageConfig = () => {
    if (!formData.selectedServiceCategories.includes('套組產品 (P)')) {
      return null;
    }
    const apItem = formData.serviceItems.find(item => item.category === '套組產品 (P)');
    if (apItem && apItem.services[0].service) {
      const apOptions = serviceOptionsByCategory['套組產品 (P)'] || [];
      const config = apOptions.find(opt => opt.value === apItem.services[0].service);
      if (!config) return null;

      const binding = {
        ...(config.binding || {})
      };

      if (!binding.speciesCommon && config.speciesCommon) {
        binding.speciesCommon = config.speciesCommon;
      }

      if (!binding.scRNAPlatformLabel && config.scRNAPlatformLabel) {
        binding.scRNAPlatformLabel = config.scRNAPlatformLabel;
      }

      if (!binding.scRNAPipelineLabel && config.scRNAPipelineLabel) {
        binding.scRNAPipelineLabel = config.scRNAPipelineLabel;
      }

      if (!binding.scRNAExperimentServiceNames && Array.isArray(config.scRNAExperimentServiceNames)) {
        binding.scRNAExperimentServiceNames = config.scRNAExperimentServiceNames;
      }

      const seqPerSample = binding.seqAmountGb ? Number(binding.seqAmountGb) : null;

      return {
        ...config,
        binding,
        quantity: parseInt(apItem.services[0].quantity) || 1,
        ...(seqPerSample ? { seqPerSample } : {})
      };
    }
    return null;
  };

  const createBlankServiceRow = () => ({
    service: '',
    quantity: '',
    description: '',
    spec: '',
    price: '',
    subtotal: 0
  });

  const createBlankServiceGroup = (category = '請選擇服務類別') => ({
    category,
    services: [createBlankServiceRow()],
    libraryType: '無',
    seqSpec: ''
  });

  const createBlankSampleInfoRow = (no = 1, expectedSeq = '') => ({
    no,
    sampleName: '',
    sampleBarcode: '',
    // SC04：fastqName 是 GEX 的 fastq 檔名前綴，vdjType 與兩個 *FastqName 記錄配對的 V(D)J library。
    fastqName: '',
    vdjType: '',
    bcrFastqName: '',
    tcrFastqName: '',
    tubeLabel: '',
    expectedSeq,
    conc: '',
    vol: '',
    ratio260280: '',
    ratio260230: '',
    dqnRqn: '',
    note: ''
  });

  const createBlankLibrarySampleRow = (no = 1, expectedSeq = '') => ({
    no,
    sampleName: '',
    tubeLabel: '',
    conc: '',
    vol: '',
    ngsConc: '',
    expectedSeq,
    note: ''
  });

  const createBlankLibraryDetailRow = (no = 1) => ({
    no,
    sampleName: '',
    libraryPrepKit: '',
    indexAdapterKit: '',
    setWellPosition: '',
    index1Seq: '',
    index2Seq: '',
    note: '',
    library: '',
    tubeName: ''
  });

  const preventNumberInputScroll = (event) => {
    if (event?.currentTarget) {
      event.currentTarget.blur();
    }
  };

  const resizeRows = (rows = [], count = 1, createRow = createBlankSampleInfoRow) => {
    const total = Math.max(1, parseInt(count, 10) || 1);
    return Array.from({ length: total }, (_, idx) => {
      const existing = rows[idx] || {};
      return {
        ...createRow(idx + 1),
        ...existing,
        no: idx + 1
      };
    });
  };

  const getSelectedPackage = (packageId) => (
    safeCommonPackages.find(pkg => pkg.id === packageId) || null
  );

  const getPublicPackageDefaultServices = (selectedPkg, category = '') => {
    const serviceOptions = serviceOptionsByCategory[category] || [];
    const availableServices = new Set(serviceOptions.map(opt =>
      String(typeof opt === 'string' ? opt : opt?.value || '')
    ));

    return (selectedPkg?.defaultServices || []).filter(svc => (
      svc.category === category && availableServices.has(String(svc.service || ''))
    ));
  };

  const buildPackageServiceItems = (selectedPkg, categories = [], multiplier = 1) => {
    const categoryOrder = [
      'QC (Q)',
      '萃取/QC (EQ)',
      '建庫服務 (L)',
      '定序服務 (S)',
      '分析服務 (A)',
      '套組產品 (P)'
    ];
    const sortedCategories = categoryOrder.filter(cat => categories.includes(cat));

    return sortedCategories.map(category => {
      const defaultServices = getPublicPackageDefaultServices(selectedPkg, category);

      if (defaultServices.length === 0) {
        return createBlankServiceGroup(category);
      }

      const serviceOptions = serviceOptionsByCategory[category] || [];
      return {
        category,
        services: defaultServices.map(ds => {
          const serviceConfig = serviceOptions.find(opt =>
            (typeof opt === 'string' ? opt : opt.value) === ds.service
          );
          const baseQuantity = parseInt(ds.defaultQuantity, 10) || 1;
          const quantity = ds.excludeFromMultiplier
            ? String(baseQuantity)
            : String(baseQuantity * multiplier);

          return {
            service: ds.service || '',
            quantity,
            description: serviceConfig?.description || '',
            spec: serviceConfig?.spec || '',
            price: '',
            subtotal: 0
          };
        }),
        libraryType: '無',
        seqSpec: ''
      };
    });
  };

  const getPackageDefaultServiceForRow = (defaultServicesForCategory = [], service = {}, serviceIndex = 0) => {
    if (!service?.service) {
      return null;
    }

    return defaultServicesForCategory.find(ds => ds.service === service.service)
      || defaultServicesForCategory[serviceIndex]
      || null;
  };

  const syncPackageServiceQuantities = (serviceItems = [], selectedPkg = null, multiplier = 1) => {
    if (!selectedPkg) return serviceItems;

    return serviceItems.map(item => {
      const defaultServicesForCategory = getPublicPackageDefaultServices(selectedPkg, item.category);

      if (defaultServicesForCategory.length === 0) {
        return item;
      }

      return {
        ...item,
        services: item.services.map((service, serviceIndex) => {
          const defaultService = getPackageDefaultServiceForRow(
            defaultServicesForCategory,
            service,
            serviceIndex
          );

          if (!defaultService) {
            return service;
          }

          const baseQuantity = parseInt(defaultService.defaultQuantity, 10) || 1;
          const quantity = defaultService.excludeFromMultiplier
            ? String(baseQuantity)
            : String(baseQuantity * multiplier);

          return {
            ...service,
            quantity,
            subtotal: (parseFloat(service.price) || 0) * (parseInt(quantity, 10) || 0)
          };
        })
      };
    });
  };

  const buildSampleSheetStateForCount = ({
    prev,
    sampleType,
    count,
    preserveExisting = true
  }) => {
    const total = Math.max(1, parseInt(count, 10) || 1);
    const apConfig = getAPPackageConfig();
    const lockedExpectedSeq = apConfig?.binding?.seqAmountGb
      ? String(apConfig.binding.seqAmountGb)
      : '';

    if (sampleType === 'Library') {
      const sampleRows = resizeRows(
        preserveExisting ? prev.libraryInfo?.sampleSheet || [] : [],
        total,
        (no) => createBlankLibrarySampleRow(no, lockedExpectedSeq)
      ).map(row => (
        lockedExpectedSeq ? { ...row, expectedSeq: lockedExpectedSeq } : row
      ));

      const existingLibraryDetailRows = preserveExisting ? prev.libraryInfo?.librarySampleSheet || [] : [];
      const libraryDetailCount = Math.max(total, existingLibraryDetailRows.length || 1);
      const libraryRows = resizeRows(
        existingLibraryDetailRows,
        libraryDetailCount,
        createBlankLibraryDetailRow
      );

      return {
        sampleCount: total,
        sampleInfo: {
          ...prev.sampleInfo,
          sampleSheet: [createBlankSampleInfoRow(1)]
        },
        libraryInfo: {
          ...prev.libraryInfo,
          sampleSheet: sampleRows,
          librarySampleSheet: libraryRows
        }
      };
    }

    if (sampleType === '無送樣') {
      const isAnalysisOnly = isAnalysisOnlySampleOrder(prev);

      if (isAnalysisOnly) {
        if (!shouldUseAnalysisSampleSheetLite(prev)) {
          return {
            sampleCount: '',
            sampleInfo: {
              ...prev.sampleInfo,
              sampleSheet: []
            },
            libraryInfo: {
              ...prev.libraryInfo,
              sampleSheet: [createBlankLibrarySampleRow(1)],
              librarySampleSheet: [createBlankLibraryDetailRow(1)]
            }
          };
        }

        const sampleRows = resizeRows(
          preserveExisting ? prev.sampleInfo?.sampleSheet || [] : [],
          total,
          createBlankSampleInfoRow
        );

        return {
          sampleCount: resolveAnalysisSampleCount(prev, total, sampleRows),
          sampleInfo: {
            ...prev.sampleInfo,
            sampleSheet: sampleRows
          },
          libraryInfo: {
            ...prev.libraryInfo,
            sampleSheet: [createBlankLibrarySampleRow(1)],
            librarySampleSheet: [createBlankLibraryDetailRow(1)]
          }
        };
      }

      return {
        sampleCount: prev.selectedPackage ? total : '',
        sampleInfo: {
          ...prev.sampleInfo,
          sampleSheet: [createBlankSampleInfoRow(1)]
        },
        libraryInfo: {
          ...prev.libraryInfo,
          sampleSheet: [createBlankLibrarySampleRow(1)],
          librarySampleSheet: [createBlankLibraryDetailRow(1)]
        }
      };
    }

    const sampleRows = resizeRows(
      preserveExisting ? prev.sampleInfo?.sampleSheet || [] : [],
      total,
      (no) => createBlankSampleInfoRow(no, lockedExpectedSeq)
    ).map(row => (
      lockedExpectedSeq ? { ...row, expectedSeq: lockedExpectedSeq } : row
    ));

    return {
      sampleCount: total,
      sampleInfo: {
        ...prev.sampleInfo,
        sampleSheet: sampleRows
      },
      libraryInfo: {
        ...prev.libraryInfo,
        sampleSheet: [createBlankLibrarySampleRow(1)],
        librarySampleSheet: [createBlankLibraryDetailRow(1)]
      }
    };
  };

  const getPackageDrivenSampleCount = (prev = {}) => (
    Math.max(
      1,
      prev.selectedPackage
        ? (parseInt(prev.packageMultiplier, 10) || parseInt(prev.sampleCount, 10) || 1)
        : (parseInt(prev.sampleCount, 10) || parseInt(prev.packageMultiplier, 10) || 1)
    )
  );

  // ChIP-seq 的版面固定成對，沒送的 input 那列會留白佔位；留白不算送樣也不計價，
  // 所以「樣品數量」算的是實際填了 Sample_Name 的列數，而不是列數本身。
  const resolveAnalysisSampleCount = (prev = {}, rowCount = 0, sampleSheet = null) => {
    if (!isChIPseqOrder(prev)) return rowCount;

    return getChIPseqSubmittedSampleCount({
      sampleInfo: { sampleSheet: sampleSheet || prev.sampleInfo?.sampleSheet || [] }
    });
  };

  // 步驟三填的樣本數，決定 Sample Sheet 要開幾列。
  const getAnalysisOnlySampleCount = (prev = {}) => {
    if (!shouldUseAnalysisSampleSheetLite(prev)) {
      return 0;
    }

    const configuredSampleSheetQuantity = getAnalysisSampleSheetLiteQuantity({
      formData: prev,
      serviceCatalog: serviceOptionsByCategory
    });

    if (configuredSampleSheetQuantity > 0) {
      return configuredSampleSheetQuantity;
    }

    const analysisQuantities = Array.isArray(prev.serviceItems)
      ? prev.serviceItems
        .filter(item => item?.category === '分析服務 (A)')
        .flatMap(item => Array.isArray(item.services) ? item.services : [])
        .map(service => parseInt(service?.quantity, 10))
        .filter(quantity => Number.isFinite(quantity) && quantity > 0)
      : [];

    if (analysisQuantities.length > 0) {
      return Math.max(...analysisQuantities);
    }

    const existingCount = parseInt(prev.sampleCount, 10);
    if (Number.isFinite(existingCount) && existingCount > 0) {
      return existingCount;
    }

    return 1;
  };

  const syncSampleSheetRowsToCount = (prev = {}) => {
    if (isAnalysisOnlySampleOrder(prev)) {
      const sampleRows = Array.isArray(prev.sampleInfo?.sampleSheet) ? prev.sampleInfo.sampleSheet : [];

      if (!shouldUseAnalysisSampleSheetLite(prev)) {
        if (sampleRows.length === 0 && prev.sampleCount === '') {
          return prev;
        }

        return normalizeSpeciesFields({
          ...prev,
          ...buildSampleSheetStateForCount({
            prev,
            sampleType: '無送樣',
            count: 0,
            preserveExisting: false
          }),
          ...syncPackageStateAfterCountChange(prev, 0)
        });
      }

      // 部分 scRNA-seq 實驗類型在步驟三填的其實是 fastq / library 數，樣本數可能比它多
      // （SC02、SC06 的 multiplex）也可能比它少（SC04 一個樣本多份 fastq），因此不設上下限，
      // 只在完全沒有列時用步驟三的數量開場，之後列數完全由使用者決定。
      // ChIP-seq 一列就是一個送樣樣本，列數等於步驟三數量，走一般規則。
      const configuredCount = getAnalysisOnlySampleCount(prev);
      const nextRowCount = getSampleSheetScRnaRule(prev).unlockRowCount
        ? Math.max(1, sampleRows.length || configuredCount)
        : configuredCount;
      const nextSampleCount = resolveAnalysisSampleCount(prev, nextRowCount, sampleRows);

      if (
        sampleRows.length === nextRowCount &&
        parseInt(prev.sampleCount, 10) === nextSampleCount
      ) {
        return prev;
      }

      return normalizeSpeciesFields({
        ...prev,
        ...buildSampleSheetStateForCount({
          prev,
          sampleType: '無送樣',
          count: nextRowCount,
          preserveExisting: true
        }),
        ...syncPackageStateAfterCountChange(prev, nextSampleCount)
      });
    }

    const desiredCount = getPackageDrivenSampleCount(prev);
    const sampleType = prev.sampleType || '無送樣';
    const sampleRows = Array.isArray(prev.sampleInfo?.sampleSheet) ? prev.sampleInfo.sampleSheet : [];
    const libraryRows = Array.isArray(prev.libraryInfo?.sampleSheet) ? prev.libraryInfo.sampleSheet : [];
    const libraryDetailRows = Array.isArray(prev.libraryInfo?.librarySampleSheet) ? prev.libraryInfo.librarySampleSheet : [];

    if (sampleType === 'Library') {
      if (
        libraryRows.length === desiredCount &&
        libraryDetailRows.length >= desiredCount &&
        parseInt(prev.sampleCount, 10) === desiredCount
      ) {
        return prev;
      }

      return normalizeSpeciesFields({
        ...prev,
        ...buildSampleSheetStateForCount({
          prev,
          sampleType: 'Library',
          count: desiredCount,
          preserveExisting: true
        }),
        ...syncPackageStateAfterCountChange(prev, desiredCount)
      });
    }

    if (sampleType === '無送樣') {
      const isStableNoSampleState = (
        sampleRows.length === 1 &&
        (!prev.selectedPackage || parseInt(prev.sampleCount, 10) === desiredCount)
      );

      if (isStableNoSampleState) {
        return prev;
      }

      return normalizeSpeciesFields({
        ...prev,
        ...buildSampleSheetStateForCount({
          prev,
          sampleType: '無送樣',
          count: desiredCount,
          preserveExisting: true
        }),
        ...syncPackageStateAfterCountChange(prev, desiredCount)
      });
    }

    if (
      sampleRows.length === desiredCount &&
      parseInt(prev.sampleCount, 10) === desiredCount
    ) {
      return prev;
    }

    return normalizeSpeciesFields({
      ...prev,
      ...buildSampleSheetStateForCount({
        prev,
        sampleType,
        count: desiredCount,
        preserveExisting: true
      }),
      ...syncPackageStateAfterCountChange(prev, desiredCount)
    });
  };

  const syncPackageStateAfterCountChange = (prev, count) => {
    const selectedPkg = getSelectedPackage(prev.selectedPackage);
    const packageAdjustedServiceItems = selectedPkg
      ? syncPackageServiceQuantities(
        prev.serviceItems,
        selectedPkg,
        Math.max(1, parseInt(count, 10) || 1)
      )
      : prev.serviceItems;

    if (!selectedPkg) {
      return {
        serviceItems: packageAdjustedServiceItems
      };
    }

    const multiplier = Math.max(1, parseInt(count, 10) || 1);
    return {
      packageMultiplier: multiplier,
      packageQuantity: multiplier,
      serviceItems: packageAdjustedServiceItems
    };
  };

  const getRowCountChangeNotice = (rowLabel = '樣本', action = '新增') => {
    const hasPackageMultiplier = !!getSelectedPackage(formData.selectedPackage);
    const spacedRowLabel = /^[A-Za-z]/.test(rowLabel) ? ` ${rowLabel}` : rowLabel;
    const actionPrefix = `已${action}${spacedRowLabel}`;

    if (hasPackageMultiplier) {
      return `${actionPrefix}；此常用組合會依 ${rowLabel} 列數同步調整 Step2 服務數量與定序量。`;
    }

    return `${actionPrefix}；此操作只調整送件資訊列，不會自動增加 Step2 購買定序量。若需加購，請回 Step2 調整服務數量。`;
  };

  const getAddRowTitle = (rowLabel = '樣本') => {
    const titleRowLabel = /^[A-Za-z]/.test(rowLabel) ? ` ${rowLabel} ` : rowLabel;

    if (getSelectedPackage(formData.selectedPackage)) {
      return `新增${titleRowLabel}後，常用組合會依列數同步調整 Step2 服務數量與定序量`;
    }

    return `新增${titleRowLabel}只會增加送件資訊列，不會自動增加 Step2 購買定序量`;
  };

  const getRowCountRuleHint = (rowLabel = '樣本') => {
    if (getSelectedPackage(formData.selectedPackage)) {
      return `新增或刪除 ${rowLabel} 會同步調整 Step2 常用組合的服務數量與定序量`;
    }

    return `新增或刪除 ${rowLabel} 只調整送件資訊，不會自動增加 Step2 購買定序量`;
  };

  // 在 useState 區域加入錯誤追蹤  = validateStep(3) 中檢查Liabaray資料
  const [fieldErrors, setFieldErrors] = useState({
    sampleSheet: {}, // { rowIndex: { fieldName: errorMessage } }
    librarySampleSheet: {},
    libraryDetailSheet: {}
  });

  // 「分析參數」：可自填的下拉選單（抗體）剛選了「其他」還沒打字時，畫面要維持在「其他」。
  const [analysisParameterCustomModes, setAnalysisParameterCustomModes] = useState({});
  // 「進階閾值（通常不用改）」預設收合；送出時裡面有錯誤會自動展開。
  const [isAnalysisThresholdsOpen, setIsAnalysisThresholdsOpen] = useState(false);

  // 拖放上傳 Excel 檔案功能
  const [isDragging, setIsDragging] = useState(false);
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];

      // 檢查檔案類型
      if (!file.name.match(/\.(xlsx|xls)$/i)) {
        alert('請上傳 Excel 檔案 (.xlsx 或 .xls)');
        return;
      }

      // 建立一個假的 event 物件來呼叫現有的 handleExcelUpload
      const fakeEvent = {
        target: {
          files: [file],
          value: ''
        }
      };

      handleExcelUpload(fakeEvent);
    }
  };

  const KitReferenceModal = ({ isOpen, onClose, onSelect, onApplyToAll, type = "library" }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All');

    if (!isOpen) return null;

    // 根据类型决定使用哪个数据源
    const dataSource = type === "library" ? COMMON_LIBRARY_KITS :
      [{ category: "Index Kits", kits: COMMON_INDEX_KITS }];

    // 获取所有分类
    const categories = ['All', ...dataSource.map(cat => cat.category)];

    // 过滤 kits
    const filteredKits = dataSource
      .filter(cat => selectedCategory === 'All' || cat.category === selectedCategory)
      .flatMap(cat =>
        cat.kits
          .filter(kit => kit.toLowerCase().includes(searchTerm.toLowerCase()))
          .map(kit => ({ category: cat.category, name: kit }))
      );

    return (
      <div className="fixed inset-0 tgia-modal-backdrop flex items-center justify-center z-50 p-3 sm:p-0">
        <div className="bg-[var(--tgia-color-surface)] rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] sm:max-h-[80vh] flex flex-col">
          {/* Header */}
          <div className="flex items-start sm:items-center justify-between gap-3 p-4 sm:p-6 border-b bg-[var(--tgia-color-surface-soft)]">
            <div className="min-w-0">
              <h3 className="text-xl font-bold text-[var(--tgia-color-text)]">
                {type === 'library' ? '📋 常用 Library Prep Kit 參考' : '🧬 常用 Index Adapter Kit 參考'}
              </h3>
              <p className="text-sm text-[var(--tgia-color-text-muted)] mt-1">點擊 Kit 名稱填入表格</p>
            </div>
            <button
              onClick={onClose}
              className="flex-shrink-0 p-2 hover:bg-[var(--tgia-color-border-subtle)] rounded-full transition"
              aria-label="關閉"
            >
              <X size={24} className="text-[var(--tgia-color-text-muted)]" />
            </button>
          </div>

          {/* Search Bar */}
          <div className="p-4 border-b bg-[var(--tgia-color-surface-muted)]">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="🔍 搜索 Kit 名稱..."
              className={inputToneClasses.text}
              autoFocus
            />
          </div>

          {/* Category Tabs */}
          {type === 'library' && (
            <div className="p-4 border-b bg-[var(--tgia-color-surface-muted)]">
              <div className="flex gap-2 overflow-x-auto pb-2">
                {categories.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`rounded-full border px-4 py-2 text-sm font-medium leading-5 whitespace-nowrap transition flex-shrink-0 ${selectedCategory === cat
                      ? 'border-transparent bg-[var(--tgia-color-upload)] text-[var(--tgia-color-on-filled)] shadow-md'
                      : 'bg-[var(--tgia-color-surface)] text-[var(--tgia-color-text)] hover:bg-[var(--tgia-color-brand-surface)] border border-[var(--tgia-color-border)]'
                      }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Kit List */}
          <div className="flex-1 overflow-y-auto p-4">
            {filteredKits.length === 0 ? (
              <div className="text-center py-12 text-[var(--tgia-color-text-subtle)]">
                <AlertCircle size={48} className="mx-auto mb-4 text-[var(--tgia-color-placeholder)]" />
                <p className="text-lg font-medium">找不到符合的 Kit</p>
                <p className="text-sm mt-2">請嘗試其他搜索關鍵字</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredKits.map((kit, idx) => (
                  <div
                    key={idx}
                    className="border-2 border-[var(--tgia-color-border-subtle)] bg-[var(--tgia-color-surface)] rounded-lg hover:border-[var(--tgia-color-upload)] hover:bg-[var(--tgia-color-brand-surface)] transition group"
                  >
                    {/* Kit 信息 */}
                    <button
                      onClick={() => onSelect(kit.name)}
                      className="w-full text-left p-4"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-medium text-[var(--tgia-color-text)] group-hover:text-[var(--tgia-color-upload)]">
                            {kit.name}
                          </div>
                          {type === 'library' && (
                            <div className="text-xs text-[var(--tgia-color-text-subtle)] mt-1">
                              <span className="px-2 py-1 bg-[var(--tgia-color-surface-active)] rounded-full text-[var(--tgia-color-text-muted)]">
                                {kit.category}
                              </span>
                            </div>
                          )}
                        </div>
                        <ChevronRight className="text-[var(--tgia-color-placeholder)] group-hover:text-[var(--tgia-color-upload)] transition" size={20} />
                      </div>
                    </button>

                    {/* Apply to all 按钮 */}
                    <div className="border-t border-[var(--tgia-color-border-subtle)] px-4 py-2 bg-[var(--tgia-color-surface-muted)]">
                      <button
                        onClick={() => onApplyToAll(kit.name)}
                        className="w-full text-sm text-[var(--tgia-color-ap-locked-text)] hover:text-[var(--tgia-color-ap-locked-text)] font-medium flex items-center justify-center gap-2 py-1 rounded-md hover:bg-[var(--tgia-color-ap-locked-surface)] transition"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        套用到所有行
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t bg-[var(--tgia-color-surface-muted)] flex flex-wrap gap-3 justify-between items-center">
            <p className="text-sm text-[var(--tgia-color-text-muted)]">
              📌 共 {filteredKits.length} 個選項 | 💡 點擊 Kit 名稱填入當前行
            </p>
            <button
              onClick={onClose}
              className={`${buttonSizeClasses.sm} border-transparent px-6 bg-[var(--tgia-color-text-muted)] text-[var(--tgia-color-on-filled)] hover:bg-[var(--tgia-color-text-subtle)]`}
            >
              關閉
            </button>
          </div>
        </div>
      </div>
    );
  };

  const steps = ['基本驗證', '基本資訊', '委託內容', '送測樣品', '預覽提交'];
  const [salesCodes, setSalesCodes] = useState([]);
  const [isSalesCodesLoading, setIsSalesCodesLoading] = useState(true);
  const [salesCodesLoadError, setSalesCodesLoadError] = useState('');
  const [customerCodes, setCustomerCodes] = useState([]);
  const [isCustomerCodesLoading, setIsCustomerCodesLoading] = useState(true);
  const [customerCodesLoadError, setCustomerCodesLoadError] = useState('');

  // 訂單資料變數群
  const [formData, setFormData] = useState(() => createDefaultOrderFormData({
    salesCode: DEFAULT_SALES_CODE,
    salesPerson: '',
    organization: DEFAULT_ORG,
    principalInvestigator: DEFAULT_PI,
    contactPerson: DEFAULT_CONTACT,
    contactPhone: DEFAULT_PHONE,
    email: DEFAULT_EMAIL
  }));

  useEffect(() => {
    const sendCurrentPresence = () => {
      if (
        authSessionStartedAtRef.current
        && Date.now() - authSessionStartedAtRef.current >= FORCE_LOGOUT_TIMEOUT_MS
      ) {
        void forcedLogoutHandlerRef.current?.('timeout');
        return;
      }

      if (currentStep === 0 && isDocumentHidden()) {
        return;
      }

      sendPresenceHeartbeat({
        sessionId: presenceSessionIdRef.current,
        authState: currentStep > 0 ? 'authenticated' : 'anonymous',
        currentStep,
        salesCode: currentStep > 0 ? formData.salesCode : ''
      });
    };
    const sendClosedPresence = () => {
      sendPresenceHeartbeat({
        sessionId: presenceSessionIdRef.current,
        authState: 'closed',
        currentStep,
        salesCode: currentStep > 0 ? formData.salesCode : ''
      });
    };

    sendCurrentPresence();
    const interval = window.setInterval(sendCurrentPresence, PRESENCE_HEARTBEAT_INTERVAL_MS);
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        sendCurrentPresence();
        return;
      }

      if (currentStep === 0) {
        sendClosedPresence();
      }
    };

    window.addEventListener('focus', sendCurrentPresence);
    window.addEventListener('pagehide', sendClosedPresence);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', sendCurrentPresence);
      window.removeEventListener('pagehide', sendClosedPresence);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentStep, formData.salesCode]);

  useEffect(() => {
    let isMounted = true;

    const fetchConfig = async (url, key, defaultValue) => {
      try {
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const result = await response.json();
        return result?.[key] ?? defaultValue;
      } catch (error) {
        console.error(`${url} 載入失敗:`, error);
        return defaultValue;
      }
    };

    const loadExternalConfig = async () => {
      setIsCatalogConfigLoading(true);
      setCatalogConfigLoadError('');

      const [units, species, experiments, productLine, commonPackagesConfig, analysisTemplatesConfig, analysisDocumentLatency] = await Promise.all([
        fetchConfig('/api/config/units', 'units', []),
        fetchConfig('/api/config/available-species', 'availableSpecies', {}),
        fetchConfig('/api/config/experiment-info', 'experimentInfo', {}),
        fetchConfig('/api/config/product-line', 'productLine', {}),
        fetchConfig('/api/config/common-packages', 'commonPackages', []),
        fetchConfig('/api/config/analysis-templates', 'analysisTemplates', {}),
        fetchConfig('/api/analysis-documents/latency-estimate', 'estimate', null)
      ]);

      if (!isMounted) {
        return;
      }

      const normalizedProductLine = filterPublicProductLineCatalog(
        productLine && typeof productLine === 'object' ? productLine : {}
      );
      const hasProductLine = Object.keys(normalizedProductLine).length > 0;

      setOrganizationOptions(Array.isArray(units) ? units : []);
      setAvailableSpecies(species && typeof species === 'object' ? species : {});
      setExperimentInfo(experiments && typeof experiments === 'object' ? experiments : {});
      setServiceOptionsByCategory(normalizedProductLine);
      if (hasProductLine) {
        setFormData(prev => normalizeSpeciesFields({
          ...prev,
          serviceItems: sanitizeServiceItemsForPublicCatalog(prev.serviceItems, normalizedProductLine)
        }));
      }
      setCommonPackages(Array.isArray(commonPackagesConfig) ? commonPackagesConfig : []);
      setAnalysisTemplatesByServiceType(
        analysisTemplatesConfig && typeof analysisTemplatesConfig === 'object' && !Array.isArray(analysisTemplatesConfig)
          ? analysisTemplatesConfig
          : {}
      );
      setAnalysisDocumentLatencyEstimate(
        analysisDocumentLatency && typeof analysisDocumentLatency === 'object'
          ? analysisDocumentLatency
          : null
      );
      setCatalogConfigLoadError(hasProductLine ? '' : '服務品項設定載入失敗，請確認後端 /api/config/product-line 與 /app/config/product_line.config.json');
      setIsCatalogConfigLoading(false);
    };

    loadExternalConfig();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadSalesCodes = async () => {
      try {
        const response = await fetch('/api/config/sales-codes');

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const result = await response.json();
        const loadedSalesCodes = Array.isArray(result.salesCodes) ? result.salesCodes : [];

        if (!isMounted) {
          return;
        }

        setSalesCodes(loadedSalesCodes);
        setSalesCodesLoadError('');

        setFormData(prev => {
          if (!prev.salesCode || prev.salesPerson) {
            return prev;
          }

          const upperCode = prev.salesCode.toUpperCase();
          const foundSales = loadedSalesCodes.find(s => s.code === upperCode);

          if (!foundSales) {
            return { ...prev, salesCode: upperCode };
          }

          return {
            ...prev,
            salesCode: upperCode,
            salesPerson: foundSales.name
          };
        });
      } catch (error) {
        console.error('業務代碼資料載入失敗:', error);

        if (!isMounted) {
          return;
        }

        setSalesCodes([]);
        setSalesCodesLoadError('業務代碼資料載入失敗，請稍後再試');
      } finally {
        if (isMounted) {
          setIsSalesCodesLoading(false);
        }
      }
    };

    loadSalesCodes();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadCustomerCodes = async () => {
      try {
        const response = await fetch('/api/config/customer-codes');

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const result = await response.json();
        const loadedCustomerCodes = Array.isArray(result.customerCodes) ? result.customerCodes : [];

        if (!isMounted) {
          return;
        }

        setCustomerCodes(loadedCustomerCodes);
        setCustomerCodesLoadError('');

        setFormData(prev => {
          if (!prev.customerCode) {
            return prev;
          }

          const upperCode = prev.customerCode.toUpperCase();
          const foundCustomer = loadedCustomerCodes.find(c => c.code === upperCode);

          if (!foundCustomer) {
            return { ...prev, customerCode: upperCode };
          }

          return {
            ...prev,
            customerCode: upperCode,
            organization: foundCustomer.organization || '',
            principalInvestigator: foundCustomer.principalInvestigator || '',
            contactPerson: foundCustomer.contactPerson || '',
            contactPhone: foundCustomer.contactPhone || '',
            email: foundCustomer.email || '',
            address: foundCustomer.address || '',
            invoiceTitle: foundCustomer.invoiceTitle || '',
            taxId: foundCustomer.taxId || ''
          };
        });
      } catch (error) {
        console.error('客戶代碼資料載入失敗:', error);

        if (!isMounted) {
          return;
        }

        setCustomerCodes([]);
        setCustomerCodesLoadError('客戶代碼資料載入失敗，請在下一步手動填寫客戶資訊');
      } finally {
        if (isMounted) {
          setIsCustomerCodesLoading(false);
        }
      }
    };

    loadCustomerCodes();

    return () => {
      isMounted = false;
    };
  }, []);

  // 處理業務代碼輸入（即時檢查並回報狀況） 
  const handleSalesCodeChange = (code) => {
    const upperCode = code.toUpperCase();
    const foundSales = salesCodes.find(s => s.code === upperCode);

    setStep0Warning('');
    setLoadOrderWarning('');
    setFormData(prev => ({
      ...prev,
      salesCode: upperCode,
      salesPerson: foundSales ? foundSales.name : ''
    }));
  };

  // 處理客戶代碼輸入（即時檢查並回報狀況）
  const handleCustomerCodeChange = (code) => {
    if (!code.trim()) {
      setFormData(prev => ({ ...prev, customerCode: '' }));
      setMessage('');
      return;
    }
    const upperCode = code.toUpperCase();

    if (isCustomerCodesLoading || customerCodesLoadError) {
      setFormData(prev => ({ ...prev, customerCode: upperCode }));
      setMessage(customerCodesLoadError || '客戶代碼資料載入中，請稍候或在下一步手動填寫客戶資訊');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    const foundCustomer = customerCodes.find(c => c.code === upperCode);

    if (foundCustomer) {
      setFormData(prev => ({
        ...prev,
        customerCode: upperCode,
        organization: foundCustomer.organization || '',
        principalInvestigator: foundCustomer.principalInvestigator || '',
        contactPerson: foundCustomer.contactPerson || '',
        contactPhone: foundCustomer.contactPhone || '',
        email: foundCustomer.email || '',
        address: foundCustomer.address || '',
        invoiceTitle: foundCustomer.invoiceTitle || '',
        taxId: foundCustomer.taxId || ''
      }));
      setMessage(`✓ 已帶入客戶資訊：${foundCustomer.organization}`);
      setTimeout(() => setMessage(''), 2000);
    } else {
      setFormData(prev => ({ ...prev, customerCode: upperCode }));
      setMessage('❌ 查無此客戶代碼，請在下一步手動填寫基本資訊');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const [submitted, setSubmitted] = useState(false);
  const [message, setMessage] = useState('');
  const [step0Warning, setStep0Warning] = useState('');
  const [loadOrderWarning, setLoadOrderWarning] = useState('');
  const [step0Tab, setStep0Tab] = useState('create');
  const [orderId, setOrderId] = useState('');
  const [draftOrderCode, setDraftOrderCode] = useState('');
  const [editingOrderCode, setEditingOrderCode] = useState('');
  const [editingOrderSource, setEditingOrderSource] = useState('');
  const [orderCodeToRestore, setOrderCodeToRestore] = useState('');
  const [isLoadingOrder, setIsLoadingOrder] = useState(false);
  const step0TabRefs = useRef({ create: null, load: null, draft: null });
  // const [showSignaturePad, setShowSignaturePad] = useState(false);
  const excelUploadRef = useRef(null);
  const [kitModalOpen, setKitModalOpen] = useState(false);
  const [kitModalConfig, setKitModalConfig] = useState({
    rowIndex: null,
    field: null,
    type: 'library' // 'library' 或 'index'
  });
  const isAnalysisOnlyOrder = (data = formData) => {
    return isAnalysisOnlySampleOrder(data);
  };
  const shouldUseAnalysisSampleSheetLite = (data = formData) => requiresAnalysisSampleSheetLite({
    formData: data,
    serviceCatalog: serviceOptionsByCategory
  });

  // 「分析參數」是品項決定的額外分析設定（目前只有 ChIP-seq：抗體、peak calling mode 與幾個閾值）。
  // 換品項後不再適用的參數會被丟掉，不會殘留在送出的資料裡。
  const getAnalysisParameterDefinitionsForOrder = (data = formData) => getAnalysisParameterDefinitions({
    serviceItems: data?.serviceItems || [],
    serviceCatalog: serviceOptionsByCategory
  });

  // 畫面上的值：數字保留使用者正在打的字串，送出時才轉成 number 並驗證。
  const getResolvedAnalysisParameters = (data = formData) => resolveAnalysisParameterFormValues(
    getAnalysisParameterDefinitionsForOrder(data),
    data?.analysisParameters
  );

  // 選到的 scRNA-seq 實驗類型決定 Sample Sheet 要不要多開 Sample_Barcode / Fastq_Name 欄位。
  // 只有「這張單目前真的跑得到這個實驗類型」時才算數，否則換品項後會留著舊欄位與舊提示。
  const getSampleSheetScRnaRule = (data = formData) => {
    const serviceName = data?.scRNAExperimentInfo?.serviceName || '';
    if (!serviceName) return getScRnaExperimentSampleSheetRule('');

    // 設定檔還沒載入時無法判斷，先照存起來的實驗類型走，避免誤判成「沒有規則」
    // 而把還原回來的訂單多填的列砍掉。
    if (!experimentInfo?.scRNAseq) return getScRnaExperimentSampleSheetRule(serviceName);

    const pipelineInfo = getScRNAseqPipelineInfo({
      serviceItems: data?.serviceItems || [],
      serviceCatalog: serviceOptionsByCategory
    });

    // 已經沒有 scRNA-seq 服務，或這個實驗類型不在目前品項的支援範圍
    const stillApplies = Boolean(pipelineInfo) && isScRNAExperimentSelectable(
      pipelineInfo.platformLabel,
      serviceName,
      getAPPackageConfig(),
      pipelineInfo.serviceValue
    );

    return getScRnaExperimentSampleSheetRule(stillApplies ? serviceName : '');
  };

  const isGeneralNoSampleOrder = isGeneralNoSampleOrderCheck(formData);
  const resolvedSampleFormData = normalizeNoSampleOrderData(formData, {
    hasAnalysisSampleSheetLite: shouldUseAnalysisSampleSheetLite(formData)
  });

  React.useEffect(() => {
    if (skipNextSampleSheetSyncRef.current) {
      skipNextSampleSheetSyncRef.current = false;
      return;
    }

    setFormData(prev => syncSampleSheetRowsToCount(prev));
  }, [
    formData.selectedPackage,
    formData.packageMultiplier,
    formData.sampleCount,
    formData.sampleType,
    formData.sampleInfo?.sampleSheet?.length,
    formData.libraryInfo?.sampleSheet?.length,
    formData.libraryInfo?.librarySampleSheet?.length,
    // 換實驗類型會改變列數規則（例如離開 SC06 時要把多出來的列收回步驟三的數量）
    formData.scRNAExperimentInfo?.serviceName,
  ]);

  // 換分析服務品項後，先前選的實驗類型可能已經不適用：可能是換到不支援它的 pipeline
  // （A-B017 的 SC09 換成 A-B013），也可能是整張單已經沒有 scRNA-seq 服務了。
  // 留著的話物種限制、Sample Sheet 欄位與提示都還會照舊實驗類型跑，所以要主動清掉。
  React.useEffect(() => {
    const selectedExperimentName = formData.scRNAExperimentInfo?.serviceName;
    if (!selectedExperimentName) return;

    // 設定檔還沒載入時不要誤判成「不支援」而清掉還原回來的訂單
    if (!experimentInfo?.scRNAseq) return;

    const pipelineInfo = getScRNAseqPipelineInfo({
      serviceItems: formData.serviceItems || [],
      serviceCatalog: serviceOptionsByCategory
    });

    if (pipelineInfo && isScRNAExperimentSelectable(
      pipelineInfo.platformLabel,
      selectedExperimentName,
      getAPPackageConfig(),
      pipelineInfo.serviceValue
    )) {
      return;
    }

    setFormData(prev => normalizeSpeciesFields({
      ...prev,
      scRNAExperimentInfo: { serviceName: '', description: '' }
    }));
  }, [
    formData.scRNAExperimentInfo?.serviceName,
    formData.serviceItems,
    experimentInfo
  ]);

  //  調整組合倍數（排除不受影響的服務）
  const handlePackageMultiplierChange = (newMultiplier) => {
    if (!formData.selectedPackage) return;

    const multiplier = Math.max(1, parseInt(newMultiplier) || 1);
    const selectedPkg = getSelectedPackage(formData.selectedPackage);
    if (!selectedPkg) {
      setMessage('❌ 找不到此組合配置');
      return;
    }

    setFormData(prev => {
      const sampleState = buildSampleSheetStateForCount({
        prev,
        sampleType: prev.sampleType,
        count: multiplier,
        preserveExisting: true
      });

      return normalizeSpeciesFields({
        ...prev,
        packageMultiplier: multiplier,
        packageQuantity: multiplier,
        ...sampleState,
        serviceItems: syncPackageServiceQuantities(
          prev.serviceItems,
          selectedPkg,
          multiplier
        )
      });
    });
    setFieldErrors({
      sampleSheet: {},
      librarySampleSheet: {},
      libraryDetailSheet: {}
    });

  };

  const hasDraftContent = (data = formData) => {
    if (!data) return false;

    const hasCoreInfo = Boolean(
      data.organization ||
      data.contactPerson ||
      data.email ||
      data.selectedPackage ||
      (Array.isArray(data.selectedServiceCategories) && data.selectedServiceCategories.length > 0)
    );

    const hasServiceDetails = Array.isArray(data.serviceItems)
      && data.serviceItems.some(item => Array.isArray(item.services)
        && item.services.some(service =>
          service?.service ||
          service?.quantity ||
          service?.description ||
          service?.spec
        ));

    const hasSampleDetails = Array.isArray(data.sampleInfo?.sampleSheet)
      && data.sampleInfo.sampleSheet.some(row =>
        row?.sampleName ||
        row?.tubeLabel ||
        row?.expectedSeq ||
        row?.conc ||
        row?.vol
      );

    const hasLibraryDetails = Array.isArray(data.libraryInfo?.sampleSheet)
      && data.libraryInfo.sampleSheet.some(row =>
        row?.sampleName ||
        row?.tubeLabel ||
        row?.expectedSeq ||
        row?.conc ||
        row?.vol
      );

    return hasCoreInfo || hasServiceDetails || hasSampleDetails || hasLibraryDetails;
  };

  latestAutoLogoutSnapshotRef.current = {
    formData,
    resolvedSampleFormData,
    currentStep,
    isLocked,
    isSubmitting,
    isSavingDraft,
    submitted,
    orderId,
    draftOrderCode,
    editingOrderCode,
    editingOrderSource,
    hasDraftContent: hasDraftContent(formData)
  };
  const hasDraftData = latestAutoLogoutSnapshotRef.current.hasDraftContent;

  forcedLogoutHandlerRef.current = async (reason = 'timeout') => {
    const snapshot = latestAutoLogoutSnapshotRef.current;

    if (forcedLogoutInProgressRef.current || snapshot.currentStep === 0) {
      return;
    }

    forcedLogoutInProgressRef.current = true;

    try {
      if (!snapshot.isLocked && !snapshot.isSubmitting && !snapshot.isSavingDraft) {
        const payload = buildSubmitPayload(snapshot.resolvedSampleFormData || snapshot.formData || {});
        payload.formTiming = buildForcedLogoutTimingMetadata();
        payload.isDraft = true;
        const reusableDraftOrderCode = snapshot.editingOrderCode || snapshot.draftOrderCode;
        if (reusableDraftOrderCode) {
          payload.draftOrderCode = reusableDraftOrderCode;
        }

        const response = await fetch('/api/orders/forced-logout', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          let errorMessage = `HTTP ${response.status}`;
          try {
            const result = await response.json();
            errorMessage = result.error || errorMessage;
          } catch (error) {
            // Keep the HTTP status if the backend did not return JSON.
          }
          console.warn('強制登出草稿保存失敗:', errorMessage);
        }
      }
    } finally {
      if (forcedLogoutTimerRef.current) {
        window.clearTimeout(forcedLogoutTimerRef.current);
        forcedLogoutTimerRef.current = null;
      }

      authSessionStartedAtRef.current = null;
      resetToFreshLoginPage();
      forcedLogoutInProgressRef.current = false;
    }
  };

  useEffect(() => {
    if (currentStep === 0) {
      if (forcedLogoutTimerRef.current) {
        window.clearTimeout(forcedLogoutTimerRef.current);
        forcedLogoutTimerRef.current = null;
      }
      authSessionStartedAtRef.current = null;
      return undefined;
    }

    if (!authSessionStartedAtRef.current) {
      const startedAtMs = Date.now();
      authSessionStartedAtRef.current = startedAtMs;
      formTimingRef.current = {
        sessionId: createFormSessionId(),
        startedAtMs,
        startedAt: new Date(startedAtMs).toISOString()
      };
    }

    if (forcedLogoutTimerRef.current) {
      window.clearTimeout(forcedLogoutTimerRef.current);
      forcedLogoutTimerRef.current = null;
    }

    const elapsedMs = Date.now() - authSessionStartedAtRef.current;
    const remainingMs = FORCE_LOGOUT_TIMEOUT_MS - elapsedMs;

    if (remainingMs <= 0) {
      void forcedLogoutHandlerRef.current?.('timeout');
      return undefined;
    }

    forcedLogoutTimerRef.current = window.setTimeout(() => {
      void forcedLogoutHandlerRef.current?.('timeout');
    }, remainingMs);

    return () => {
      if (forcedLogoutTimerRef.current) {
        window.clearTimeout(forcedLogoutTimerRef.current);
        forcedLogoutTimerRef.current = null;
      }
    };
  }, [currentStep]);

  // 🆕 加入離開頁面警示
  React.useEffect(() => {
    // 只在表單有資料且尚未提交時警告
    const handleBeforeUnload = (e) => {
      // 如果已提交或沒有資料，不顯示警告
      if (submitted || !hasDraftData) {
        return;
      }
      // 標準的離開確認訊息
      e.preventDefault();
      e.returnValue = ''; // Chrome 需要設定 returnValue

      // 某些瀏覽器會顯示這個訊息，但大多數現代瀏覽器會顯示預設訊息
      return '您尚未完成訂單需求提交，離開此頁面將會遺失所有填寫的資料。確定要離開嗎？';
    };

    // 註冊事件監聽器
    window.addEventListener('beforeunload', handleBeforeUnload);

    // 清除事件監聽器
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasDraftData, submitted]); // 當資料狀態或 submitted 改變時重新註冊

  const availableServiceCategories = [
    { id: 'qc', label: 'QC (Q)', value: 'QC (Q)' },
    { id: 'eq', label: '萃取/QC (EQ)', value: '萃取/QC (EQ)' },
    { id: 'library', label: '建庫服務 (L)', value: '建庫服務 (L)' },
    { id: 'sequencing', label: '定序服務 (S)', value: '定序服務 (S)' },
    { id: 'analysis', label: '分析服務 (A)', value: '分析服務 (A)' },
    { id: 'package', label: '套組產品 (P)', value: '套組產品 (P)' }
  ];

  // 加入計算函數（支援 AP 套組和一般服務）
  const calculateTotalSequencing = () => {
    let total = 0;

    if (!formData.serviceItems || !Array.isArray(formData.serviceItems)) {
      return 0;
    }

    formData.serviceItems.forEach(item => {
      // === 情況1：一般定序服務 ===
      if (item.category === '定序服務 (S)') {
        if (!item.services || !Array.isArray(item.services)) {
          return;
        }

        item.services.forEach(service => {
          // 🆕 从 product_line.config.json 动态读取
          const sequencingOptions = serviceOptionsByCategory['定序服務 (S)'] || [];
          const serviceConfig = sequencingOptions.find(opt => opt.value === service.service);
          const gbPerUnit = serviceConfig?.sequencingAmount || 0;

          const quantity = parseInt(service.quantity) || 0;
          total += gbPerUnit * quantity;
        });
      }

      // === 情況2：AP 套組產品 ===
      if (item.category === '套組產品 (P)') {
        if (!item.services || !Array.isArray(item.services)) {
          return;
        }

        item.services.forEach(service => {
          if (!service.service) return;

          const apOptions = serviceOptionsByCategory['套組產品 (P)'] || [];
          const apConfig = apOptions.find(opt => opt.value === service.service);

          if (apConfig?.binding?.seqAmountGb) {
            const quantity = parseInt(service.quantity) || 0;
            total += apConfig.binding.seqAmountGb * quantity;
          }
        });
      }
    });

    return total;
  };

  const calculateExpectedSequencing = () => {
    let total = 0;

    if (resolvedSampleFormData.sampleType === 'Library') {
      // 🆕 加入檢查
      if (!formData.libraryInfo?.sampleSheet || !Array.isArray(formData.libraryInfo.sampleSheet)) {
        return 0;
      }

      formData.libraryInfo.sampleSheet.forEach(row => {
        const expectedSeq = parseFloat(row.expectedSeq) || 0;
        total += expectedSeq;
      });
    } else if (!isGeneralNoSampleOrder) {
      // 🆕 加入檢查
      if (!formData.sampleInfo?.sampleSheet || !Array.isArray(formData.sampleInfo.sampleSheet)) {
        return 0;
      }

      formData.sampleInfo.sampleSheet.forEach(row => {
        const expectedSeq = parseFloat(row.expectedSeq) || 0;
        total += expectedSeq;
      });
    }
    return Number(total.toFixed(6));
  };

  // 在 calculateTotalSequencing 函數後面，添加均分定序量的函數
  const handleDistributeSequencing = () => {
    const totalSequencing = calculateTotalSequencing();

    if (totalSequencing === 0) {
      setMessage('❌ 尚未選擇定序服務，無法均分定序量');
      setTimeout(() => setMessage(''), 2000);
      return;
    }

    // 🆕 檢查是否為 AP 套組（套組的定序量是鎖定的）
    const apConfig = getAPPackageConfig();
    if (apConfig?.seqPerSample) {
      setMessage('⚠️ 套組產品的預期定序量已鎖定，無法使用均分功能');
      setTimeout(() => setMessage(''), 2500);
      return;
    }

    if (resolvedSampleFormData.sampleType === 'Library') {
      const sampleRows = Array.isArray(formData.libraryInfo.sampleSheet)
        ? formData.libraryInfo.sampleSheet
        : [];
      const rowCount = sampleRows.length;

      if (rowCount === 0) {
        setMessage('❌ 目前沒有可均分的樣本列');
        setTimeout(() => setMessage(''), 2000);
        return;
      }

      const avgSeq = (totalSequencing / rowCount).toFixed(2);
      const newSampleSheet = sampleRows.map(row => ({
        ...row,
        expectedSeq: avgSeq
      }));

      setFormData(prev => ({
        ...prev,
        libraryInfo: {
          ...prev.libraryInfo,
          sampleSheet: newSampleSheet
        }
      }));

      setMessage(`✅ 已均分定序量：每個樣本 ${avgSeq} GB（共 ${rowCount} 列）`);
      setTimeout(() => setMessage(''), 3000);

    } else if (!isGeneralNoSampleOrder) {
      const sampleRows = Array.isArray(formData.sampleInfo.sampleSheet)
        ? formData.sampleInfo.sampleSheet
        : [];
      const rowCount = sampleRows.length;

      if (rowCount === 0) {
        setMessage('❌ 目前沒有可均分的樣本列');
        setTimeout(() => setMessage(''), 2000);
        return;
      }

      const avgSeq = (totalSequencing / rowCount).toFixed(2);
      const newSampleSheet = sampleRows.map(row => ({
        ...row,
        expectedSeq: avgSeq
      }));

      setFormData(prev => ({
        ...prev,
        sampleInfo: {
          ...prev.sampleInfo,
          sampleSheet: newSampleSheet
        }
      }));

      setMessage(`✅ 已均分定序量：每個樣本 ${avgSeq} GB（共 ${rowCount} 列）`);
      setTimeout(() => setMessage(''), 3000);
    }
  };
  // 處理常用組合選擇
  const handlePackageSelect = (packageId) => {
    if (!packageId) {
      setFormData(prev => normalizeSpeciesFields({
        ...prev,
        selectedPackage: '',
        packageMultiplier: 1,
        packageQuantity: 1,
        sampleCount: '',
        sampleInfo: {
          ...prev.sampleInfo,
          sampleSheet: [createBlankSampleInfoRow(1)]
        },
        libraryInfo: {
          ...prev.libraryInfo,
          sampleSheet: [createBlankLibrarySampleRow(1)],
          librarySampleSheet: [createBlankLibraryDetailRow(1)]
        }
      }));
      setFieldErrors({
        sampleSheet: {},
        librarySampleSheet: {},
        libraryDetailSheet: {}
      });
      return;
    }

    const selectedPkg = getSelectedPackage(packageId);
    if (!selectedPkg) {
      setMessage('❌ 找不到此組合');
      return;
    }

    setFormData(prev => {
      const nextSampleType = selectedPkg.recommendedSampleType || prev.sampleType;
      const sampleState = buildSampleSheetStateForCount({
        prev,
        sampleType: nextSampleType,
        count: 1,
        preserveExisting: false
      });

      return normalizeSpeciesFields({
        ...prev,
        selectedPackage: packageId,
        selectedServiceCategories: selectedPkg.categories || [],
        sampleType: nextSampleType,
        sampleTypeOther: nextSampleType === '其他' ? prev.sampleTypeOther : '',
        packageMultiplier: 1,
        packageQuantity: 1,
        ...sampleState,
        serviceItems: buildPackageServiceItems(selectedPkg, selectedPkg.categories || [], 1)
      });
    });
    setFieldErrors({
      sampleSheet: {},
      librarySampleSheet: {},
      libraryDetailSheet: {}
    });

  };


  // 一鍵清除所有服務類別與常用組合
  const handleClearAllCategories = () => {
    if (formData.selectedServiceCategories.length === 0 && !formData.selectedPackage) {
      setMessage('目前沒有選擇任何服務類別或組合');
      setTimeout(() => setMessage(''), 2000);
      return;
    }

    if (window.confirm('確定要清除所有已選擇的服務類別與常用組合嗎？')) {
      setFormData(prev => normalizeSpeciesFields({
        ...prev,
        selectedPackage: '',
        packageMultiplier: 1,
        packageQuantity: 1,
        selectedServiceCategories: [],
        serviceItems: [createBlankServiceGroup()],
        sampleCount: '',
        sampleInfo: {
          ...prev.sampleInfo,
          sampleSheet: [createBlankSampleInfoRow(1)]
        },
        libraryInfo: {
          ...prev.libraryInfo,
          sampleSheet: [createBlankLibrarySampleRow(1)],
          librarySampleSheet: [createBlankLibraryDetailRow(1)]
        }
      }));
      setFieldErrors({
        sampleSheet: {},
        librarySampleSheet: {},
        libraryDetailSheet: {}
      });
    }
  };

  // 處理服務類別勾選（含聯動）
  const handleServiceCategoryToggle = (categoryValue) => {
    setFormData(prev => {
      let newSelected = [...prev.selectedServiceCategories];

      if (newSelected.includes(categoryValue)) {
        // === 取消勾選 ===

        if (categoryValue === '建庫服務 (L)' || categoryValue === '定序服務 (S)') {
          if (newSelected.includes('萃取/QC (EQ)')) {
            setMessage('❌ 已勾選「萃取/QC」，無法取消此項目');
            setTimeout(() => setMessage(''), 2000);
            return prev;
          }
        }

        if (categoryValue === '定序服務 (S)') {
          if (newSelected.includes('建庫服務 (L)')) {
            setMessage('❌ 已勾選「建庫服務」，無法取消定序服務');
            setTimeout(() => setMessage(''), 2000);
            return prev;
          }
        }

        newSelected = newSelected.filter(c => c !== categoryValue);

      } else {
        // === 勾選 ===

        // 🔥 情況1：QC (Q) - 像 AP 一樣處理（獨立服務）
        if (categoryValue === 'QC (Q)') {
          newSelected = ['QC (Q)'];  // 清除其他，只留 QC
          setMessage('✓ 已選擇純 QC 服務，其他服務類別已自動清除');
          setTimeout(() => setMessage(''), 2500);
          return { ...prev, selectedServiceCategories: newSelected };
        }

        // 🔥 情況2：選擇其他類別時，如果已有 QC，則清除 QC
        if (newSelected.includes('QC (Q)')) {
          newSelected = newSelected.filter(c => c !== 'QC (Q)');
          setMessage('✓ 已取消純 QC 服務，改為其他服務（已包含 QC）');
          setTimeout(() => setMessage(''), 2500);
        }

        // 🔥 情況3：AP 套組處理
        if (categoryValue === '套組產品 (P)') {
          newSelected = ['套組產品 (P)'];
          setMessage('✓ 已選擇套組產品，其他服務類別已自動清除');
          setTimeout(() => setMessage(''), 2500);
          return {
            ...prev,
            selectedServiceCategories: newSelected,
            selectedPackage: '',
            packageMultiplier: 1
          };
        }

        if (newSelected.includes('套組產品 (P)')) {
          newSelected = newSelected.filter(c => c !== '套組產品 (P)');
        }

        // 🔥 情況4：勾選 萃取/QC (EQ)
        if (categoryValue === '萃取/QC (EQ)') {
          newSelected.push(categoryValue);

          const autoChecked = [];
          if (!newSelected.includes('建庫服務 (L)')) {
            newSelected.push('建庫服務 (L)');
            autoChecked.push('建庫服務');
          }
          if (!newSelected.includes('定序服務 (S)')) {
            newSelected.push('定序服務 (S)');
            autoChecked.push('定序服務');
          }
          if (autoChecked.length > 0) {
            setMessage(`✓ 已自動勾選：${autoChecked.join('、')}`);
            setTimeout(() => setMessage(''), 2000);
          }

          return { ...prev, selectedServiceCategories: newSelected };
        }

        // 🔥 情況5：勾選 建庫服務 (L)
        if (categoryValue === '建庫服務 (L)') {
          newSelected.push(categoryValue);

          if (!newSelected.includes('定序服務 (S)')) {
            newSelected.push('定序服務 (S)');
            setMessage('✓ 已自動勾選：定序服務');
            setTimeout(() => setMessage(''), 2000);
          }

          return { ...prev, selectedServiceCategories: newSelected };
        }
        // 🔥 情況6：其他類別（定序、分析等）
        newSelected.push(categoryValue);
      }

      return { ...prev, selectedServiceCategories: newSelected };
    });
  };

  /**
   * 分析參數驗證：抗體必填、數字閾值要在範圍內。錯誤標在欄位下方，
   * 錯誤在收合的「進階閾值」裡時自動展開，不然使用者看不到哪裡錯。
   */
  const validateAnalysisParameters = () => {
    const parameterDefinitions = getAnalysisParameterDefinitionsForOrder(formData);
    const parameterErrors = getAnalysisParameterErrors(
      parameterDefinitions,
      getResolvedAnalysisParameters(formData)
    );
    const firstErrorDefinition = parameterDefinitions.find(definition => parameterErrors[definition.id]);
    if (!firstErrorDefinition) return true;

    setFieldErrors(prev => ({ ...prev, analysisParameters: parameterErrors }));
    if (parameterDefinitions.some(definition => definition.advanced && parameterErrors[definition.id])) {
      setIsAnalysisThresholdsOpen(true);
    }
    setMessage(`❌ ${parameterErrors[firstErrorDefinition.id]}`);
    return false;
  };

  // 驗證當前步驟必填欄位
  const validateStep = (step) => {
    // 清除之前的錯誤
    setFieldErrors({
      sampleSheet: {},
      librarySampleSheet: {},
      libraryDetailSheet: {}
    });

    if (step >= 3) {
      const sampleCountConsistencyError = getAnalysisSampleCountConsistencyError({
        formData,
        serviceCatalog: serviceOptionsByCategory
      });
      if (sampleCountConsistencyError) {
        setMessage(`❌ ${sampleCountConsistencyError}`);
        return false;
      }
    }

    // 送出前（step 4）再擋一次分析參數，避免回到步驟四改壞之後直接從預覽送出。
    if (step >= 4 && !validateAnalysisParameters()) {
      return false;
    }

    switch (step) {
      case 0: // Step 0 驗證
        if (isSalesCodesLoading) {
          setStep0Warning('業務代碼資料載入中，請稍候');
          setMessage('');
          return false;
        }

        if (salesCodesLoadError) {
          setStep0Warning(salesCodesLoadError);
          setMessage('');
          return false;
        }

        if (!formData.salesCode) {
          setStep0Warning('❌ 請輸入業務代碼');
          setMessage('');
          return false;
        }

        // 檢查業務代碼是否存在
        const foundSales = salesCodes.find(s => s.code === formData.salesCode);
        if (!foundSales) {
          setStep0Warning('❌ 業務代碼不存在，請確認後重新輸入');
          setMessage('');
          return false;
        }
        break;
      case 1:
        // 🆕 檢查業務人員
        if (!formData.salesPerson || formData.salesPerson === '請選擇業務人員') {
          setMessage('請選擇業務人員');
          return false;
        }
        if (!formData.organization || !formData.contactPerson || !formData.email) {
          setMessage('請填寫所有必填欄位（標 * 者）');
          return false;
        }
        //  email 格式
        if (!validateEmail(formData.email)) {
          setMessage('Email 格式不正確（例：user@example.com，多 email 可用逗號或分號分隔）');
          return false;
        }
        if (formData.selectedServiceCategories.length === 0) {
          setMessage('請至少勾選一個服務類別');
          return false;
        }
        if (isCatalogConfigLoading) {
          setMessage('服務品項資料載入中，請稍候');
          return false;
        }
        if (catalogConfigLoadError) {
          setMessage(catalogConfigLoadError);
          return false;
        }
        break;
      case 2:
        // 驗證服務項目
        const hasEmptyService = formData.serviceItems.some(item =>
          !item.services.some(s => s.service && s.quantity)
        );
        if (hasEmptyService) {
          setMessage('請填寫所有服務品項與數量');
          return false;
        }

        // 🆕 檢查 S-G000 的最低數量限制
        const sequencingItem = formData.serviceItems.find(item => item.category === '定序服務 (S)');
        if (sequencingItem) {
          const sg001Service = sequencingItem.services.find(s =>
            s.service === 'S-G001 二代定序 - 定序量購買'
          );

          if (sg001Service && sg001Service.quantity) {
            // 🆕 从 JSON 读取最小数量
            const sequencingOptions = serviceOptionsByCategory['定序服務 (S)'] || [];
            const sg001Config = sequencingOptions.find(opt => opt.value === 'S-G001 二代定序 - 定序量購買');
            const minQty = sg001Config?.minQuantity || 5;

            const quantity = parseInt(sg001Service.quantity);
            if (quantity < minQty) {
              setMessage(`❌ S-G001 定序量購買最低數量為 ${minQty} GB`);
              return false;
            }
          }
        }
        // 🆕 檢查建庫服務是否與萃取類型匹配
        const extractionType = getExtractionType();
        const libraryItem = formData.serviceItems.find(item => item.category === '建庫服務 (L)');

        if (extractionType && libraryItem) {
          const hasInvalidLibrary = libraryItem.services.some(s => {
            if (!s.service) return false;

            if (extractionType === 'DNA') {
              // DNA 萃取不能選 L-RN 開頭
              return s.service.startsWith('L-RN');
            }
            if (extractionType === 'RNA') {
              // RNA 萃取只能選 L-RN 開頭
              return !s.service.startsWith('L-RN');
            }
            return false;
          });

          if (hasInvalidLibrary) {
            setMessage(`❌ 建庫服務與萃取類型不符！${extractionType === 'RNA' ? 'RNA 萃取只能選擇 L-RN 開頭的建庫服務' : 'DNA 萃取不能選擇 L-RN 開頭的建庫服務'}`);
            return false;
          }
        }
        break;

      case 3:
        const errors = {
          sampleSheet: {},
          librarySampleSheet: {},
          libraryDetailSheet: {}
        };

        if (resolvedSampleFormData.sampleType === 'Library') {
          const libraryReferenceRows = Array.isArray(formData.libraryInfo.sampleSheet)
            ? formData.libraryInfo.sampleSheet
            : [];
          const validLibraryNames = new Set(
            libraryReferenceRows.map(row => row.sampleName?.trim()).filter(Boolean)
          );
          const validTubeNames = new Set(
            libraryReferenceRows.map(row => row.tubeLabel?.trim()).filter(Boolean)
          );

          // ========== Library 驗證 ==========        
          // === 1. Library (第一個表格) 驗證 ===
          for (let i = 0; i < formData.libraryInfo.sampleSheet.length; i++) {
            const row = formData.libraryInfo.sampleSheet[i];

            if (!row.sampleName || !row.sampleName.trim()) {
              if (!errors.sampleSheet[i]) errors.sampleSheet[i] = {};
              errors.sampleSheet[i].sampleName = 'Library Name 不可為空';
            }
            // 目前先開放 Sample_Name 可用數字開頭，以下驗證暫時保留備用。
            // if (/^\d/.test(row.sampleName.trim())) {
            //   if (!errors.sampleSheet[i]) errors.sampleSheet[i] = {};
            //   errors.sampleSheet[i].sampleName = 'Sample_Name 不可為數字開頭';
            // }

            // 🆕 檢查 Tube_Name (tubeLabel)
            if (!row.tubeLabel || !row.tubeLabel.trim()) {
              if (!errors.sampleSheet[i]) errors.sampleSheet[i] = {};
              errors.sampleSheet[i].tubeLabel = 'Tube Label 不可為空';
            }
            // 🆕 檢查 預期定序量 (expectedSeq)
            if (!row.expectedSeq || !row.expectedSeq.trim()) {
              if (!errors.sampleSheet[i]) errors.sampleSheet[i] = {};
              errors.sampleSheet[i].expectedSeq = '預期定序量不可為空';
            }
          }
          // === 2. Library 第一個表格重複檢查 ===
          const sampleNames1 = formData.libraryInfo.sampleSheet
            .map((row, idx) => ({ name: row.sampleName?.trim(), idx }))
            .filter(item => item.name);

          const duplicateMap1 = {};
          sampleNames1.forEach(item => {
            if (duplicateMap1[item.name]) {
              duplicateMap1[item.name].push(item.idx);
            } else {
              duplicateMap1[item.name] = [item.idx];
            }
          });

          Object.entries(duplicateMap1).forEach(([name, indices]) => {
            if (indices.length > 1) {
              indices.forEach(idx => {
                if (!errors.sampleSheet[idx]) errors.sampleSheet[idx] = {};
                errors.sampleSheet[idx].sampleName = `重複的 Library Name: ${name}`;
              });
            }
          });

          const tubeLabels1 = formData.libraryInfo.sampleSheet
            .map((row, idx) => ({ name: row.tubeLabel?.trim(), idx }))
            .filter(item => item.name);

          const duplicateTubeLabelMap = {};
          tubeLabels1.forEach(item => {
            if (duplicateTubeLabelMap[item.name]) {
              duplicateTubeLabelMap[item.name].push(item.idx);
            } else {
              duplicateTubeLabelMap[item.name] = [item.idx];
            }
          });

          Object.entries(duplicateTubeLabelMap).forEach(([name, indices]) => {
            if (indices.length > 1) {
              indices.forEach(idx => {
                if (!errors.sampleSheet[idx]) errors.sampleSheet[idx] = {};
                errors.sampleSheet[idx].tubeLabel = `重複的 Tube Label: ${name}`;
              });
            }
          });

          // === 3. Library Sample Sheet (第二個表格) 驗證 ===
          for (let i = 0; i < formData.libraryInfo.librarySampleSheet.length; i++) {
            const row = formData.libraryInfo.librarySampleSheet[i];

            if (!row.sampleName || !row.sampleName.trim()) {
              if (!errors.libraryDetailSheet[i]) errors.libraryDetailSheet[i] = {};
              errors.libraryDetailSheet[i].sampleName = 'Sample_Name 不可為空';
            }
            // 目前先開放 Sample_Name 可用數字開頭，以下驗證暫時保留備用。
            // if (/^\d/.test(row.sampleName.trim())) {
            //   if (!errors.libraryDetailSheet[i]) errors.libraryDetailSheet[i] = {};
            //   errors.libraryDetailSheet[i].sampleName = 'Sample_Name 不可為數字開頭';
            // }
            // 🆕 检查 Library Prep Kit (libraryPrepKit)
            if (!row.libraryPrepKit || !row.libraryPrepKit.trim()) {
              if (!errors.libraryDetailSheet[i]) errors.libraryDetailSheet[i] = {};
              errors.libraryDetailSheet[i].libraryPrepKit = 'Library Prep Kit 不可為空';
            }
            // 🆕 檢查 Index 1 (i7) (index1Seq)
            if (!row.index1Seq || !row.index1Seq.trim()) {
              if (!errors.libraryDetailSheet[i]) errors.libraryDetailSheet[i] = {};
              errors.libraryDetailSheet[i].index1Seq = 'Index 1 (i7) 不可為空';
            }

            // 🆕 檢查 Index 2 (i5) (index2Seq)
            if (!row.index2Seq || !row.index2Seq.trim()) {
              if (!errors.libraryDetailSheet[i]) errors.libraryDetailSheet[i] = {};
              errors.libraryDetailSheet[i].index2Seq = 'Index 2 (i5) 不可為空';
            }

            // 🆕 檢查 Library (library)
            if (!row.library || !row.library.trim()) {
              if (!errors.libraryDetailSheet[i]) errors.libraryDetailSheet[i] = {};
              errors.libraryDetailSheet[i].library = 'Library 不可為空';
            } else if (!validLibraryNames.has(row.library.trim())) {
              if (!errors.libraryDetailSheet[i]) errors.libraryDetailSheet[i] = {};
              errors.libraryDetailSheet[i].library = 'Library Name 必須從 Library 資訊選擇';
            }

            if (!row.tubeName || !row.tubeName.trim()) {
              if (!errors.libraryDetailSheet[i]) errors.libraryDetailSheet[i] = {};
              errors.libraryDetailSheet[i].tubeName = 'Tube Name 不可為空';
            } else if (!validTubeNames.has(row.tubeName.trim())) {
              if (!errors.libraryDetailSheet[i]) errors.libraryDetailSheet[i] = {};
              errors.libraryDetailSheet[i].tubeName = 'Tube Name 必須從 Library 資訊選擇';
            }
          }

          // === 4. Library Sample Sheet 重複檢查 ===
          const sampleNames2 = formData.libraryInfo.librarySampleSheet
            .map((row, idx) => ({ name: row.sampleName?.trim(), idx }))
            .filter(item => item.name);

          const duplicateMap2 = {};
          sampleNames2.forEach(item => {
            if (duplicateMap2[item.name]) {
              duplicateMap2[item.name].push(item.idx);
            } else {
              duplicateMap2[item.name] = [item.idx];
            }
          });

          Object.entries(duplicateMap2).forEach(([name, indices]) => {
            if (indices.length > 1) {
              indices.forEach(idx => {
                if (!errors.libraryDetailSheet[idx]) errors.libraryDetailSheet[idx] = {};
                errors.libraryDetailSheet[idx].sampleName = `重複的 Sample_Name: ${name}`;
              });
            }
          });

        } else if (isAnalysisOnlyOrder(formData) && shouldUseAnalysisSampleSheetLite(formData)) {
          // ========== 分析服務（純分析）Lite 驗證 ==========
          const liteScRnaRule = getSampleSheetScRnaRule(formData);
          const liteIsChIPseq = isChIPseqOrder(formData);

          for (let i = 0; i < formData.sampleInfo.sampleSheet.length; i++) {
            const row = formData.sampleInfo.sampleSheet[i];
            // ChIP-seq 的 input 列是選填，留白代表這個樣本沒有送 input。
            const sampleNameRequired = !liteIsChIPseq || isChIPseqSampleNameRequired(i);

            if (sampleNameRequired && (!row.sampleName || !row.sampleName.trim())) {
              if (!errors.sampleSheet[i]) errors.sampleSheet[i] = {};
              errors.sampleSheet[i].sampleName = 'Sample_Name 不可為空';
            }

            // Sample_Barcode / GEX 與 V(D)J 的 fastq 欄位依實驗類型而定，規則集中在 helpers。
            const rowErrors = getScRnaSampleSheetRowErrors({ row, rule: liteScRnaRule });
            if (Object.keys(rowErrors).length > 0) {
              errors.sampleSheet[i] = { ...errors.sampleSheet[i], ...rowErrors };
            }
          }

          const sampleNames = formData.sampleInfo.sampleSheet
            .map((row, idx) => ({ name: row.sampleName?.trim(), idx }))
            .filter(item => item.name);

          const duplicateMap = {};
          sampleNames.forEach(item => {
            if (duplicateMap[item.name]) {
              duplicateMap[item.name].push(item.idx);
            } else {
              duplicateMap[item.name] = [item.idx];
            }
          });

          Object.entries(duplicateMap).forEach(([name, indices]) => {
            if (indices.length > 1) {
              indices.forEach(idx => {
                if (!errors.sampleSheet[idx]) errors.sampleSheet[idx] = {};
                errors.sampleSheet[idx].sampleName = `重複的 Sample_Name: ${name}`;
              });
            }
          });
        } else if (!isGeneralNoSampleOrder) {
          // ========== Sample (DNA/RNA/Cell/Blood) 驗證 ==========
          // 檢查空值
          for (let i = 0; i < formData.sampleInfo.sampleSheet.length; i++) {
            const row = formData.sampleInfo.sampleSheet[i];

            if (!row.sampleName || !row.sampleName.trim()) {
              if (!errors.sampleSheet[i]) errors.sampleSheet[i] = {};
              errors.sampleSheet[i].sampleName = 'Sample_Name 不可為空';
            }
            // 目前先開放 Sample_Name 可用數字開頭，以下驗證暫時保留備用。
            // if (/^\d/.test(row.sampleName.trim())) {
            //   if (!errors.sampleSheet[i]) errors.sampleSheet[i] = {};
            //   errors.sampleSheet[i].sampleName = 'Sample_Name 不可為數字開頭';
            // }

            if (!row.tubeLabel || !row.tubeLabel.trim()) {
              if (!errors.sampleSheet[i]) errors.sampleSheet[i] = {};
              errors.sampleSheet[i].tubeLabel = 'Tube Label 不可為空';
            }
            // 🆕 檢查預期定序量
            if (!row.expectedSeq || !String(row.expectedSeq).trim()) {
              if (!errors.sampleSheet[i]) errors.sampleSheet[i] = {};
              errors.sampleSheet[i].expectedSeq = '預期定序量不可為空';
            }
          }

          // 檢查重複
          const sampleNames = formData.sampleInfo.sampleSheet
            .map((row, idx) => ({ name: row.sampleName?.trim(), idx }))
            .filter(item => item.name);

          const duplicateMap = {};
          sampleNames.forEach(item => {
            if (duplicateMap[item.name]) {
              duplicateMap[item.name].push(item.idx);
            } else {
              duplicateMap[item.name] = [item.idx];
            }
          });

          Object.entries(duplicateMap).forEach(([name, indices]) => {
            if (indices.length > 1) {
              indices.forEach(idx => {
                if (!errors.sampleSheet[idx]) errors.sampleSheet[idx] = {};
                errors.sampleSheet[idx].sampleName = `重複的 Sample_Name: ${name}`;
              });
            }
          });
        }

        // 🆕 scRNA-seq 實驗資訊驗證
        const hasScRNAseqValidation = formData.serviceItems.some(item =>
          item.services.some(svc => isScRNAseqService(svc.service))
        );
        const scRNAseqPipelineInfo = getScRNAseqPipelineInfo({
          serviceItems: formData.serviceItems || [],
          serviceCatalog: serviceOptionsByCategory
        });
        if (hasScRNAseqValidation) {
          const scRNAExperimentOption = findScRNAExperimentOption(
            scRNAseqPipelineInfo?.platformLabel,
            formData.scRNAExperimentInfo?.serviceName,
            getAPPackageConfig()
          );

          if (!scRNAExperimentOption) {
            setMessage('❌ 請選擇 scRNA-seq 實驗類型');
            return false;
          }

          if (!isScRNAExperimentSelectable(
            scRNAseqPipelineInfo?.platformLabel,
            scRNAExperimentOption.ServiceName,
            getAPPackageConfig(),
            scRNAseqPipelineInfo?.serviceValue
          )) {
            setMessage(`❌ 「${scRNAExperimentOption.ServiceName}」不在此分析流程的支援範圍，請重新選擇實驗類型`);
            return false;
          }
        }

        // 如果有錯誤，設置錯誤狀態並返回 false
        if (Object.keys(errors.sampleSheet).length > 0 ||
          Object.keys(errors.libraryDetailSheet).length > 0) {
          setFieldErrors(errors);
          setMessage('❌ 表格中有錯誤，請檢查紅色標示的欄位');
          return false;
        }

        // 🆕 定序量檢查（無論過多過少都不能通過）
        const totalSequencing = calculateTotalSequencing();
        const expectedSequencing = calculateExpectedSequencing();
        const EPSILON = 1e-6;

        if (totalSequencing > 0 && !isGeneralNoSampleOrder) {
          if (Math.abs(expectedSequencing) <= EPSILON) {
            setMessage('❌ 請填寫樣本的預期定序量');
            return false;
          }

          const diff = expectedSequencing - totalSequencing;

          if (diff > EPSILON) {
            setMessage(`❌ 預期定序量超過委託量 ${Number(diff.toFixed(2)).toLocaleString()} GB，請調整樣本預期定序量或增加定序服務數量`);
            return false;
          }

          if (diff < -EPSILON) {
            setMessage(`❌ 預期定序量不足，還有 ${Number(Math.abs(diff).toFixed(2)).toLocaleString()} GB 未分配，請調整樣本預期定序量`);
            return false;
          }
        }

        const apPackageInfo = getAPPackageConfig();
        const speciesMode = getSpeciesMode(formData.serviceItems);
        // 🆕 物種驗證
        if (!apPackageInfo?.binding?.speciesCommon) {
          if (isDetailedSpeciesMode(speciesMode) && formData.speciesSelection === '物種請選擇') {
            setMessage('❌ 請選擇物種');
            return false;
          }

          if (isDetailedSpeciesMode(speciesMode) && formData.speciesSelection !== '其他') {
            const speciesOptions = getSpeciesOptionsForMode(speciesMode, formData.serviceItems, formData);
            const selectedSpecies = findSpeciesOptionBySelection(
              formData.speciesSelectionKey || formData.speciesSelection,
              speciesOptions
            );

            if (!selectedSpecies) {
              setMessage('❌ 請重新選擇目前服務支援的物種');
              return false;
            }
          }

          if (formData.speciesSelection === '其他') {
            if (!canSelectOtherSpecies(speciesMode, formData.serviceItems, formData)) {
              setMessage('❌ 此服務或實驗類型只支援 Human / Mouse，請重新選擇物種');
              return false;
            }

            if (isDetailedSpeciesMode(speciesMode)) {
              if (!formData.speciesCommon?.trim()) {
                setMessage('❌ 請填寫自定義物種的「俗名 (Common Name)」');
                return false;
              }
              if (!formData.speciesScientific?.trim()) {
                setMessage('❌ 請填寫自定義物種的「學名 (Scientific Name)」');
                return false;
              }
            } else {
              // 一般服務的「其他」驗證
              if (!formData.speciesOther?.trim()) {
                setMessage('❌ 請填寫自定義物種名稱');
                return false;
              }
            }
          }
        }

        // 🆕 分析參數驗證（抗體必填、數字閾值要在範圍內）；後端送出時也會再驗一次。
        if (!validateAnalysisParameters()) {
          return false;
        }

        break;
      default:
        break;
    }

    if (step === 0) {
      setStep0Warning('');
    }
    setMessage('');
    return true;
  };

  const nextStep = () => {
    if (isLocked) return;

    if (!validateStep(currentStep)) return;

    const hasExistingServiceItems = hasMeaningfulServiceItems(formData.serviceItems);
    const existingServiceCategories = Array.isArray(formData.serviceItems)
      ? formData.serviceItems.map(item => item?.category).filter(Boolean)
      : [];
    const serviceItemsMatchSelectedCategories =
      existingServiceCategories.length === formData.selectedServiceCategories.length
      && formData.selectedServiceCategories.every(category => existingServiceCategories.includes(category));

    // 從步驟1到步驟2時
    if (currentStep === 1 && formData.selectedServiceCategories.length > 0) {
      if (hasExistingServiceItems && serviceItemsMatchSelectedCategories) {
        setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
        window.scrollTo(0, 0);
        return;
      }

      // 🆕 特殊處理：如果選擇了 AP 套組
      if (formData.selectedServiceCategories.includes('套組產品 (P)')) {
        const newServiceItems = [{
          category: '套組產品 (P)',
          services: [{ service: '', quantity: '1', description: '', spec: '' }],
          libraryType: '無',
          seqSpec: ''
        }];

        setFormData(prev => normalizeSpeciesFields({ ...prev, serviceItems: newServiceItems }));
        setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
        window.scrollTo(0, 0);
        return;
      }

      // 原有的處理邏輯（一般服務類別）
      const categoryOrder = [
        'QC (Q)', '萃取/QC (EQ)', '建庫服務 (L)', '定序服務 (S)', '分析服務 (A)', '套組產品 (P)'
      ];
      const sortedCategories = categoryOrder.filter(cat =>
        formData.selectedServiceCategories.includes(cat)
      );

      let newServiceItems;

      if (formData.selectedPackage) {
        const selectedPkg = safeCommonPackages.find(pkg => pkg.id === formData.selectedPackage);

        if (selectedPkg && selectedPkg.defaultServices) {
          // 按照順序建立 serviceItems
          newServiceItems = sortedCategories.map(category => {
            // 找出該類別的預設服務
            const defaultServices = getPublicPackageDefaultServices(selectedPkg, category);

            if (defaultServices.length > 0) {
              // 有預設服務，帶入預設值
              return {
                category,
                services: defaultServices.map(ds => {
                  // 🆕 查找服务配置
                  const serviceOptions = getServiceOptionsForCategory(category) || [];
                  const serviceConfig = serviceOptions.find(opt =>
                    (typeof opt === 'string' ? opt : opt.value) === ds.service
                  );

                  return {
                    service: ds.service || '',
                    quantity: ds.defaultQuantity || '1',
                    description: serviceConfig?.description || '',  // 🔑
                    spec: serviceConfig?.spec || '',                // 🔑
                    price: '',
                    subtotal: 0
                  };
                }),
                libraryType: '無',
                seqSpec: ''
              };
            } else {
              // 沒有預設服務，給空白
              return {
                category,
                services: [{ service: '', quantity: '', description: '', spec: '' }],
                libraryType: '無',
                seqSpec: ''
              };
            }
          });
        } else {
          // 找不到組合配置，使用空骨架
          console.warn('找不到組合配置，使用空骨架');
          newServiceItems = sortedCategories.map(category => ({
            category,
            services: [{ service: '', quantity: '', description: '', spec: '' }],
            libraryType: '無',
            seqSpec: ''
          }));
        }
      } else {
        // 一般模式：給空骨架
        newServiceItems = sortedCategories.map(category => ({
          category,
          services: [{ service: '', quantity: '', description: '', spec: '' }],
          libraryType: '無',
          seqSpec: ''
        }));
      }

      setFormData(prev => normalizeSpeciesFields({ ...prev, serviceItems: newServiceItems }));
    }


    // 從步驟2進入步驟3時的處理
    if (currentStep === 2) {
      // 🆕 如果選擇了 AP 套組，自動設定樣品類型和定序量
      if (formData.selectedServiceCategories.includes('套組產品 (P)')) {
        const apItem = formData.serviceItems.find(item => item.category === '套組產品 (P)');
        if (apItem && apItem.services[0].service) {
          const selectedAPService = apItem.services[0].service;
          const quantity = parseInt(apItem.services[0].quantity) || 1;
          const apOptions = serviceOptionsByCategory['套組產品 (P)'] || [];
          const apConfig = apOptions.find(opt => opt.value === selectedAPService);

          if (apConfig && apConfig.binding) {
            // 統一處理：將 sampleType 轉為陣列
            const allowedTypes = Array.isArray(apConfig.binding.sampleType)
              ? apConfig.binding.sampleType
              : [apConfig.binding.sampleType];

            // 自動設定樣品類型
            let selectedType = formData.sampleType;
            if (!allowedTypes.includes(formData.sampleType)) {
              selectedType = allowedTypes[0];
            }

            // 🔥 準備更新的欄位（完整版）
            const updates = {
              sampleType: selectedType
            };

            // 保存方式
            if (apConfig.binding.preservationMethod) {
              if (typeof apConfig.binding.preservationMethod === 'object') {
                updates.preservationMethod = apConfig.binding.preservationMethod.default;
              } else {
                updates.preservationMethod = apConfig.binding.preservationMethod;
              }
            }

            // 物種
            if (apConfig.binding.speciesCommon) {
              const apSpeciesMode = getSpeciesMode(formData.serviceItems);
              const apSpeciesOptions = getSpeciesOptionsForMode(apSpeciesMode, formData.serviceItems, formData);
              const matchedSpecies = findSpeciesOptionBySelection(apConfig.binding.speciesCommon, apSpeciesOptions);

              updates.speciesCommon = apConfig.binding.speciesCommon;
              updates.speciesSelection = apConfig.binding.speciesCommon;
              updates.speciesSelectionKey = matchedSpecies ? getSpeciesSelectionKey(matchedSpecies) : apConfig.binding.speciesCommon;
            }

            if (Array.isArray(apConfig.binding.scRNAExperimentServiceNames) && apConfig.binding.scRNAExperimentServiceNames.length > 0) {
              updates.scRNAExperimentInfo = {
                serviceName: '',
                description: ''
              };
            }

            // 寄送方式
            if (apConfig.binding.shippingMethod) {
              if (typeof apConfig.binding.shippingMethod === 'object') {
                updates.shippingMethod = apConfig.binding.shippingMethod.default;
              } else {
                updates.shippingMethod = apConfig.binding.shippingMethod;
              }
            }

            // 🔥 根據數量建立樣本行（同時準備）
            if (apConfig.binding.seqAmountGb) {
              const seqPerSample = apConfig.binding.seqAmountGb;

              const sampleRows = Array.from({ length: quantity }, (_, idx) => ({
                no: idx + 1,
                sampleName: '',
                sampleBarcode: '',
                fastqName: '',
                vdjType: '',
                bcrFastqName: '',
                tcrFastqName: '',
                tubeLabel: '',
                expectedSeq: String(seqPerSample),
                conc: '',
                vol: '',
                ratio260280: '',
                ratio260230: '',
                dqnRqn: '',
                note: ''
              }));

              const libraryRows = Array.from({ length: quantity }, (_, idx) => ({
                no: idx + 1,
                sampleName: '',
                tubeLabel: '',
                conc: '',
                vol: '',
                ngsConc: '',
                expectedSeq: String(seqPerSample),
                note: ''
              }));

              // 🔥 根據樣品類型準備對應的表單資料
              if (selectedType === 'Library') {
                updates.libraryInfo = {
                  ...formData.libraryInfo,  // 保留原有的其他欄位
                  sampleSheet: libraryRows
                };
                updates.sampleCount = quantity;
              } else if (selectedType !== '無送樣') {
                updates.sampleInfo = {
                  ...formData.sampleInfo,  // 保留原有的其他欄位
                  sampleSheet: sampleRows
                };
                updates.sampleCount = quantity;
              }

              Object.assign(updates, syncPackageStateAfterCountChange(formData, quantity));

              const totalSeq = seqPerSample * quantity;
              setMessage(`✓ 已自動設定套組參數並建立 ${quantity} 個樣本，每個樣本定序量 ${seqPerSample} GB，總計 ${totalSeq} GB`);
              setTimeout(() => setMessage(''), 3500);
            }

            // 🔥 關鍵：只呼叫一次 setFormData，一次性更新所有欄位
            setFormData(prev => normalizeSpeciesFields({
              ...prev,
              ...updates
            }));
          }
        }
      } else {
        // 原有的邏輯（非 AP 套組）
        const allowedTypes = getAllowedSampleTypes();
        if (!allowedTypes.includes(formData.sampleType)) {
          setFormData(prev => ({
            ...prev,
            sampleType: allowedTypes[0]
          }));
          setMessage(`✓ 已自動選擇樣品類型：${allowedTypes[0]}`);
          setTimeout(() => setMessage(''), 2000);
        }
      }
    }

    setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
    window.scrollTo(0, 0);
  };

  // 🆕 重新設計：根據具體萃取品項判斷類型
  // 🆕 重新設計：根據具體萃取品項判斷類型
  const getExtractionType = () => {
    const eqItem = formData.serviceItems.find(item => item.category === '萃取/QC (EQ)');

    if (!eqItem || !eqItem.services || eqItem.services.length === 0) {
      return null;
    }

    const extractionOptions = serviceOptionsByCategory['萃取/QC (EQ)'] || [];
    const hasDNA = eqItem.services.some(svc => {
      if (!svc.service) return false;
      const config = extractionOptions.find(opt => opt.value === svc.service);
      return config?.binding?.type === 'DNA';
    });

    const hasRNA = eqItem.services.some(svc => {
      if (!svc.service) return false;
      const config = extractionOptions.find(opt => opt.value === svc.service);
      return config?.binding?.type === 'RNA';
    });

    if (hasDNA && hasRNA) return 'MIXED';
    if (hasDNA) return 'DNA';
    if (hasRNA) return 'RNA';
    return null;
  };  // ✅ 正確結束函數

  // 🆕 根據萃取類型過濾建庫服務選項
  const getFilteredLibraryServices = () => {
    const allServices = serviceOptionsByCategory['建庫服務 (L)'] || [];

    // 🆕 第一步：套組過濾（保留）
    let filtered = allServices;

    if (formData.selectedPackage) {
      const pkg = safeCommonPackages.find(p => p.id === formData.selectedPackage);
      if (pkg && pkg.serviceFilters && pkg.serviceFilters['建庫服務 (L)']) {
        const allowed = pkg.serviceFilters['建庫服務 (L)'].allowedServices || [];
        if (allowed.length > 0) {
          const allowSet = new Set(allowed);
          filtered = filtered.filter(opt => {
            const v = typeof opt === 'string' ? opt : opt.value;
            return allowSet.has(v);
          });
        }
      }
    }

    // 🆕 第二步：根據萃取服務的 binding 配置過濾
    const eqItem = formData.serviceItems.find(item => item.category === '萃取/QC (EQ)');
    if (!eqItem || !eqItem.services || eqItem.services.length === 0) {
      return filtered;
    }

    // 收集所有萃取服務允許的建庫服務
    const allowedLibraries = new Set();
    const extractionOptions = serviceOptionsByCategory['萃取/QC (EQ)'] || [];

    eqItem.services.forEach(svc => {
      if (!svc.service) return;

      const extractionConfig = extractionOptions.find(opt => opt.value === svc.service);
      if (extractionConfig?.binding?.libraryServices) {
        extractionConfig.binding.libraryServices.forEach(lib => {
          allowedLibraries.add(lib);
        });
      }
    });

    // 如果有配置，就過濾
    if (allowedLibraries.size > 0) {
      filtered = filtered.filter(opt => {
        const v = typeof opt === 'string' ? opt : opt.value;
        return allowedLibraries.has(v);
      });
    }

    return filtered;
  };  // ✅ 正確結束函數



  // 依「套組」限制某一類別在 Step2 可選的服務
  const getServiceOptionsForCategory = (category) => {
    const base = serviceOptionsByCategory?.[category] || [];
    let filtered = base;

    // 第一步：根據套組過濾
    const pkg = formData?.selectedPackage
      ? safeCommonPackages.find(p => p.id === formData.selectedPackage)
      : null;

    if (pkg && pkg.serviceFilters && pkg.serviceFilters[category]) {
      const allowed = pkg.serviceFilters[category].allowedServices || [];
      if (allowed.length > 0) {
        const allowSet = new Set(allowed);
        filtered = filtered.filter(opt => {
          const v = typeof opt === 'string' ? opt : opt.value;
          return allowSet.has(v);
        });
      }
    }

    if (category === '萃取/QC (EQ)') {
      filtered = filterExtractionServicesForSequencing(
        filtered,
        formData.serviceItems,
        serviceOptionsByCategory
      );
    }



    // 🆕 第三步：分析服務根據萃取類型過濾
    if (category === '分析服務 (A)') {
      const extractionType = getExtractionType();

      if (extractionType === 'DNA') {
        // DNA 萃取：排除 RNA 相關分析
        filtered = filtered.filter(opt => {
          const v = typeof opt === 'string' ? opt : opt.value;
          const name = v.toLowerCase();
          // 方法1：根據編號前綴（例如 A3 開頭是 RNA）
          // return !v.startsWith('A3');

          // 方法2：根據關鍵字
          return !name.includes('rna') && !name.includes('rnaseq');
        });
      } else if (extractionType === 'RNA') {
        // RNA 萃取：只保留 RNA 相關分析
        filtered = filtered.filter(opt => {
          const v = typeof opt === 'string' ? opt : opt.value;
          const name = v.toLowerCase();
          // 方法1：根據編號前綴
          // return v.startsWith('A3');

          // 方法2：根據關鍵字
          return name.includes('rna') || name.includes('rnaseq');
        });
      }
    }

    return filtered;
  };

  const getServiceQuantityUnitLabel = (serviceName = '') => getServiceQuantityUnit({
    serviceCatalog: serviceOptionsByCategory,
    serviceName
  });

  // 🆕 根據服務類別獲取允許的樣品類型
  const getAllowedSampleTypes = () => {
    const hasQC = formData.selectedServiceCategories.includes('QC (Q)');
    const hasEQ = formData.selectedServiceCategories.includes('萃取/QC (EQ)');
    const hasLibrary = formData.selectedServiceCategories.includes('建庫服務 (L)');
    const hasSequencing = formData.selectedServiceCategories.includes('定序服務 (S)');

    // 🆕 情況0：單純 QC 服務（最優先判斷）
    if (hasQC && formData.selectedServiceCategories.length === 1) {
      const qcItem = formData.serviceItems.find(item => item.category === 'QC (Q)');
      if (qcItem && qcItem.services && qcItem.services[0]?.service) {
        const qcOptions = serviceOptionsByCategory['QC (Q)'] || [];
        const qcConfig = qcOptions.find(opt => opt.value === qcItem.services[0].service);

        if (qcConfig?.binding?.sampleType) {
          return [qcConfig.binding.sampleType];
        }
      }
      // 如果還沒選擇 QC 品項，顯示所有 QC 可能的類型
      return ['DNA', 'RNA', 'Library'];
    }

    // 情況1：有萃取 - 不能選 DNA/RNA/Library（因為萃取是從原始樣本提取）
    if (hasEQ) {
      return ['Cell', 'Blood', 'Tissue', '其他'];
    }

    // 情況2：有建庫但沒萃取 - 不能選 Library（因為建庫是將 DNA/RNA 做成 Library）
    if (hasLibrary && !hasEQ) {
      return ['DNA', 'RNA'];
    }

    // 情況3：只有定序（沒有萃取、沒有建庫）- 只能選 Library
    if (hasSequencing && !hasEQ && !hasLibrary) {
      const sequencingOptions = serviceOptionsByCategory['定序服務 (S)'] || [];
      const sequencingItem = formData.serviceItems.find(item => item.category === '定序服務 (S)');
      const declaredTypes = new Set((sequencingItem?.services || [])
        .map(item => {
          if (!item?.service) return null;
          const seqConfig = sequencingOptions.find(opt => opt.value === item.service);
          const sampleType = seqConfig?.binding?.sampleType;
          return sampleType || null;
        })
        .filter(Boolean));

      if (declaredTypes.size === 1) {
        return Array.from(declaredTypes);
      }

      return ['Library'];
    }

    if (isAnalysisOnlyOrder()) {
      return ['無送樣'];
    }

    // 其他情況或沒有選擇任何服務：全部可選
    return ['無送樣', 'Library', 'DNA', 'RNA', 'Cell', 'Blood', '其他'];
  };

  // 🆕 獲取樣品類型限制的提示訊息
  const getSampleTypeRestrictionMessage = () => {
    const hasQC = formData.selectedServiceCategories.includes('QC (Q)');
    const hasEQ = formData.selectedServiceCategories.includes('萃取/QC (EQ)');
    const hasLibrary = formData.selectedServiceCategories.includes('建庫服務 (L)');
    const hasSequencing = formData.selectedServiceCategories.includes('定序服務 (S)');

    // 🆕 單純 QC 服務
    if (hasQC && formData.selectedServiceCategories.length === 1) {
      const qcItem = formData.serviceItems.find(item => item.category === 'QC (Q)');
      if (qcItem && qcItem.services && qcItem.services[0]?.service) {
        const qcOptions = serviceOptionsByCategory['QC (Q)'] || [];
        const qcConfig = qcOptions.find(opt => opt.value === qcItem.services[0].service);

        if (qcConfig?.binding?.sampleType) {
          return `📌「${qcItem.services[0].service}」服務，樣品類型限制為：${qcConfig.binding.sampleType}`;
        }
      }
      return '📌「QC」服務，請根據 QC 類型選擇對應的樣品類型';
    }

    if (hasEQ) {
      return '📌「萃取/QC」服務，請送原始樣本（Cell、Blood 等）';
    }

    if (hasLibrary && !hasEQ) {
      return '📌「建庫服務」，請送 DNA 或 RNA 樣本';
    }

    if (hasSequencing && !hasEQ && !hasLibrary) {
      return '📌「定序服務」，請送已經完成建庫的 Library';
    }

    if (isAnalysisOnlyOrder()) {
      return '📌「分析服務 (A)」僅需分析作業，樣品類型固定為「無送樣」';
    }

    return '';
  };


  const clearTransientMessages = () => {
    setMessage('');
    setStep0Warning('');
    setLoadOrderWarning('');
  };


  const resetToFreshLoginPage = () => {
    setFormData(createDefaultOrderFormData());
    setAnalysisParameterCustomModes({});
    setIsAnalysisThresholdsOpen(false);
    setIsLocked(false);
    setSubmitted(false);
    setExportReady(false);
    setIsSavingDraft(false);
    setOrderId('');
    setDraftOrderCode('');
    formTimingRef.current = {
      sessionId: createFormSessionId(),
      startedAtMs: Date.now(),
      startedAt: new Date().toISOString()
    };
    presenceSessionIdRef.current = createFormSessionId();
    authSessionStartedAtRef.current = null;
    forcedLogoutInProgressRef.current = false;
    if (forcedLogoutTimerRef.current) {
      window.clearTimeout(forcedLogoutTimerRef.current);
      forcedLogoutTimerRef.current = null;
    }
    skipNextSampleSheetSyncRef.current = true;
    setEditingOrderCode('');
    setEditingOrderSource('');
    setOrderCodeToRestore('');
    setNeedsAnalysisRequest(false);
    setFieldErrors({
      sampleSheet: {},
      librarySampleSheet: {},
      libraryDetailSheet: {}
    });
    setShowOrgSuggestions(false);
    setFilteredOrgs([]);
    clearTransientMessages();
    setStep0Tab('create');
    setCurrentStep(0);
  };

  const prevStep = () => {
    if (isLocked) return;

    clearTransientMessages();
    setCurrentStep(prev => Math.max(prev - 1, 0));
    window.scrollTo(0, 0);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === 'checkbox' ? checked : value;

    if (name === 'sampleType') {
      setFormData(prev => {
        const count = prev.selectedPackage
          ? getPackageDrivenSampleCount(prev)
          : isAnalysisOnlySampleOrder(prev)
            ? getAnalysisOnlySampleCount(prev)
          : 1;

        const sampleState = buildSampleSheetStateForCount({
          prev,
          sampleType: nextValue,
          count,
          preserveExisting: true
        });

        return normalizeSpeciesFields({
          ...prev,
          sampleType: nextValue,
          sampleTypeOther: nextValue === '其他' ? prev.sampleTypeOther : '',
          ...sampleState
        });
      });
      return;
    }

    // 🆕 若選擇了物種下拉選單
    if (name === 'speciesSelection') {
      if (nextValue === '物種請選擇') {
        setFormData(prev => ({
          ...prev,
          ...createEmptySpeciesState()
        }));
        return;
      }

      if (nextValue === '其他') {
        const speciesMode = getSpeciesMode(formData.serviceItems);
        if (!canSelectOtherSpecies(speciesMode, formData.serviceItems, formData)) {
          return;
        }

        setFormData(prev => ({
          ...prev,
          ...createEmptySpeciesState(),
          speciesSelection: '其他',
          speciesSelectionKey: '其他'
        }));
        return;
      }

      const speciesMode = getSpeciesMode(formData.serviceItems);
      const detailedSpeciesList = getSpeciesOptionsForMode(speciesMode, formData.serviceItems, formData);
      const selectedSpecies = findSpeciesOptionBySelection(nextValue, detailedSpeciesList);

      setFormData(prev => normalizeSpeciesFields({
        ...prev,
        speciesSelection: selectedSpecies?.commonName || nextValue,
        speciesSelectionKey: nextValue
      }));
      return;
    }

    if (name === 'sampleCount') {
      setFormData(prev => {
        if (nextValue === '') {
          skipNextSampleSheetSyncRef.current = true;

          return normalizeSpeciesFields({
            ...prev,
            sampleCount: ''
          });
        }

        return normalizeSpeciesFields({
          ...prev,
          sampleCount: nextValue
        });
      });
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: nextValue
    }));

    if (name === 'organization') {
      const filtered = organizationOptions.filter(org =>
        org.toLowerCase().includes(String(nextValue).toLowerCase())
      );
      setFilteredOrgs(filtered);
      setShowOrgSuggestions(String(nextValue).length > 0 && filtered.length > 0);
    }
  };

  const selectOrganization = (org) => {
    setFormData(prev => ({ ...prev, organization: org }));
    setShowOrgSuggestions(false);
  };

  const handleServiceChange = (categoryIndex, serviceIndex, field, value) => {
    const category = formData.serviceItems[categoryIndex].category;

    setFormData(prev => {
      const newServiceItems = [...prev.serviceItems];
      const service = newServiceItems[categoryIndex].services[serviceIndex];

      // 更新欄位值
      service[field] = value;

      // 如果改變了服務名稱，同時保存 description 和 spec
      if (field === 'service' && value) {
        const serviceOptions = getServiceOptionsForCategory(category) || [];
        const serviceConfig = serviceOptions.find(opt =>
          (typeof opt === 'string' ? opt : opt.value) === value
        );

        if (serviceConfig && typeof serviceConfig === 'object') {
          service.description = serviceConfig.description || '';
          service.spec = serviceConfig.spec || '';
        } else {
          service.description = '';
          service.spec = '';
        }

        const selectedPkg = getSelectedPackage(prev.selectedPackage);
        if (selectedPkg) {
          const defaultServicesForCategory = getPublicPackageDefaultServices(selectedPkg, category);
          const defaultService = getPackageDefaultServiceForRow(
            defaultServicesForCategory,
            service,
            serviceIndex
          );

          if (defaultService) {
            const baseQuantity = parseInt(defaultService.defaultQuantity, 10) || 1;
            const multiplier = Math.max(1, parseInt(prev.packageMultiplier, 10) || 1);
            service.quantity = defaultService.excludeFromMultiplier
              ? String(baseQuantity)
              : String(baseQuantity * multiplier);
          }
        }
      }

      // 重新計算小計
      service.subtotal = (parseFloat(service.price) || 0) * (parseInt(service.quantity) || 0);

      // 🆕 【同步邏輯1】當萃取/QC 服務的數量改變時，同步更新對應的建庫服務數量
      if (category === '萃取/QC (EQ)' && field === 'quantity') {
        const extractionServiceName = service.service;
        if (extractionServiceName) {
          const extractionOptions = serviceOptionsByCategory['萃取/QC (EQ)'] || [];
          const extractionConfig = extractionOptions.find(opt => opt.value === extractionServiceName);

          // 如果這個萃取服務有綁定的建庫服務
          if (extractionConfig?.binding?.libraryServices) {
            const boundLibraryServices = new Set(extractionConfig.binding.libraryServices);

            // 找到建庫服務分類
            const libraryItemIndex = newServiceItems.findIndex(item => item.category === '建庫服務 (L)');
            if (libraryItemIndex !== -1) {
              // 遍歷所有建庫服務，同步數量
              newServiceItems[libraryItemIndex].services.forEach(libService => {
                if (boundLibraryServices.has(libService.service)) {
                  libService.quantity = value;
                  // 重新計算建庫服務的小計
                  libService.subtotal = (parseFloat(libService.price) || 0) * (parseInt(value) || 0);
                }
              });
            }
          }
        }
      }

      // 🆕 【同步邏輯2】當選擇建庫服務時，自動設置數量為對應的萃取服務數量
      if (category === '建庫服務 (L)' && field === 'service' && value) {
        const eqItemIndex = newServiceItems.findIndex(item => item.category === '萃取/QC (EQ)');
        if (eqItemIndex !== -1) {
          const extractionOptions = serviceOptionsByCategory['萃取/QC (EQ)'] || [];

          // 找到綁定了這個建庫服務的萃取服務
          newServiceItems[eqItemIndex].services.forEach(eqService => {
            if (eqService.service) {
              const extractionConfig = extractionOptions.find(opt => opt.value === eqService.service);
              if (extractionConfig?.binding?.libraryServices?.includes(value)) {
                // 自動設置建庫服務的數量為萃取服務的數量
                service.quantity = eqService.quantity;
                service.subtotal = (parseFloat(service.price) || 0) * (parseInt(eqService.quantity) || 0);
              }
            }
          });
        }
      }

      if (category === '定序服務 (S)' && field === 'service') {
        const eqItemIndex = newServiceItems.findIndex(item => item.category === '萃取/QC (EQ)');
        const extractionOptions = serviceOptionsByCategory['萃取/QC (EQ)'] || [];
        const allowedExtractionOptions = filterExtractionServicesForSequencing(
          extractionOptions,
          newServiceItems,
          serviceOptionsByCategory
        );

        if (eqItemIndex !== -1 && allowedExtractionOptions.length === 1) {
          const forcedExtraction = allowedExtractionOptions[0];
          const forcedValue = forcedExtraction?.value || '';

          if (forcedValue) {
            newServiceItems[eqItemIndex].services.forEach(eqService => {
              if (eqService.service === forcedValue) return;

              eqService.service = forcedValue;
              eqService.description = forcedExtraction.description || '';
              eqService.spec = forcedExtraction.spec || '';
              eqService.quantity = eqService.quantity || '1';
              eqService.subtotal = (parseFloat(eqService.price) || 0) * (parseInt(eqService.quantity) || 0);
            });
          }
        }
      }

      const nextState = {
        ...prev,
        serviceItems: newServiceItems
      };

      if (category === '分析服務 (A)' && isAnalysisOnlySampleOrder(nextState)) {
        const sampleCount = getAnalysisOnlySampleCount(nextState);
        return normalizeSpeciesFields({
          ...nextState,
          ...buildSampleSheetStateForCount({
            prev: nextState,
            sampleType: '無送樣',
            count: sampleCount,
            preserveExisting: true
          })
        });
      }

      return normalizeSpeciesFields(nextState);
    });
  };


  const addService = (itemIndex) => {
    if (isLocked) {
      setMessage('⚠️ 此訂單已提交並鎖定，無法新增品項');
      return;
    }

    const items = [...formData.serviceItems];
    const category = items[itemIndex].category;

    if (!canAddServiceForCategory(category)) {
      setMessage(`此類別「${category}」不允許新增品項`);
      return;
    }

    const allowedOpts = getServiceOptionsForCategory(category);
    const first = allowedOpts[0];

    const value = first
      ? (typeof first === 'string' ? first : (first.value ?? first.label))
      : '';

    const firstDesc = first && typeof first === 'object'
      ? (first.description || '')
      : '';

    const firstSpec = first && typeof first === 'object'
      ? (first.spec || '')
      : '';

    items[itemIndex].services.push({
      service: value,
      quantity: '1',
      description: firstDesc,
      spec: firstSpec,
      price: '',
      subtotal: 0
    });

    setFormData(prev => normalizeSpeciesFields({ ...prev, serviceItems: items }));
  };


  const removeService = (itemIndex, serviceIndex) => {
    const newItems = [...formData.serviceItems];
    if (newItems[itemIndex].services.length === 1) {
      alert('至少需要保留一項服務品項');
      return;
    }
    newItems[itemIndex].services = newItems[itemIndex].services.filter((_, i) => i !== serviceIndex);
    setFormData(prev => normalizeSpeciesFields({ ...prev, serviceItems: newItems }));
  };

  // 🆕 修改 handleLibrarySampleSheetChange
  const handleLibrarySampleSheetChange = (index, field, value) => {
    const newSampleSheet = [...formData.libraryInfo.sampleSheet];

    // 🆕 如果是 AP 套組，阻止修改 expectedSeq
    if (field === 'expectedSeq') {
      const apConfig = getAPPackageConfig();
      if (apConfig?.seqPerSample) {
        setMessage(`⚠️ 套組產品的預期定序量已鎖定為 ${apConfig.seqPerSample} GB，無法修改`);
        setTimeout(() => setMessage(''), 2500);
        return; // 阻止修改
      }
    }

    newSampleSheet[index][field] = field === 'sampleName' ? sanitizeSampleName(value) : value;
    const count = newSampleSheet.length;

    setFormData(prev => normalizeSpeciesFields({
      ...prev,
      libraryInfo: {
        ...prev.libraryInfo,
        sampleSheet: newSampleSheet,
        librarySampleSheet: (prev.libraryInfo.librarySampleSheet || []).map(row =>
          syncLibraryDetailReferenceFields(row, newSampleSheet, 'paste')
        )
      },
      sampleCount: count
    }));
  };

  const getClipboardRows = (text = '') => (
    String(text)
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .split('\n')
      .filter(row => row.trim())
  );

  const shouldHandleTablePaste = (text = '') => {
    const normalizedText = String(text)
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n');
    const rows = getClipboardRows(normalizedText);

    return normalizedText.includes('\t') || rows.length > 1;
  };

  const handleTablePaste = (e, startIndex) => {
    const pastedText = e.clipboardData.getData('text');
    if (!shouldHandleTablePaste(pastedText)) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    const rows = getClipboardRows(pastedText);

    const newSampleSheet = [...formData.libraryInfo.sampleSheet];

    rows.forEach((row, rowIndex) => {
      const columns = row.split('\t');
      const targetIndex = startIndex + rowIndex;

      // 智能判斷：如果第一欄是數字，就跳過（認為是序號）
      //const startCol = /^\d+$/.test(columns[0]) ? 1 : 0;
      const startCol = 0;
      while (targetIndex >= newSampleSheet.length) {
        newSampleSheet.push({
          no: newSampleSheet.length + 1,
          sampleName: '',
          tubeLabel: '',
          conc: '',
          vol: '',
          ngsConc: '',
          expectedSeq: '',
          note: ''
        });
      }

      if (columns.length > startCol) {
        newSampleSheet[targetIndex] = {
          no: targetIndex + 1,
          sampleName: sanitizeSampleName(columns[startCol] || ''),  // 🆕 清理
          tubeLabel: columns[startCol + 1] || '',
          conc: columns[startCol + 2] || '',
          vol: columns[startCol + 3] || '',
          ngsConc: columns[startCol + 4] || '',
          expectedSeq: columns[startCol + 5] || '',
          note: columns[startCol + 6] || ''
        };
      }
    });

    const count = newSampleSheet.length;

    setFormData(prev => normalizeSpeciesFields({
      ...prev,
      libraryInfo: {
        ...prev.libraryInfo,
        sampleSheet: newSampleSheet,
        librarySampleSheet: (prev.libraryInfo.librarySampleSheet || []).map(row =>
          syncLibraryDetailReferenceFields(row, newSampleSheet, 'paste')
        )
      },
      sampleCount: count,
      ...syncPackageStateAfterCountChange(prev, count)
    }));
    setFieldErrors({
      sampleSheet: {},
      librarySampleSheet: {},
      libraryDetailSheet: {}
    });

    setMessage(`已貼上 ${rows.length} 行資料`);
    setTimeout(() => setMessage(''), 2000);
  };

  // 🆕 修改 addLibrarySampleSheetRow
  const addLibrarySampleSheetRow = () => {
    const newRow = {
      no: formData.libraryInfo.sampleSheet.length + 1,
      sampleName: '',
      tubeLabel: '',
      conc: '',
      vol: '',
      ngsConc: '',
      expectedSeq: '',
      note: ''
    };
    const newSampleSheet = [...formData.libraryInfo.sampleSheet, newRow];

    const count = newSampleSheet.length;

    setFormData(prev => normalizeSpeciesFields({
      ...prev,
      libraryInfo: {
        ...prev.libraryInfo,
        sampleSheet: newSampleSheet,
        librarySampleSheet: (prev.libraryInfo.librarySampleSheet || []).map(row =>
          syncLibraryDetailReferenceFields(row, newSampleSheet, 'paste')
        )
      },
      sampleCount: count,
      ...syncPackageStateAfterCountChange(prev, count)
    }));
    setFieldErrors({
      sampleSheet: {},
      librarySampleSheet: {},
      libraryDetailSheet: {}
    });
    setMessage(getRowCountChangeNotice('Library', '新增'));
    setTimeout(() => setMessage(''), 3000);
  };


  // 🆕 修改 removeLibrarySampleSheetRow
  const removeLibrarySampleSheetRow = (index) => {
    if (formData.libraryInfo.sampleSheet.length === 1) {
      alert('至少需要保留一行');
      return;
    }
    const newSampleSheet = formData.libraryInfo.sampleSheet.filter((_, i) => i !== index);

    const count = newSampleSheet.length;

    setFormData(prev => normalizeSpeciesFields({
      ...prev,
      libraryInfo: {
        ...prev.libraryInfo,
        sampleSheet: newSampleSheet,
        librarySampleSheet: (prev.libraryInfo.librarySampleSheet || []).map(row =>
          syncLibraryDetailReferenceFields(row, newSampleSheet, 'paste')
        )
      },
      sampleCount: count,
      ...syncPackageStateAfterCountChange(prev, count)
    }));
    setFieldErrors({
      sampleSheet: {},
      librarySampleSheet: {},
      libraryDetailSheet: {}
    });
    setMessage(getRowCountChangeNotice('Library', '刪除'));
    setTimeout(() => setMessage(''), 3000);
  };

  const findLibraryReferenceByLibraryName = (sampleSheet = [], libraryName = '') => {
    const normalizedLibraryName = String(libraryName || '').trim();
    if (!normalizedLibraryName) return null;
    const references = Array.isArray(sampleSheet) ? sampleSheet : [];
    return references.find(sample => sample?.sampleName === normalizedLibraryName) || null;
  };

  const findLibraryReferenceByTubeName = (sampleSheet = [], tubeName = '') => {
    const normalizedTubeName = String(tubeName || '').trim();
    if (!normalizedTubeName) return null;
    const references = Array.isArray(sampleSheet) ? sampleSheet : [];
    return references.find(sample => sample?.tubeLabel === normalizedTubeName) || null;
  };

  const syncLibraryDetailReferenceFields = (row = {}, sampleSheet = [], preferredField = 'library') => {
    const libraryValue = String(row.library || '').trim();
    const tubeNameValue = String(row.tubeName || '').trim();
    const libraryMatch = findLibraryReferenceByLibraryName(sampleSheet, libraryValue);
    const tubeNameMatch = findLibraryReferenceByTubeName(sampleSheet, tubeNameValue);

    let matchedSample = null;
    if (preferredField === 'tubeName') {
      matchedSample = tubeNameValue ? (tubeNameMatch || libraryMatch) : null;
    } else if (preferredField === 'library') {
      matchedSample = libraryValue ? (libraryMatch || tubeNameMatch) : null;
    } else {
      matchedSample = libraryMatch || tubeNameMatch;
    }

    return {
      ...row,
      library: matchedSample?.sampleName || '',
      tubeName: matchedSample?.tubeLabel || ''
    };
  };

  const handleLibraryDetailChange = (rowIndex, field, value) => {
    setFormData(prev => {
      const newLibrarySampleSheet = [...prev.libraryInfo.librarySampleSheet];

      // 🆕 如果是 sampleName 字段，清理特殊字符
      if (field === 'sampleName') {
        newLibrarySampleSheet[rowIndex][field] = sanitizeSampleName(value);
      } else {
        newLibrarySampleSheet[rowIndex][field] = value;
      }

      const sampleSheet = prev.libraryInfo.sampleSheet || [];

      // 🆕 双向同步逻辑
      if (field === 'library' || field === 'tubeName') {
        newLibrarySampleSheet[rowIndex] = syncLibraryDetailReferenceFields(
          newLibrarySampleSheet[rowIndex],
          sampleSheet,
          field
        );
      }

      return {
        ...prev,
        libraryInfo: {
          ...prev.libraryInfo,
          librarySampleSheet: newLibrarySampleSheet
        }
      };
    });
  };

  // Kit 选择处理函数
  const handleKitSelect = (kitName) => {
    const { rowIndex, field } = kitModalConfig;
    if (rowIndex !== null && field) {
      handleLibraryDetailChange(rowIndex, field, kitName);
    }
    setKitModalOpen(false);
  };

  // Kit 应用到所有行
  // ✅ 正确
  const handleKitApplyToAll = (kitName) => {
    const { field } = kitModalConfig;
    if (!field) return;

    const confirmed = window.confirm(`確定要將「${kitName}」套用到所有行的 ${field === 'libraryPrepKit' ? 'Library Prep Kit' : 'Index Adapter Kit'} 嗎？`);

    if (confirmed) {
      const rowCount = formData.libraryInfo.librarySampleSheet.length;  // 先获取数量

      setFormData(prev => {
        const newLibrarySampleSheet = prev.libraryInfo.librarySampleSheet.map(row => ({
          ...row,
          [field]: kitName
        }));

        return {
          ...prev,
          libraryInfo: {
            ...prev.libraryInfo,
            librarySampleSheet: newLibrarySampleSheet
          }
        };
      });

      setMessage(`✅ 已將「${kitName}」套用到所有 ${rowCount} 行`);  // 使用预先获取的数量
      setTimeout(() => setMessage(''), 2000);
      setKitModalOpen(false);
    }
  };

  // 打开 Kit 选择弹窗
  const openKitModal = (rowIndex, field, type = 'library') => {
    setKitModalConfig({ rowIndex, field, type });
    setKitModalOpen(true);
  };

  const handleLibraryDetailTablePaste = (e, startRowIndex) => {
    const pastedData = e.clipboardData.getData('text');
    if (!shouldHandleTablePaste(pastedData)) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    const rows = getClipboardRows(pastedData);

    setFormData(prev => {
      const newLibrarySampleSheet = [...prev.libraryInfo.librarySampleSheet];
      const sampleSheet = prev.libraryInfo.sampleSheet || [];

      rows.forEach((row, rowIdx) => {
        const cols = row.split('\t');
        const targetIndex = startRowIndex + rowIdx;

        // 確保有足夠的行數
        while (newLibrarySampleSheet.length <= targetIndex) {
          newLibrarySampleSheet.push({
            no: newLibrarySampleSheet.length + 1,
            sampleName: '',
            libraryPrepKit: '',
            indexAdapterKit: '',
            setWellPosition: '',
            index1Seq: '',
            index2Seq: '',
            note: '',
            library: '',
            tubeName: ''
          });
        }

        // 填入各欄位
        if (cols[0]) newLibrarySampleSheet[targetIndex].sampleName = cols[0].trim();
        if (cols[1]) newLibrarySampleSheet[targetIndex].libraryPrepKit = cols[1].trim();
        if (cols[2]) newLibrarySampleSheet[targetIndex].indexAdapterKit = cols[2].trim();
        if (cols[3]) newLibrarySampleSheet[targetIndex].setWellPosition = cols[3].trim();
        if (cols[4]) newLibrarySampleSheet[targetIndex].index1Seq = cols[4].trim();
        if (cols[5]) newLibrarySampleSheet[targetIndex].index2Seq = cols[5].trim();
        if (cols[6]) newLibrarySampleSheet[targetIndex].note = cols[6].trim();
        if (cols[7]) newLibrarySampleSheet[targetIndex].library = sanitizeSampleName(cols[7].trim());

        newLibrarySampleSheet[targetIndex] = syncLibraryDetailReferenceFields(
          newLibrarySampleSheet[targetIndex],
          sampleSheet,
          'library'
        );
      });

      return {
        ...prev,
        libraryInfo: {
          ...prev.libraryInfo,
          librarySampleSheet: newLibrarySampleSheet
        }
      };
    });

    console.log(`✅ 已貼上 ${rows.length} 行資料並完成同步`);
  };

  const addLibraryDetailRow = () => {
    setFormData(prev => {
      const currentRows = Array.isArray(prev.libraryInfo?.librarySampleSheet)
        ? prev.libraryInfo.librarySampleSheet
        : [];
      const nextRows = [
        ...currentRows,
        createBlankLibraryDetailRow(currentRows.length + 1)
      ];

      return {
        ...prev,
        libraryInfo: {
          ...prev.libraryInfo,
          librarySampleSheet: nextRows
        }
      };
    });
    setFieldErrors({
      sampleSheet: {},
      librarySampleSheet: {},
      libraryDetailSheet: {}
    });
  };

  const removeLibraryDetailRow = (index) => {
    if (formData.libraryInfo.librarySampleSheet.length === 1) {
      alert('至少需要保留一行');
      return;
    }
    const newLibrarySheet = formData.libraryInfo.librarySampleSheet
      .filter((_, i) => i !== index)
      .map((row, rowIndex) => ({ ...row, no: rowIndex + 1 }));
    setFormData(prev => ({
      ...prev,
      libraryInfo: {
        ...prev.libraryInfo,
        librarySampleSheet: newLibrarySheet
      }
    }));
    setFieldErrors({
      sampleSheet: {},
      librarySampleSheet: {},
      libraryDetailSheet: {}
    });
  };


  const handleExcelUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const XLSX = await import('xlsx');
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const fileName = file.name.toLowerCase();

      if (fileName.includes('library')) {
        // === Library 範本處理 ===
        console.log('📊 Library 工作表:', workbook.SheetNames);

        // 工作表1: Sample Sheet
        if (workbook.SheetNames[0]) {
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          console.log('📋 Sample Sheet 原始資料:', jsonData);

          const newSampleSheet = [];
          jsonData.forEach((row, index) => {
            if (index > 3 && row && row.length > 1) {
              const hasSeqNum = typeof row[0] === 'number' || !isNaN(row[0]);
              const startIdx = hasSeqNum ? 1 : 0;

              const sampleName = sanitizeSampleName(row[startIdx] ?? '');

              if (sampleName &&
                sampleName !== 'Sample_Name' &&
                !sampleName.startsWith('Sample_Nam')) {
                newSampleSheet.push({
                  no: newSampleSheet.length + 1,
                  sampleName: sampleName,
                  tubeLabel: String(row[startIdx + 1] ?? ''),
                  conc: String(row[startIdx + 2] ?? ''),
                  vol: String(row[startIdx + 3] ?? ''),
                  ngsConc: String(row[startIdx + 4] ?? ''),
                  expectedSeq: String(row[startIdx + 5] ?? ''),
                  note: String(row[startIdx + 6] ?? '')
                });
              }
            }
          });

          console.log('✅ 解析後的 Sample Sheet:', newSampleSheet);

          if (newSampleSheet.length > 0) {
            const count = newSampleSheet.length;

            setFormData(prev => ({
              ...prev,
              libraryInfo: {
                ...prev.libraryInfo,
                sampleSheet: newSampleSheet,
                librarySampleSheet: (prev.libraryInfo.librarySampleSheet || []).map(row =>
                  syncLibraryDetailReferenceFields(row, newSampleSheet, 'paste')
                )
              },
              sampleCount: count
            }));
          }
        }

        // 工作表2: Library Sample Sheet
        if (workbook.SheetNames[1]) {
          const worksheet = workbook.Sheets[workbook.SheetNames[1]];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          console.log('📋 Library Sample Sheet 原始資料:', jsonData);

          setFormData(prev => {
            // 🔑 先取得 Sample Sheet 資料（用於同步）
            const sampleSheet = prev.libraryInfo.sampleSheet || [];

            const newLibrarySheet = [];
            jsonData.forEach((row, index) => {
              if (index > 3 && row && row.length > 1) {
                const hasSeqNum = typeof row[0] === 'number' || !isNaN(row[0]);
                const startIdx = hasSeqNum ? 1 : 0;

                const sampleName = sanitizeSampleName(row[startIdx] ?? '');

                if (sampleName &&
                  sampleName !== 'Sample_Name' &&
                  !sampleName.startsWith('Sample_Nam')) {

                  const rowData = {
                    no: newLibrarySheet.length + 1,
                    sampleName: sampleName,
                    libraryPrepKit: String(row[startIdx + 1] ?? ''),
                    indexAdapterKit: String(row[startIdx + 2] ?? ''),
                    setWellPosition: String(row[startIdx + 3] ?? ''),
                    index1Seq: String(row[startIdx + 4] ?? ''),
                    index2Seq: String(row[startIdx + 5] ?? ''),
                    note: String(row[startIdx + 6] ?? ''),
                    // library: String(row[startIdx + 7] ?? ''),
                    library: sanitizeSampleName(row[startIdx + 7] ?? ''),
                    tubeName: ''
                  };

                  newLibrarySheet.push(syncLibraryDetailReferenceFields(rowData, sampleSheet, 'library'));
                }
              }
            });

            console.log('✅ 解析後的 Library Sample Sheet (含同步):', newLibrarySheet);

            if (newLibrarySheet.length > 0) {
              return {
                ...prev,
                libraryInfo: {
                  ...prev.libraryInfo,
                  librarySampleSheet: newLibrarySheet
                }
              };
            }

            return prev;
          });
        }

        setMessage(`✅ Library Excel 檔案已匯入並完成同步`);

      } else if (fileName.includes('sample')) {
        // === Sample 範本處理 ===
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        console.log('📋 Sample 原始資料:', jsonData);

        const newSampleSheet = [];
        jsonData.forEach((row, index) => {
          if (index > 4 && row && row.length > 1) {
            const hasSeqNum = typeof row[0] === 'number' || !isNaN(row[0]);
            const startIdx = hasSeqNum ? 1 : 0;

            const sampleName = sanitizeSampleName(row[startIdx] ?? '');

            if (sampleName &&
              sampleName !== 'Sample_Name' &&
              !sampleName.startsWith('Sample_Nam')) {
              newSampleSheet.push({
                no: newSampleSheet.length + 1,
                sampleName: sampleName,
                sampleBarcode: '',
                fastqName: '',
                vdjType: '',
                bcrFastqName: '',
                tcrFastqName: '',
                tubeLabel: String(row[startIdx + 1] ?? ''),
                expectedSeq: String(row[startIdx + 2] ?? ''),
                conc: String(row[startIdx + 3] ?? ''),
                vol: String(row[startIdx + 4] ?? ''),
                ratio260280: String(row[startIdx + 5] ?? ''),
                ratio260230: String(row[startIdx + 6] ?? ''),
                dqnRqn: String(row[startIdx + 7] ?? ''),
                note: String(row[startIdx + 8] ?? '')
              });
            }
          }
        });

        console.log('✅ 解析後的 Sample:', newSampleSheet);

        if (newSampleSheet.length > 0) {
          const count = newSampleSheet.length;

          setFormData(prev => ({
            ...prev,
            sampleInfo: {
              ...prev.sampleInfo,
              sampleSheet: newSampleSheet
            },
            sampleCount: count
          }));
          setMessage(`✅ Sample Excel 檔案已匯入 (${count} 個樣本)`);
        } else {
          alert('未讀取到有效資料，請確認檔案格式');
        }
      } else {
        alert('檔案名稱必須包含 "library" 或 "sample"');
        return;
      }

      setTimeout(() => setMessage(''), 2000);
      e.target.value = '';
    } catch (error) {
      console.error('❌ 上傳錯誤:', error);
      alert('上傳失敗：' + error.message);
    }
  };

  const clearSampleSheet = () => {
    if (window.confirm('確定要清空所有 Sample Sheet 資料嗎？')) {
      setFormData(prev => normalizeSpeciesFields({
        ...prev,
        libraryInfo: {
          ...prev.libraryInfo,
          sampleSheet: [createBlankLibrarySampleRow(1)],
          librarySampleSheet: (prev.libraryInfo.librarySampleSheet || []).map(row =>
            syncLibraryDetailReferenceFields(row, [], 'paste')
          )
        },
        sampleCount: 1,
        ...syncPackageStateAfterCountChange(prev, 1)
      }));
      setFieldErrors({
        sampleSheet: {},
        librarySampleSheet: {},
        libraryDetailSheet: {}
      });
    }
  };

  const clearLibrarySheet = () => {
    if (window.confirm('確定要清空所有 Library Sample Sheet 資料嗎？')) {
      setFormData(prev => ({
        ...prev,
        libraryInfo: {
          ...prev.libraryInfo,
          librarySampleSheet: [{
            no: 1,
            sampleName: '',
            libraryPrepKit: '',
            indexAdapterKit: '',
            setWellPosition: '',
            index1Seq: '',
            index2Seq: '',
            note: '',
            library: '',
            tubeName: ''

          }]
        }
      }));
      setFieldErrors({
        sampleSheet: {},
        librarySampleSheet: {},
        libraryDetailSheet: {}
      });
    }
  };

  const clearSampleInfoSheet = () => {
    if (window.confirm('確定要清空所有 Sample 資料嗎？')) {
      setFormData(prev => {
        const resetCount = isAnalysisOnlySampleOrder(prev)
          ? getAnalysisOnlySampleCount(prev)
          : 1;

        return normalizeSpeciesFields({
          ...prev,
          ...buildSampleSheetStateForCount({
            prev,
            sampleType: prev.sampleType || '無送樣',
            count: resetCount,
            preserveExisting: false
          }),
          ...syncPackageStateAfterCountChange(prev, resetCount)
        });
      });
      setFieldErrors({
        sampleSheet: {},
        librarySampleSheet: {},
        libraryDetailSheet: {}
      });
    }
  };

  const clearSampleSheetFieldError = (index, field) => {
    setFieldErrors(prev => {
      if (!prev.sampleSheet?.[index]?.[field]) return prev;

      const sampleSheet = { ...prev.sampleSheet };
      const rowErrors = { ...sampleSheet[index] };
      delete rowErrors[field];

      if (Object.keys(rowErrors).length === 0) {
        delete sampleSheet[index];
      } else {
        sampleSheet[index] = rowErrors;
      }

      return { ...prev, sampleSheet };
    });
  };

  /**
   * 換 V(D)J 類型時，順手清掉這個樣本沒有做的 library 的 fastq 欄位，
   * 免得畫面上灰掉的欄位還留著舊值。
   */
  const handleVdjTypeChange = (index, value, rule) => {
    const newSampleSheet = [...formData.sampleInfo.sampleSheet];
    newSampleSheet[index] = sanitizeScRnaSampleSheetRow(
      { ...newSampleSheet[index], vdjType: value },
      rule
    );

    setFormData(prev => ({
      ...prev,
      sampleInfo: {
        ...prev.sampleInfo,
        sampleSheet: newSampleSheet
      }
    }));

    VDJ_LIBRARY_FASTQ_FIELDS.forEach(({ field }) => clearSampleSheetFieldError(index, field));
    clearSampleSheetFieldError(index, 'vdjType');
  };

  // 🆕 修改 handleSampleSheetChange
  const handleSampleSheetChange = (index, field, value) => {
    const newSampleSheet = [...formData.sampleInfo.sampleSheet];

    // 🆕 如果是 AP 套組，阻止修改 expectedSeq
    if (field === 'expectedSeq') {
      const apConfig = getAPPackageConfig();
      if (apConfig?.seqPerSample) {
        setMessage(`⚠️ 套組產品的預期定序量已鎖定為 ${apConfig.seqPerSample} GB，無法修改`);
        setTimeout(() => setMessage(''), 2500);
        return; // 阻止修改
      }
    }

    newSampleSheet[index][field] = field === 'sampleName' ? sanitizeSampleName(value) : value;

    // ChIP-seq 的「樣本來源」跟著 Sample_Name 走：這列沒有樣本時來源一併清空（欄位也會灰掉）。
    if (field === 'sampleName' && !newSampleSheet[index].sampleName && newSampleSheet[index].sampleSource) {
      newSampleSheet[index].sampleSource = '';
    }

    const count = newSampleSheet.length;

    setFormData(prev => normalizeSpeciesFields({
      ...prev,
      sampleInfo: {
        ...prev.sampleInfo,
        sampleSheet: newSampleSheet
      },
      sampleCount: resolveAnalysisSampleCount(prev, count, newSampleSheet)
    }));
  };

  // 🆕 修改 handleSampleTablePaste (Sample)
  const handleSampleTablePaste = (e, startIndex) => {
    const pastedText = e.clipboardData.getData('text');
    if (!shouldHandleTablePaste(pastedText)) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    const rows = getClipboardRows(pastedText);
    const isLiteMode = isAnalysisOnlySampleOrder(formData);
    // Lite 表格的欄位數量會隨實驗類型改變，貼上時要依照畫面上的欄位順序對應。
    const liteRule = getSampleSheetScRnaRule(formData);
    const liteIsChIPseq = isChIPseqOrder(formData);

    const newSampleSheet = [...formData.sampleInfo.sampleSheet];

    rows.forEach((row, rowIndex) => {
      const columns = row.split('\t');
      const targetIndex = startIndex + rowIndex;
      const startCol = /^\d+$/.test(columns[0]) ? 1 : 0;

      while (targetIndex >= newSampleSheet.length) {
        newSampleSheet.push(createBlankSampleInfoRow(newSampleSheet.length + 1));
      }

      if (columns.length > startCol) {
        if (isLiteMode) {
          let col = startCol;
          const sampleName = sanitizeSampleName(columns[col++] || '');
          const sampleBarcode = liteRule.requireSampleBarcode ? (columns[col++] || '') : '';
          const fastqName = liteRule.showFastqName ? (columns[col++] || '') : '';
          // SC04 的 V(D)J 欄位：類型、BCR fastq、TCR fastq，順序與畫面上的欄位一致。
          const vdjType = liteRule.requireVdjLibrary ? normalizeVdjLibraryType(columns[col++] || '') : '';
          const bcrFastqName = liteRule.requireVdjLibrary ? (columns[col++] || '') : '';
          const tcrFastqName = liteRule.requireVdjLibrary ? (columns[col++] || '') : '';
          // ChIP-seq 的「樣本來源」排在 Sample_Name 後面、備註前面，跟畫面一致；沒有樣本的列不收來源。
          const sampleSource = liteIsChIPseq ? (columns[col++] || '') : '';

          newSampleSheet[targetIndex] = {
            no: targetIndex + 1,
            sampleName,
            sampleBarcode,
            fastqName,
            vdjType,
            bcrFastqName: vdjLibraryTypeIncludes(vdjType, 'BCR') ? bcrFastqName : '',
            tcrFastqName: vdjLibraryTypeIncludes(vdjType, 'TCR') ? tcrFastqName : '',
            ...(liteIsChIPseq
              ? { sampleSource: sampleName ? sanitizeChIPseqSampleSource(sampleSource) : '' }
              : {}),
            tubeLabel: '',
            expectedSeq: '',
            conc: '',
            vol: '',
            ratio260280: '',
            ratio260230: '',
            dqnRqn: '',
            note: columns[col] || ''
          };
        } else {
          newSampleSheet[targetIndex] = {
            no: targetIndex + 1,
            sampleName: sanitizeSampleName(columns[startCol] || ''),
            sampleBarcode: '',
            fastqName: '',
            vdjType: '',
            bcrFastqName: '',
            tcrFastqName: '',
            tubeLabel: columns[startCol + 1] || '',
            expectedSeq: columns[startCol + 2] || '',
            conc: columns[startCol + 3] || '',
            vol: columns[startCol + 4] || '',
            ratio260280: columns[startCol + 5] || '',
            ratio260230: columns[startCol + 6] || '',
            dqnRqn: columns[startCol + 7] || '',
            note: columns[startCol + 8] || ''
          };
        }
      }
    });

    const count = newSampleSheet.length;

    setFormData(prev => normalizeSpeciesFields({
      ...prev,
      sampleInfo: {
        ...prev.sampleInfo,
        sampleSheet: newSampleSheet
      },
      sampleCount: resolveAnalysisSampleCount(prev, count, newSampleSheet),
      ...syncPackageStateAfterCountChange(prev, resolveAnalysisSampleCount(prev, count, newSampleSheet))
    }));
    setFieldErrors({
      sampleSheet: {},
      librarySampleSheet: {},
      libraryDetailSheet: {}
    });

    setMessage(`已貼上 ${rows.length} 行資料，Sample Sheet 列數：${count}`);
    setTimeout(() => setMessage(''), 2000);
  };

  // 🆕 修改 addSampleSheetRow
  const addSampleSheetRow = () => {
    const apConfig = getAPPackageConfig();
    const expectedSeq = apConfig?.seqPerSample ? String(apConfig.seqPerSample) : ''; // 🔒 AP 套組自動帶入

    const createRow = (no) => ({
      no,
      sampleName: '',
      sampleBarcode: '',
      fastqName: '',
      vdjType: '',
      bcrFastqName: '',
      tcrFastqName: '',
      tubeLabel: '',
      expectedSeq: expectedSeq, // 🆕 自動帶入
      conc: '',
      vol: '',
      ratio260280: '',
      ratio260230: '',
      dqnRqn: '',
      note: ''
    });

    const existingRows = formData.sampleInfo.sampleSheet;
    const newSampleSheet = [...existingRows, createRow(existingRows.length + 1)];

    const count = newSampleSheet.length;

    setFormData(prev => normalizeSpeciesFields({
      ...prev,
      sampleInfo: {
        ...prev.sampleInfo,
        sampleSheet: newSampleSheet
      },
      sampleCount: resolveAnalysisSampleCount(prev, count, newSampleSheet),
      ...syncPackageStateAfterCountChange(prev, resolveAnalysisSampleCount(prev, count, newSampleSheet))
    }));
    setFieldErrors({
      sampleSheet: {},
      librarySampleSheet: {},
      libraryDetailSheet: {}
    });
    setMessage(getRowCountChangeNotice('樣本', '新增'));
    setTimeout(() => setMessage(''), 3000);
  };

  // 🆕 修改 removeSampleSheetRow
  const removeSampleSheetRow = (index) => {
    if (formData.sampleInfo.sampleSheet.length === 1) {
      alert('至少需要保留一行');
      return;
    }
    const newSampleSheet = formData.sampleInfo.sampleSheet.filter((_, i) => i !== index);

    const count = newSampleSheet.length;

    setFormData(prev => normalizeSpeciesFields({
      ...prev,
      sampleInfo: {
        ...prev.sampleInfo,
        sampleSheet: newSampleSheet
      },
      sampleCount: resolveAnalysisSampleCount(prev, count, newSampleSheet),
      ...syncPackageStateAfterCountChange(prev, resolveAnalysisSampleCount(prev, count, newSampleSheet))
    }));
    setFieldErrors({
      sampleSheet: {},
      librarySampleSheet: {},
      libraryDetailSheet: {}
    });
    setMessage(getRowCountChangeNotice('樣本', '刪除'));
    setTimeout(() => setMessage(''), 3000);
  };
  // Library Sample Sheet 範本下載
  const downloadSampleTemplate = async () => {
    setIsExporting(true);  // 顯示載入動畫
    try {
      const response = await fetch(`/api/templates/sample_template.xlsx`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'TGIA_Sample_Template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setMessage('✅ Sample 範本已下載');
      setTimeout(() => setMessage(''), 2000);
    } catch (error) {
      console.error('下載錯誤:', error);
      setMessage('❌ 下載失敗：' + error.message);
    } finally {
      setIsExporting(false);
    }
  };

  // 🔧 Library 範本下載（透過後端 API）
  const downloadTemplate = async () => {
    setIsExporting(true);  // 顯示載入動畫
    try {

      const response = await fetch(`/api/templates/library_templates.xlsx`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'TGIA_Library_Template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setMessage('✅ Library 範本已下載');
      setTimeout(() => setMessage(''), 2000);
    } catch (error) {
      console.error('下載錯誤:', error);
      setMessage('❌ 下載失敗：' + error.message);
    } finally {
      setIsExporting(false);
    }
  };

  const handleSaveDraft = async () => {
    if (isLocked) {
      setMessage('⚠️ 此訂單已鎖定，無法再儲存草稿');
      return;
    }

    if (isSavingDraft || isSubmitting) {
      return;
    }

    const payload = buildSubmitPayload(resolvedSampleFormData);
    payload.formTiming = buildForcedLogoutTimingMetadata();
    payload.isDraft = true;

    const reusableDraftOrderCode = editingOrderCode || draftOrderCode;
    if (reusableDraftOrderCode) {
      payload.draftOrderCode = reusableDraftOrderCode;
    }

    setIsSavingDraft(true);
    setMessage('');

    try {
      const response = await fetch('/api/orders/draft', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      let result = {};
      try {
        result = await response.json();
      } catch (error) {
        result = {};
      }

      if (!response.ok || !result.success) {
        throw new Error(result.error || `HTTP ${response.status}`);
      }

      const savedDraftCode = result.orderId || reusableDraftOrderCode;
      setDraftOrderCode(savedDraftCode);
      setEditingOrderSource('draft');
      setMessage(
        result.draftEmailSent
          ? `草稿（${savedDraftCode}）已儲存，系統已寄出草稿紀錄 email。`
          : `草稿（${savedDraftCode}）已儲存，但草稿 email 尚未送出，請確認 email 設定。`
      );
    } catch (error) {
      setMessage('儲存草稿失敗：' + error.message);
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleSubmit = async () => {
    if (isLocked) {
      setMessage('⚠️ 此訂單已鎖定，請勿重複送出');
      return;
    }
    if (!validateStep(4)) {
      return;
    }

    const submitOrderCode = editingOrderCode || draftOrderCode;
    const isEditingOrder = Boolean(submitOrderCode);
    const isSubmittingDraftOrder = editingOrderSource === 'draft' || Boolean(draftOrderCode);
    const submitActionLabel = isSubmittingDraftOrder ? '提交' : (isEditingOrder ? '更新' : '提交');

    if (!window.confirm(`確認要${submitActionLabel}需求嗎？`)) {
      return;
    }

    // 🆕 開始提交，顯示載入動畫
    setIsSubmitting(true);
    setMessage('');

    // 🆕 提交前移除 UI 追蹤用的 sentinel，並整理 scRNAseq 的 TenX 欄位
    const submitData = buildSubmitPayload(resolvedSampleFormData);
    submitData.formTiming = buildFormTimingMetadata();
    if (isEditingOrder) {
      submitData.sourceOrderStore = isSubmittingDraftOrder ? 'draft' : 'submitted';
    }
    const submitUrl = isEditingOrder
      ? `/api/orders/${encodeURIComponent(submitOrderCode)}`
      : '/api/orders';
    const submitMethod = isEditingOrder ? 'PUT' : 'POST';

    try {
      const response = await fetch(submitUrl, {
        method: submitMethod,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData)
      });

      const result = await response.json();

      if (result.success) {
        const savedOrderCode = result.orderId || submitOrderCode;
        setSubmitted(true);
        setExportReady(true);
        setOrderId(savedOrderCode);
        if (editingOrderCode) {
          setEditingOrderCode(savedOrderCode);
        }
        setDraftOrderCode('');
        setEditingOrderSource('submitted');
        setNeedsAnalysisRequest(result.needsAnalysisRequest || false);  // 🆕 設定是否需要分析需求單

        // 🆕 根據是否需要分析需求單顯示不同訊息
        if (result.needsAnalysisRequest) {
          setMessage(`需求單（${savedOrderCode}）已成功${submitActionLabel}。偵測到分析服務，請下載「分析需求單」填寫完成後回傳給您的業務代表，謝謝。`);
        } else {
          setMessage(`需求單（${savedOrderCode}）已成功${submitActionLabel}。`);
        }

        setIsLocked(true);
        setTimeout(() => {
          setSubmitted(false);
        }, 5000);
      } else {
        setMessage('提交失敗：' + result.error);
      }
    } catch (error) {
      setMessage('提交失敗：' + error.message);
    } finally {
      // 🆕 結束提交，隱藏載入動畫
      setIsSubmitting(false);
    }
  };


  const handleLoadOrderByCode = async ({ source = 'submitted' } = {}) => {
    const orderCode = normalizeOrderCodeInput(orderCodeToRestore);
    const isDraftSource = source === 'draft';
    const endpoint = isDraftSource
      ? `/api/drafts/${encodeURIComponent(orderCode)}`
      : `/api/orders/${encodeURIComponent(orderCode)}`;

    setStep0Warning('');
    if (!orderCode) {
      setLoadOrderWarning('❌ 請輸入有效的 TGIA Order Code');
      setMessage('');
      return;
    }

    setIsLoadingOrder(true);
    setLoadOrderWarning('');
    setMessage('');

    try {
      const response = await fetch(endpoint);
      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || '載入失敗');
      }

      const restored = normalizeRestoredOrderData(result.orderData || {}, {
        findScRNAExperimentOption
      });
      const normalizedFormData = normalizeSpeciesFields({
        ...restored,
        serviceItems: sanitizeServiceItemsForPublicCatalog(restored.serviceItems, serviceOptionsByCategory)
      });
      normalizedFormData.isUrgent = false;

      setFormData(normalizedFormData);
      // 還原的抗體不在選單內時，一開始就標成「其他」，使用者把文字刪光重打時輸入框才不會消失；
      // 不沿用上一張單的 UI 狀態。
      setAnalysisParameterCustomModes(deriveAnalysisParameterCustomModes(
        getAnalysisParameterDefinitionsForOrder(normalizedFormData),
        normalizedFormData.analysisParameters
      ));
      setIsAnalysisThresholdsOpen(false);
      setOrderId(result.orderId || orderCode);
      setDraftOrderCode(isDraftSource ? (result.orderId || orderCode) : '');
      setEditingOrderCode(result.orderId || orderCode);
      setEditingOrderSource(isDraftSource ? 'draft' : 'submitted');
      setOrderCodeToRestore(orderCode);
      setExportReady(false);
      setIsLocked(false);
      setSubmitted(false);
      setNeedsAnalysisRequest(Boolean(result.orderData?.needsAnalysisRequest));
      setCurrentStep(1);
      setFieldErrors({
        sampleSheet: {},
        librarySampleSheet: {},
        libraryDetailSheet: {}
      });
      setShowOrgSuggestions(false);
      setFilteredOrgs([]);

    } catch (error) {
      setLoadOrderWarning(`載入失敗：${error.message}`);
      setMessage('');
    } finally {
      setIsLoadingOrder(false);
    }
  };

  const handleLoadSavedOrder = () => handleLoadOrderByCode({ source: 'submitted' });
  const handleLoadDraftOrder = () => handleLoadOrderByCode({ source: 'draft' });

  // 在 handleSubmit 函數後面（約第 2260 行）
  const handleCreateNewOrder = () => {
    if (!window.confirm('確定要重新建立訂單嗎？')) {
      return;
    }

    resetToFreshLoginPage();
    window.scrollTo(0, 0);
  };

  const handleCreateSimilarOrder = () => {
    if (!window.confirm('保留目前填寫內容並建立另一筆新訂單嗎？下一次提交會產生新的訂單編號。')) {
      return;
    }

    setIsLocked(false);
    setSubmitted(false);
    setExportReady(false);
    setOrderId('');
    setDraftOrderCode('');
    skipNextSampleSheetSyncRef.current = true;
    setEditingOrderCode('');
    setEditingOrderSource('');
    setOrderCodeToRestore('');
    setNeedsAnalysisRequest(false);
    setFieldErrors({
      sampleSheet: {},
      librarySampleSheet: {},
      libraryDetailSheet: {}
    });
    setShowOrgSuggestions(false);
    setFilteredOrgs([]);
    clearTransientMessages();
    setStep0Tab('create');
    setCurrentStep(1);
    window.scrollTo(0, 0);
  };



  // 🆕 下載分析需求單
  const downloadAnalysisRequest = async () => {
    if (!orderId) {
      setMessage('請先提交服務需求才能下載分析需求單');
      return;
    }

    setIsExporting(true);

    try {
      const response = await fetch(`/api/orders/${orderId}/analysis-request`);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || '下載失敗');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `TGIA_Analysis_Request_${orderId}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setMessage('✅ 分析需求單下載成功');
      setTimeout(() => setMessage(''), 2000);
    } catch (error) {
      setMessage('分析需求單下載失敗：' + error.message);
    } finally {
      setIsExporting(false);
    }
  };

  const downloadAnalysisDocument = async (analysisDocument) => {
    if (!analysisDocument?.downloadUrl) {
      setMessage('找不到文件下載連結');
      return;
    }

    const downloadUrl = orderId && analysisDocument.id
      ? `/api/orders/${encodeURIComponent(orderId)}/analysis-documents/${encodeURIComponent(analysisDocument.id)}`
      : analysisDocument.downloadUrl;

    setIsExporting(true);

    const openedWindow = window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    if (!openedWindow) {
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }

    window.setTimeout(() => {
      setIsExporting(false);
    }, 1200);
  };


  const exportToExcel = async () => {
    if (!orderId) {
      setMessage('請先提交服務需求才能下載 Excel');
      return;
    }

    // 🆕 開始下載，顯示載入動畫
    setIsExporting(true);

    try {
      const response = await fetch(`/api/orders/${orderId}/export`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `TGIA_Order_${orderId}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setMessage('✅ Excel 檔案下載成功');
      setTimeout(() => setMessage(''), 2000);
    } catch (error) {
      setMessage('下載失敗：' + error.message);
    } finally {
      // 🆕 結束下載，隱藏載入動畫
      setIsExporting(false);
    }
  };


  // 🆕 可搜尋的下拉選單組件
  const SearchableSelect = ({
    value,
    options,
    onChange,
    placeholder,
    itemIndex,
    serviceIndex,
    disabled = false,  // 🆕 添加 disabled 參數
    showHidden = false  // 🆕 是否显示隐藏选项（预设不显示）    
  }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const dropdownRef = useRef(null);


    // 過濾選項
    // 🆕 过滤逻辑：根据搜索词和 hidden 属性
    const filteredOptions = options.filter(opt => {
      // 基本搜索过滤
      const matchesSearch = opt.value.toLowerCase().includes(searchTerm.toLowerCase()) ||
        opt.description?.toLowerCase().includes(searchTerm.toLowerCase());

      if (!matchesSearch) return false;

      // 🆕 隐藏选项处理
      if (opt.hidden && !showHidden) {
        // 如果有搜索词，显示隐藏选项（让用户可以搜到）
        // 如果没搜索词（打开下拉），不显示隐藏选项
        return searchTerm.trim().length > 0;
      }

      return true;
    });

    // 點擊外部關閉
    React.useEffect(() => {
      const handleClickOutside = (e) => {
        if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
          setIsOpen(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // 鍵盤操作
    const handleKeyDown = (e) => {
      if (!isOpen || disabled) return;  // 🆕 disabled 時不處理鍵盤

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex(prev =>
            prev < filteredOptions.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
          break;
        case 'Enter':
          e.preventDefault();
          if (filteredOptions[highlightedIndex]) {
            handleSelect(filteredOptions[highlightedIndex].value);
          }
          break;
        case 'Escape':
          setIsOpen(false);
          break;
        default:
          break;
      }
    };

    const handleSelect = (selectedValue) => {
      if (disabled) return;  // 🆕 disabled 時不能選擇
      onChange(selectedValue);
      setIsOpen(false);
      setSearchTerm('');
    };

    const displayValue = value ? options.find(opt => opt.value === value)?.value || value : '';

    return (
      <div ref={dropdownRef} className="relative">
        <div className="relative">
          <input
            type="text"
            value={isOpen ? searchTerm : displayValue}
            onChange={(e) => {
              if (disabled) return;
              setSearchTerm(e.target.value);
              if (!isOpen) setIsOpen(true);
              setHighlightedIndex(0);
            }}
            onFocus={() => {
              if (!disabled) setIsOpen(true);
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            className={`${compactInputToneClasses.text} pr-8`}
          />
          <div className={`absolute right-2 top-1/2 -translate-y-1/2 ${disabled ? 'text-[var(--tgia-color-disabled-text)]' : 'text-[var(--tgia-color-placeholder)]'
            }`}>
            <ChevronRight
              size={16}
              className={`transform transition-transform ${isOpen ? 'rotate-90' : ''}`}
            />
          </div>
        </div>

        {/* 下拉选项列表 */}
        {isOpen && !disabled && (
          <div className={dropdownToneClasses.panel}>
            {filteredOptions.length === 0 ? (
              <div className={dropdownToneClasses.emptyText}>
                找不到符合的選項
              </div>
            ) : (
              filteredOptions.map((opt, idx) => (
                <div
                  key={idx}
                  onClick={() => handleSelect(opt.value)}
                  className={`${dropdownToneClasses.option} ${idx === highlightedIndex
                    ? dropdownToneClasses.optionActive
                    : dropdownToneClasses.optionHover
                    } ${opt.value === value ? dropdownToneClasses.optionSelected : ''
                    } ${opt.hidden ? dropdownToneClasses.optionHidden : ''  // 🆕 隐藏选项特殊样式
                    }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className={dropdownToneClasses.optionText}>
                        {opt.value}
                      </div>
                      {opt.description && (
                        <div className={dropdownToneClasses.descriptionText}>
                          {opt.description}
                        </div>
                      )}
                    </div>
                    {opt.hidden && (
                      <span className="ml-2 rounded-full bg-[var(--tgia-color-caution-surface)] px-2 py-0.5 text-xs text-[var(--tgia-color-caution-text)]">

                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    );
  };

  // 🆕 渲染步驟0：快速帶入（簡化版）
  const renderStep0 = () => {
    const salesExists = formData.salesCode && salesCodes.find(s => s.code === formData.salesCode);
    const canValidateSalesCode = !isSalesCodesLoading && !salesCodesLoadError;
    const canValidateCustomerCode = !isCustomerCodesLoading && !customerCodesLoadError;
    const customerExists = canValidateCustomerCode && formData.customerCode && customerCodes.find(c => c.code === formData.customerCode);
    const step0TabOrder = ['create', 'load', 'draft'];
    const step0TabPanelIds = {
      create: 'step0-panel-create',
      load: 'step0-panel-load',
      draft: 'step0-panel-draft'
    };

    const handleStep0TabKeyDown = (event) => {
      const currentIndex = step0TabOrder.indexOf(step0Tab);
      let nextIndex = null;

      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        nextIndex = (currentIndex + 1) % step0TabOrder.length;
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        nextIndex = (currentIndex - 1 + step0TabOrder.length) % step0TabOrder.length;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = step0TabOrder.length - 1;
      }

      if (nextIndex === null) return;

      event.preventDefault();
      const nextTab = step0TabOrder[nextIndex];
      setStep0Tab(nextTab);
      step0TabRefs.current[nextTab]?.focus();
    };

    return (
      <div className="space-y-0">
        <div className={segmentedTabToneClasses.root}>
          <div className={segmentedTabToneClasses.stack}>
            <div
              className={segmentedTabToneClasses.tabList}
              role="tablist"
              aria-label="訂單模式切換"
              aria-orientation="horizontal"
              onKeyDown={handleStep0TabKeyDown}
            >
              <button
                type="button"
                role="tab"
                id="step0-tab-create"
                ref={(node) => {
                  step0TabRefs.current.create = node;
                }}
                onClick={() => setStep0Tab('create')}
                aria-controls={step0TabPanelIds.create}
                aria-selected={step0Tab === 'create'}
                tabIndex={step0Tab === 'create' ? 0 : -1}
                className={`${segmentedTabToneClasses.tab} ${step0Tab === 'create' ? segmentedTabToneClasses.active : segmentedTabToneClasses.inactive}`}
              >
                建立新訂單
              </button>
              <button
                type="button"
                role="tab"
                id="step0-tab-load"
                ref={(node) => {
                  step0TabRefs.current.load = node;
                }}
                onClick={() => setStep0Tab('load')}
                aria-controls={step0TabPanelIds.load}
                aria-selected={step0Tab === 'load'}
                tabIndex={step0Tab === 'load' ? 0 : -1}
                className={`${segmentedTabToneClasses.tab} ${step0Tab === 'load' ? segmentedTabToneClasses.active : segmentedTabToneClasses.inactive}`}
              >
                載入舊訂單
              </button>
              <button
                type="button"
                role="tab"
                id="step0-tab-draft"
                ref={(node) => {
                  step0TabRefs.current.draft = node;
                }}
                onClick={() => setStep0Tab('draft')}
                aria-controls={step0TabPanelIds.draft}
                aria-selected={step0Tab === 'draft'}
                tabIndex={step0Tab === 'draft' ? 0 : -1}
                className={`${segmentedTabToneClasses.tab} ${step0Tab === 'draft' ? segmentedTabToneClasses.active : segmentedTabToneClasses.inactive}`}
              >
                載入草稿
              </button>
            </div>

            <div
              className={segmentedTabToneClasses.panel}
            >
              <div className={segmentedTabToneClasses.panelBody}>
                <section
                  id={step0TabPanelIds.create}
                  role="tabpanel"
                  aria-labelledby="step0-tab-create"
                  hidden={step0Tab !== 'create'}
                  className={`${step0Tab === 'create' ? 'step0-panel-enter' : ''} space-y-6`}
                >
                  <div className="space-y-5">
                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-[var(--tgia-color-text)]">
                        業務代碼 <span className="text-[var(--tgia-color-accent)]">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.salesCode}
                        onChange={(e) => handleSalesCodeChange(e.target.value)}
                        placeholder="請輸入業務代碼"
                        className={inputToneClasses.text}
                      />

                      {isSalesCodesLoading && (
                        <div className="rounded-xl border border-[var(--tgia-color-border)] bg-[var(--tgia-color-surface-soft)] px-4 py-3 text-sm text-[var(--tgia-color-text-muted)]">
                          業務代碼資料載入中...
                        </div>
                      )}

                      {salesCodesLoadError && (
                        <div className="rounded-xl border border-[var(--tgia-color-error-border)] bg-[var(--tgia-color-error-surface)] px-4 py-3 text-sm text-[var(--tgia-color-error-text)]">
                          {salesCodesLoadError}
                        </div>
                      )}

                      {canValidateSalesCode && formData.salesCode && salesExists && (
                        <div className="rounded-xl border border-[var(--tgia-color-brand-border)] bg-[var(--tgia-color-surface-muted)] px-4 py-3 text-sm text-[var(--tgia-color-brand-text)]">
                          ✓ 業務人員：<span className="font-semibold">{salesExists.name}</span>
                          {salesExists.department && <span className="ml-1 text-[var(--tgia-color-text-muted)]">| {salesExists.department}</span>}
                        </div>
                      )}

                      {canValidateSalesCode && formData.salesCode && !salesExists && (
                        <div className="rounded-xl border border-[var(--tgia-color-error-border)] bg-[var(--tgia-color-error-surface)] px-4 py-3 text-sm text-[var(--tgia-color-error-text)]">
                          ❌ 查無此業務代碼，請確認後重新輸入
                        </div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-[var(--tgia-color-text)]">
                        客戶代碼 <span className="ml-1 rounded-full bg-[var(--tgia-color-surface-active)] px-2 py-0.5 text-xs font-semibold text-[var(--tgia-color-brand-text)]">選填</span>
                      </label>
                      <input
                        type="text"
                        value={formData.customerCode}
                        onChange={(e) => handleCustomerCodeChange(e.target.value)}
                        placeholder="請輸入客戶代碼"
                        className={inputToneClasses.text}
                      />

                      {isCustomerCodesLoading && (
                        <div className="rounded-xl border border-[var(--tgia-color-border)] bg-[var(--tgia-color-surface-soft)] px-4 py-3 text-sm text-[var(--tgia-color-text-muted)]">
                          客戶代碼資料載入中...
                        </div>
                      )}

                      {customerCodesLoadError && (
                        <div className="rounded-xl border border-[var(--tgia-color-caution-border)] bg-[var(--tgia-color-caution-surface-muted)] px-4 py-3 text-sm text-[var(--tgia-color-caution-text)]">
                          {customerCodesLoadError}
                        </div>
                      )}

                      {canValidateCustomerCode && formData.customerCode && customerExists && (
                        <div className="rounded-xl border border-[var(--tgia-color-brand-border)] bg-[var(--tgia-color-surface-muted)] px-4 py-3 text-sm text-[var(--tgia-color-brand-text)]">
                          <p className="mb-1">
                            ✓ 已帶入客戶資訊：<span className="font-semibold">{customerExists.organization}</span>
                          </p>
                          <p className="text-xs text-[var(--tgia-color-text-muted)]">
                            聯絡人：{customerExists.contactPerson} | 負責人：{customerExists.principalInvestigator}
                          </p>
                        </div>
                      )}

                      {canValidateCustomerCode && formData.customerCode && !customerExists && (
                        <div className={`rounded-xl border px-4 py-3 text-sm ${infoToneClasses.error}`}>
                          ❌ 查無此客戶代碼，請在下一步手動填寫客戶資訊
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={nextStep}
                        disabled={isSalesCodesLoading}
                        className={`${buttonToneClasses.primary} w-full ${isSalesCodesLoading ? 'pointer-events-none opacity-60' : ''}`}
                      >
                        下一步
                        <ChevronRight size={20} />
                      </button>

                      {step0Warning && (
                        <div className="rounded-xl border border-[var(--tgia-color-error-border)] bg-[var(--tgia-color-error-surface)] px-4 py-3 text-sm font-medium text-[var(--tgia-color-error-text)]">
                          {step0Warning}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <InfoPanel
                      title="使用說明"
                      tone="caution_secondary"
                      icon={<AlertCircle size={18} />}
                    >
                      <ul className="space-y-1.5">
                        <li>業務代碼為必填，輸入正確的代碼才能進入下一步，或以 <code>TGIA-NGS</code> 進行後續操作。</li>
                        <li>客戶代碼輸入後會自動帶入客戶資訊。</li>
                      </ul>
                    </InfoPanel>

                    <InfoPanel
                      title="敬告 貴客戶"
                      tone="caution_primary"
                      icon={<AlertCircle size={18} />}
                    >
                      <div className="space-y-3">
                        <div>
                          <p className="font-semibold">1. 付款方式</p>
                          <p>首次交易須於送件時全額付款。後續訂單先付 50% 作為前置費用，報告交付後 30 日內支付尾款 50%。</p>
                          <p>未依約付款者，本公司得暫停服務、延後報告或停止數據提供。</p>
                        </div>

                        <div>
                          <p className="font-semibold">2. 注意事項</p>
                          <ul className="space-y-1">
                            <li>2.1 檢測過程中樣品可能耗損。</li>
                            <li>2.2 樣品自出貨日起保存 30 日，逾期依規處理；需退樣請事先註明，運費由委託者負擔。</li>
                            <li>2.3 委託者須確保資料正確與合法，並同意用於檢測與報告；如有不實須自負法律責任。</li>
                            <li>2.4 報告交期以最長檢測項目為準。</li>
                            <li>2.5 數據於出貨區保管 30 日，逾期申請需先支付相關費用。</li>
                            <li>2.6 非常態或客戶指定試劑耗材須全額預付；剩餘或過期物料由客戶領回，本公司不負保管責任。</li>
                            <li>2.7 <strong>保密原則：</strong>除依法令規定、主管機關要求或其他必要範圍內，本公司原則上就實驗室活動產生之相關資訊負合理保密義務。</li>
                          </ul>
                        </div>

                        <div>
                          <p className="font-semibold">3. 服務說明</p>
                          <p>詳細流程請參閱服務說明文件（定序服務說明書（W-014-01）或基因檢測服務說明書（W-014-02））。</p>
                          <p>服務時間：週一至週五 09:00–18:00（例假日及國定假日除外）。</p>
                        </div>
                      </div>
                    </InfoPanel>
                  </div>
                </section>
                <section
                  id={step0TabPanelIds.load}
                  role="tabpanel"
                  aria-labelledby="step0-tab-load"
                  hidden={step0Tab !== 'load'}
                  className={`${step0Tab === 'load' ? 'step0-panel-enter' : ''} space-y-3`}
                >
                  {renderLoadSavedOrderPanel()}
                </section>
                <section
                  id={step0TabPanelIds.draft}
                  role="tabpanel"
                  aria-labelledby="step0-tab-draft"
                  hidden={step0Tab !== 'draft'}
                  className={`${step0Tab === 'draft' ? 'step0-panel-enter' : ''} space-y-3`}
                >
                  {renderLoadSavedOrderPanel({
                    description: '請輸入草稿的 TGIA Order Code 以繼續編輯',
                    buttonLabel: '載入草稿',
                    onSubmit: handleLoadDraftOrder
                  })}
                </section>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 text-center text-sm font-medium text-[var(--tgia-color-panel-text)]">
          步驟 {currentStep + 1} / {steps.length}
        </div>
      </div>
    );
  };

  const renderLoadSavedOrderPanel = ({
    description = '請輸入您的 TGIA Order Code 以編輯上次紀錄',
    buttonLabel = '下一步',
    onSubmit = handleLoadSavedOrder
  } = {}) => (
    <div className="space-y-3">
      <p className="text-sm font-medium text-[var(--tgia-color-text-muted)]">
        {description}
      </p>
      <div className="space-y-3">
        <div className="flex rounded-xl border border-[var(--tgia-color-border)] bg-[var(--tgia-color-input)] text-[var(--tgia-color-text)] shadow-sm transition focus-within:border-[var(--tgia-color-focus)] focus-within:ring-2 focus-within:ring-[var(--tgia-color-focus-ring)]">
          <span className="inline-flex items-center rounded-l-xl border-r border-[var(--tgia-color-border-subtle)] bg-[var(--tgia-color-surface-muted)] px-4 text-lg font-semibold text-[var(--tgia-color-text-muted)]">
            {ORDER_CODE_PREFIX}
          </span>
          <input
            type="text"
            value={getOrderCodeSuffixInputValue(orderCodeToRestore)}
            onChange={(e) => {
              setOrderCodeToRestore(getOrderCodeSuffixInputValue(e.target.value));
              setStep0Warning('');
              setLoadOrderWarning('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onSubmit();
              }
            }}
            placeholder="1111111111"
            className="min-w-0 flex-1 rounded-r-xl border-0 bg-transparent px-4 py-3 text-lg text-[var(--tgia-color-text)] placeholder:text-[var(--tgia-color-placeholder)] focus:outline-none focus:ring-0"
          />
        </div>
        <button
          type="button"
          onClick={onSubmit}
          disabled={isLoadingOrder}
          className={`${buttonToneClasses.primary} w-full ${isLoadingOrder ? 'pointer-events-none opacity-60' : ''}`}
        >
          {isLoadingOrder ? '載入中...' : buttonLabel}
          <ChevronRight size={20} />
        </button>
      </div>

      {loadOrderWarning && (
        <div className="rounded-xl border border-[var(--tgia-color-error-border)] bg-[var(--tgia-color-error-surface)] px-4 py-3 text-sm font-medium text-[var(--tgia-color-error-text)]">
          {loadOrderWarning}
        </div>
      )}
    </div>
  );


  // 渲染步驟1：基本資訊
  const renderStep1 = () => (

    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-[var(--tgia-color-text)] mb-1">
            業務人員 <span className="text-[var(--tgia-color-accent)]">*</span>
          </label>
          {/* 🆕 改為唯讀顯示 */}
          <div className="w-full rounded-xl border border-[var(--tgia-color-control-border)] bg-[var(--tgia-color-surface-active)] px-4 py-3 text-base font-medium leading-6 text-[var(--tgia-color-text)]">
            {formData.salesPerson || '未設定'}
          </div>
          <p className="text-xs text-[var(--tgia-color-text-subtle)] mt-1">
            💡 由 Step0 業務代碼自動帶入
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-[var(--tgia-color-text)] mb-1">
            填寫日期
          </label>
          <input
            type="date"
            className={inputToneClasses.text}
            defaultValue={new Date().toISOString().split('T')[0]}
          />
        </div>
      </div>

      {/* 委託人資訊 */}
      <div className="border border-[var(--tgia-color-border)] rounded-2xl p-4 sm:p-6 bg-[var(--tgia-color-surface-muted)]">
        <h3 className="text-lg font-semibold text-[var(--tgia-color-text)] mb-4">委託人資訊</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="relative">
            <label className="block text-sm font-medium text-[var(--tgia-color-text)] mb-1">
              單位 <span className="text-[var(--tgia-color-accent)]">*</span>
            </label>
            <input
              type="text"
              name="organization"
              value={formData.organization}
              onChange={handleInputChange}
              onFocus={() => {
                if (formData.organization) {
                  const filtered = organizationOptions.filter(org =>
                    org.toLowerCase().includes(formData.organization.toLowerCase())
                  );
                  setFilteredOrgs(filtered);
                  setShowOrgSuggestions(filtered.length > 0);
                }
              }}
              onBlur={() => {
                setTimeout(() => setShowOrgSuggestions(false), 200);

                // 🆕 只添加這個驗證部分
                const error = validateNameFormat(formData.organization, '單位');
                if (error) {
                  setFieldErrors(prev => ({ ...prev, organization: error }));
                } else {
                  setFieldErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.organization;
                    return newErrors;
                  });
                }
              }}
              className={`${inputToneClasses.text} ${fieldErrors.organization ? 'tgia-field-error ' : ''}`}
              placeholder="例：國立陽明交通大學"
            />
            {showOrgSuggestions && (
              <div className="absolute z-10 w-full mt-1 bg-[var(--tgia-color-surface)] border border-[var(--tgia-color-border)] rounded-xl shadow-lg max-h-48 overflow-y-auto">
                {filteredOrgs.map((org, index) => (
                  <div
                    key={index}
                    onClick={() => selectOrganization(org)}
                    className="px-3 py-2 hover:bg-[var(--tgia-color-brand-surface)] cursor-pointer text-sm"
                  >
                    {org}
                  </div>
                ))}
              </div>
            )}

            {/* 🆕 添加錯誤提示 */}
            {fieldErrors.organization && (
              <p className="text-xs text-[var(--tgia-color-accent)] mt-1 flex items-center gap-1">
                <span>⚠️</span>
                {fieldErrors.organization}
              </p>
            )}
            <p className="text-xs text-[var(--tgia-color-text-subtle)] mt-1">
              💡 必須以中文或英文字母開頭
            </p>
          </div>
          {/* 🆕 負責人/主持人 - 加入驗證 */}
          <div>
            <label className="block text-sm font-medium text-[var(--tgia-color-text)] mb-1">
              負責人/主持人 <span className="text-[var(--tgia-color-accent)]">*</span>
            </label>
            <input
              type="text"
              name="principalInvestigator"
              value={formData.principalInvestigator}
              onChange={(e) => {
                handleInputChange(e);
                // 🆕 即時驗證
                const error = validateNameFormat(e.target.value, '負責人/主持人');
                if (error) {
                  setFieldErrors(prev => ({
                    ...prev,
                    principalInvestigator: error
                  }));
                } else {
                  setFieldErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.principalInvestigator;
                    return newErrors;
                  });
                }
              }}
              onBlur={(e) => {
                const error = validateNameFormat(e.target.value, '負責人/主持人');
                if (error) {
                  setFieldErrors(prev => ({
                    ...prev,
                    principalInvestigator: error
                  }));
                }
              }}
              className={`${inputToneClasses.text} ${fieldErrors.principalInvestigator ? 'tgia-field-error ' : ''}`}
              placeholder="請輸入負責人/主持人姓名"
            />
            {fieldErrors.principalInvestigator && (
              <p className="text-xs text-[var(--tgia-color-accent)] mt-1 flex items-center gap-1">
                <span>⚠️</span>
                {fieldErrors.principalInvestigator}
              </p>
            )}
            <p className="text-xs text-[var(--tgia-color-text-subtle)] mt-1">
              💡 必須以中文或英文字母開頭
            </p>
          </div>
          {/* 🆕 聯絡人 - 加入驗證 */}
          <div>
            <label className="block text-sm font-medium text-[var(--tgia-color-text)] mb-1">
              聯絡人 <span className="text-[var(--tgia-color-accent)]">*</span>
            </label>
            <input
              type="text"
              name="contactPerson"
              value={formData.contactPerson}
              onChange={(e) => {
                handleInputChange(e);
                // 🆕 即時驗證
                const error = validateNameFormat(e.target.value, '聯絡人');
                if (error) {
                  setFieldErrors(prev => ({
                    ...prev,
                    contactPerson: error
                  }));
                } else {
                  setFieldErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.contactPerson;
                    return newErrors;
                  });
                }
              }}
              onBlur={(e) => {
                // 🆕 失去焦點時驗證
                const error = validateNameFormat(e.target.value, '聯絡人');
                if (error) {
                  setFieldErrors(prev => ({
                    ...prev,
                    contactPerson: error
                  }));
                }
              }}
              className={`${inputToneClasses.text} ${fieldErrors.contactPerson ? 'tgia-field-error ' : ''}`}
              placeholder="請輸入聯絡人姓名"
            />
            {fieldErrors.contactPerson && (
              <p className="text-xs text-[var(--tgia-color-accent)] mt-1 flex items-center gap-1">
                <span>⚠️</span>
                {fieldErrors.contactPerson}
              </p>
            )}
            <p className="text-xs text-[var(--tgia-color-text-subtle)] mt-1">
              💡 必須以中文或英文字母開頭
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--tgia-color-text)] mb-1">
              聯絡電話
            </label>
            <input
              type="tel"
              name="contactPhone"
              value={formData.contactPhone}
              onChange={handleInputChange}
              className={inputToneClasses.text}
              placeholder="886-2-2826-7319"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-[var(--tgia-color-text)] mb-1">
              Email (多個 email 請用逗號或分號間隔) <span className="text-[var(--tgia-color-accent)]">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={inputToneClasses.text}
              placeholder="example@nycu.edu.tw"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-[var(--tgia-color-text)] mb-1">
              地址
            </label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              className={inputToneClasses.text}
            />
          </div>
        </div>
      </div>

      {/* 發票資訊 */}
      <div className="border border-[var(--tgia-color-border)] rounded-2xl p-4 sm:p-6 bg-[var(--tgia-color-surface-muted)]">
        <h3 className="text-lg font-semibold text-[var(--tgia-color-text)] mb-4">發票資訊</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[var(--tgia-color-text)] mb-1">
              抬頭
            </label>
            <input
              type="text"
              name="invoiceTitle"
              value={formData.invoiceTitle}
              onChange={handleInputChange}
              className={inputToneClasses.text}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--tgia-color-text)] mb-1">
              統編
            </label>
            <input
              type="text"
              name="taxId"
              value={formData.taxId}
              onChange={handleInputChange}
              className={inputToneClasses.text}
              maxLength={8}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--tgia-color-text)] mb-1">
              發票聯數
            </label>
            <select
              name="invoiceCopies"
              value={formData.invoiceCopies}
              onChange={handleInputChange}
              className={inputToneClasses.select}
            >
              <option>二聯式</option>
              <option>三聯式</option>
            </select>
          </div>
        </div>
      </div>

      {/* 數據交付資訊 */}
      <div className="border border-[var(--tgia-color-border)] rounded-2xl p-4 sm:p-6 bg-[var(--tgia-color-surface-muted)]">
        <h3 className="text-lg font-semibold text-[var(--tgia-color-text)] mb-4">數據交付資訊</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--tgia-color-text)] mb-1">
              數據提供方式 <span className="text-[var(--tgia-color-accent)]">*</span>
            </label>
            <select
              name="dataDeliveryMethod"
              value={formData.dataDeliveryMethod}
              onChange={handleInputChange}
              className={inputToneClasses.select}
            >
              <option>HDD由專人遞送</option>
              <option>國網中心下載</option>
              <option>雲端下載</option>
              <option>sFTP下載</option>
            </select>
          </div>

          {formData.dataDeliveryMethod === '國網中心下載' && (
            <div>
              <label className="block text-sm font-medium text-[var(--tgia-color-text)] mb-1">
                數據下載國網帳號
              </label>
              <input
                type="text"
                name="nchcAccount"
                value={formData.nchcAccount}
                onChange={handleInputChange}
                className={inputToneClasses.text}
                placeholder="請輸入國網中心帳號"
              />
            </div>
          )}

          {formData.dataDeliveryMethod === 'HDD由專人遞送' && (
            <>
              <div>
                <label className="block text-sm font-medium text-[var(--tgia-color-text)] mb-1">
                  數據硬碟交貨地址
                </label>
                <input
                  type="text"
                  name="deliveryAddress"
                  value={formData.deliveryAddress}
                  onChange={handleInputChange}
                  className={inputToneClasses.text}
                  placeholder="請填寫郵遞區號"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--tgia-color-text)] mb-1">
                    收件人
                  </label>
                  <input
                    type="text"
                    name="recipient"
                    value={formData.recipient}
                    onChange={handleInputChange}
                    className={inputToneClasses.text}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--tgia-color-text)] mb-1">
                    電話
                  </label>
                  <input
                    type="tel"
                    name="recipientPhone"
                    value={formData.recipientPhone}
                    onChange={handleInputChange}
                    className={inputToneClasses.text}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--tgia-color-text)] mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    name="recipientEmail"
                    value={formData.recipientEmail}
                    onChange={handleInputChange}
                    className={inputToneClasses.text}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>


      {/* 🆕 急件選擇 */}
      <div className="border border-[var(--tgia-color-border)] rounded-2xl p-4 sm:p-6 bg-[var(--tgia-color-surface-muted)]">
        <h3 className="text-lg font-semibold text-[var(--tgia-color-text)] mb-4">急件選擇</h3>
        <div className="space-y-3">
          <label className="tgia-choice-option flex items-center gap-3 p-3 border rounded-xl cursor-pointer transition hover:bg-[var(--tgia-color-surface-active)]">
            <input
              type="radio"
              name="isUrgent"
              value="false"
              checked={!formData.isUrgent}
              onChange={() => setFormData(prev => ({ ...prev, isUrgent: false }))}
              className="w-4 h-4 text-[var(--tgia-color-upload)]"
            />
            <div>
              <span className="font-medium text-[var(--tgia-color-text)]">正常件</span>
              <p className="text-xs text-[var(--tgia-color-text-subtle)]">標準處理時程</p>
            </div>
          </label>

          <label className="tgia-choice-option flex items-center gap-3 p-3 border rounded-xl cursor-not-allowed opacity-60">
            <input
              type="radio"
              name="isUrgent"
              value="true"
              checked={false}
              disabled
              onChange={() => setFormData(prev => ({ ...prev, isUrgent: false }))}
              className="w-4 h-4 text-[var(--tgia-color-upload)]"
            />
            <div>
              <span className="font-medium text-[var(--tgia-color-text)]">急件</span>
              <p className="text-xs text-[var(--tgia-color-text-subtle)]">
                <span className="text-[var(--tgia-color-accent)] font-semibold">目前暫停使用</span>
              </p>
            </div>
          </label>
        </div>
        <p className="mt-3 text-xs text-[var(--tgia-color-text-subtle)]">
          急件功能目前關閉，後續恢復時會保留這個區塊。
        </p>
      </div>


      {/* 🆕 樣品返還選項 */}
      <div className="border border-[var(--tgia-color-border)] rounded-2xl p-4 sm:p-6 bg-[var(--tgia-color-surface-muted)]">
        <h3 className="text-lg font-semibold text-[var(--tgia-color-text)] mb-4">樣品返還</h3>
        <div className="space-y-3">
          <label className="tgia-choice-option flex items-center gap-3 p-3 border rounded-xl cursor-pointer transition hover:bg-[var(--tgia-color-surface-active)]">
            <input
              type="radio"
              name="sampleReturn"
              value="不需要"
              checked={formData.sampleReturn === '不需要'}
              onChange={handleInputChange}
              className="w-4 h-4 text-[var(--tgia-color-upload)]"
            />
            <span className="font-medium text-[var(--tgia-color-text)]">不需要</span>
          </label>

          <label className="tgia-choice-option flex items-center gap-3 p-3 border rounded-xl cursor-pointer transition hover:bg-[var(--tgia-color-surface-active)]">
            <input
              type="radio"
              name="sampleReturn"
              value="代寄（運費自付）"
              checked={formData.sampleReturn === '代寄（運費自付）'}
              onChange={handleInputChange}
              className="w-4 h-4 text-[var(--tgia-color-upload)]"
            />
            <div>
              <span className="font-medium text-[var(--tgia-color-text)]">代寄（運費自付）</span>
              <p className="text-xs text-[var(--tgia-color-text-subtle)]">由我們寄回，運費另計</p>
            </div>
          </label>

          <label className="tgia-choice-option flex items-center gap-3 p-3 border rounded-xl cursor-pointer transition hover:bg-[var(--tgia-color-surface-active)]">
            <input
              type="radio"
              name="sampleReturn"
              value="自取(結案一個月內，逾期銷毀)"
              checked={formData.sampleReturn === '自取(結案一個月內，逾期銷毀)'}
              onChange={handleInputChange}
              className="w-4 h-4 text-[var(--tgia-color-upload)]"
            />
            <div>
              <span className="font-medium text-[var(--tgia-color-text)]">自取(結案一個月內，逾期銷毀)</span>
              <p className="text-xs text-[var(--tgia-color-text-subtle)]">請於結案一個月內自行取回</p>
            </div>
          </label>
        </div>
      </div>

      {/* 🆕 常用組合快速選擇 */}
      {/* 常用組合快速選擇 */}
      <div className="rounded-2xl border border-[var(--tgia-color-brand-border)] bg-[var(--tgia-color-surface-soft)] p-4 sm:p-6 shadow-sm">
        <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold text-[var(--tgia-color-text)]">
          ⚡ 快速選擇常用組合
        </h3>

        <div className="mb-4">
          <label className="block text-sm font-medium text-[var(--tgia-color-text)] mb-2">
            選擇預設組合（選擇後將自動勾選對應服務類別）
          </label>
          <select
            value={formData.selectedPackage}
            onChange={(e) => handlePackageSelect(e.target.value)}
            className={inputToneClasses.select}
          >
            <option value="">-- 常用組合可以幫助您更快挑選你需要的服務 --</option>
            {safeCommonPackages.map((pkg) => (
              <option key={pkg.id} value={pkg.id}>
                {pkg.icon} {pkg.name}
              </option>
            ))}
          </select>
        </div>

        {/* 顯示選中組合的預覽 */}
        {formData.selectedPackage && (
          <div className="rounded-2xl border border-[var(--tgia-color-brand-border)] bg-[var(--tgia-color-surface)] p-4 shadow-sm">
            {(() => {
              const pkg = safeCommonPackages.find(p => p.id === formData.selectedPackage);

              if (!pkg) {
                return (
                  <div className={`rounded-lg border p-3 text-sm ${infoToneClasses.error}`}>
                    ⚠️ 找不到此組合配置
                  </div>
                );
              }

              const packageCategories = pkg.categories || [
                ...new Set((pkg.defaultServices || []).map(svc => svc.category).filter(Boolean))
              ];
              const visibleDefaultServices = packageCategories.flatMap(category =>
                getPublicPackageDefaultServices(pkg, category)
              );

              return (
                <>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="mb-1 text-base font-semibold text-[var(--tgia-color-brand-text)]">
                        {pkg.icon} {pkg.name}
                      </h4>
                      <p className="text-sm text-[var(--tgia-color-text-muted)]">{pkg.description}</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-[var(--tgia-color-text)] mb-2">
                      📦 將自動勾選以下服務類別：
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {(pkg.categories || []).map((cat, idx) => (
                        <span
                          key={idx}
                          className="rounded-full bg-[var(--tgia-color-surface-active)] px-3 py-1.5 text-sm font-medium text-[var(--tgia-color-brand-text)]"
                        >
                          {cat}
                        </span>
                      ))}
                    </div>

                    {visibleDefaultServices.length > 0 && (
                      <>
                        <p className="text-xs font-semibold text-[var(--tgia-color-text)] mt-4 mb-2">
                          🛠️ 預設服務品項（進入下一步可調整數量）：
                        </p>
                        {visibleDefaultServices.map((svc, idx) => (
                          <div key={idx} className="rounded-xl bg-[var(--tgia-color-surface-muted)] p-2 text-xs">
                            <span className="text-[var(--tgia-color-text-muted)]">{svc.category}</span>
                            <div className="text-[var(--tgia-color-text)] mt-1">
                              • {svc.service} <span className="font-semibold text-[var(--tgia-color-brand-text)]">× {svc.defaultQuantity}</span>
                            </div>
                          </div>
                        ))}
                      </>
                    )}

                    {pkg.recommendedSampleType && (
                      <div className="mt-3 border-t border-[var(--tgia-color-border)] pt-3">
                        <p className="text-xs text-[var(--tgia-color-text-muted)]">
                          💡 建議樣品類型：
                          <span className="ml-1 font-semibold text-[var(--tgia-color-caution-text-muted)]">
                            {pkg.recommendedSampleType}
                          </span>
                        </p>
                      </div>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        )}

        <div className="mt-4 rounded-r-xl border-l-4 border-[var(--tgia-color-brand)] bg-[var(--tgia-color-surface-muted)] p-3 text-sm text-[var(--tgia-color-text)]">
          <p className="mb-1 font-semibold text-[var(--tgia-color-brand-text)]">💡 使用說明</p>
          <ul className="space-y-1 ml-4 text-xs">
            <li>• 選擇常用組合後，系統會自動勾選對應的服務類別</li>
            <li>• 進入 Step 2 後，會自動帶入預設的服務品項</li>
            <li>• 您可以在 Step 2 調整每個品項的數量或新增其他服務</li>
            <li>• 如果不想使用組合，可直接在下方手動勾選服務類別</li>
          </ul>
        </div>
      </div>


      {/* 服務類別勾選 */}
      <div className="rounded-2xl border border-[var(--tgia-color-border)] bg-[var(--tgia-color-surface-subtle)] p-4 sm:p-6 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-[var(--tgia-color-text)]">
            <Check size={20} className="text-[var(--tgia-color-brand-text)]" />
            請勾選需要的服務類別 <span className="text-[var(--tgia-color-accent)]">*</span>
          </h3>

          {/* 🆕 一鍵清除按鈕 */}
          <button
            type="button"
            onClick={handleClearAllCategories}
            disabled={(formData.selectedServiceCategories.length === 0 && !formData.selectedPackage)}
            className={`${buttonSizeClasses.sm} ${buttonToneClasses.clear}`}
          >
            <RotateCcw size={16} />
            清除全部
          </button>
        </div>
        <p className="mb-4 text-sm text-[var(--tgia-color-text-muted)]">
          💡 選擇後，下一步將自動帶入對應的表單項目
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {availableServiceCategories.map((category) => (
            <label
              key={category.id}
              className={`flex cursor-pointer items-center gap-3 rounded-xl border p-4 transition ${formData.selectedServiceCategories.includes(category.value)
                ? 'border-[var(--tgia-color-brand)] bg-[var(--tgia-color-surface-active)]'
                : 'border-[var(--tgia-color-border)] bg-[var(--tgia-color-surface)] hover:border-[var(--tgia-color-brand)]'
                }`}
            >
              <input
                type="checkbox"
                checked={formData.selectedServiceCategories.includes(category.value)}
                onChange={() => handleServiceCategoryToggle(category.value)}
                className="h-5 w-5 rounded text-[var(--tgia-color-brand-text)] focus:ring-2 focus:ring-[var(--tgia-color-focus)]"
              />
              <span className="font-medium text-[var(--tgia-color-text)]">{category.label}</span>
            </label>
          ))}
        </div>
        {formData.selectedServiceCategories.length > 0 && (
          <div className={`mt-4 rounded-xl border p-3 ${infoToneClasses.success}`}>
            <p className="text-sm">
              ✅ 已選擇 {formData.selectedServiceCategories.length} 個服務類別：
              <span className="ml-2 font-semibold">
                {formData.selectedServiceCategories.join('、')}
              </span>
            </p>
          </div>
        )}
      </div>
    </div>
  );

  // 渲染步驟2：委託內容
  // 3️⃣ 修改後的 renderStep2
  // 修改 renderStep2 中的提示訊息
  const renderStep2 = () => {
    const totalSequencing = calculateTotalSequencing();
    const extractionType = getExtractionType();

    return (
      <div className="space-y-6">
      <div className="rounded-2xl border border-[var(--tgia-color-border)] bg-[var(--tgia-color-surface)] p-4 sm:p-6 shadow-sm">
        <h3 className="mb-4 text-xl font-bold text-[var(--tgia-color-text)]">委託內容</h3>
          {/* 🆕 組合倍數調整區塊（只在使用組合時顯示） */}
          {formData.selectedPackage && (
            <div className="rounded-2xl border border-[var(--tgia-color-brand-border)] bg-[var(--tgia-color-surface-soft)] p-4">
              <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
                <div className="w-full flex-1 sm:w-auto">
                  <h4 className="mb-1 flex flex-wrap items-center gap-2 font-semibold text-[var(--tgia-color-brand-text)]">
                    ⚡ 樣本數量
                    {(() => {
                      const pkg = safeCommonPackages.find(p => p.id === formData.selectedPackage);
                      return pkg ? (
                        <span className="text-sm font-normal text-[var(--tgia-color-text-muted)]">
                          ({pkg.icon} {pkg.name})
                        </span>
                      ) : null;
                    })()}
                  </h4>
                  <p className="text-xs text-[var(--tgia-color-text-muted)]">
                    調整樣本數量會同步更新所有組合服務的數量（自行新增的服務不受影響）
                  </p>
                </div>

                {/* 倍數輸入框 */}
                <div className="flex flex-wrap items-center gap-3 sm:ml-4">
                  <label className="whitespace-nowrap text-sm font-medium text-[var(--tgia-color-text)]">
                    樣本數量：
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handlePackageMultiplierChange(formData.packageMultiplier - 1)}
                      disabled={formData.packageMultiplier <= 1}
                      aria-label="減少樣本數量"
                      className="flex h-[2.375rem] w-[2.375rem] items-center justify-center rounded-lg border border-[var(--tgia-color-control-border)] bg-[var(--tgia-color-surface)] font-bold text-[var(--tgia-color-brand-text)] hover:bg-[var(--tgia-color-surface-muted)] disabled:cursor-not-allowed disabled:border-[var(--tgia-color-border-subtle)] disabled:bg-[var(--tgia-color-surface-active)]"
                    >
                      −
                    </button>

                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={formData.packageMultiplier}
                      onWheel={preventNumberInputScroll}
                      onChange={(e) => handlePackageMultiplierChange(e.target.value)}
                      className="w-20 rounded-lg border border-[var(--tgia-color-control-border)] bg-[var(--tgia-color-input)] px-3 py-2 text-center text-sm font-semibold leading-5 text-[var(--tgia-color-brand-text)] focus:border-[var(--tgia-color-focus)] focus:outline-none focus:ring-2 focus:ring-[var(--tgia-color-focus-ring)]"
                    />

                    <button
                      type="button"
                      onClick={() => handlePackageMultiplierChange(formData.packageMultiplier + 1)}
                      aria-label="增加樣本數量"
                      className="flex h-[2.375rem] w-[2.375rem] items-center justify-center rounded-lg border border-[var(--tgia-color-control-border)] bg-[var(--tgia-color-surface)] font-bold text-[var(--tgia-color-brand-text)] hover:bg-[var(--tgia-color-surface-muted)]"
                    >
                      +
                    </button>

                    <span className="ml-2 text-sm text-[var(--tgia-color-text-muted)]"></span>
                  </div>
                </div>
              </div>

              {/* 顯示計算後的數量預覽 */}
              {(() => {
                const pkg = safeCommonPackages.find(p => p.id === formData.selectedPackage);
                if (!pkg) return null;

                // return (
                //   <div className="mt-3 pt-3 border-t border-[var(--tgia-color-ap-locked-border)]">
                //     <p className="text-xs font-semibold text-[var(--tgia-color-text)] mb-2">
                //       📊 當前組合服務數量：
                //     </p>
                //     <div className="flex flex-wrap gap-2">
                //       {pkg.defaultServices.map((svc, idx) => {
                //         const actualQuantity = (parseInt(svc.defaultQuantity) || 1) * formData.packageMultiplier;
                //         return (
                //           <div key={idx} className="bg-[var(--tgia-color-surface)] rounded px-3 py-1.5 text-xs border border-[var(--tgia-color-ap-locked-border)]">
                //             <span className="text-[var(--tgia-color-text-muted)]">{svc.category.replace(/\s*\([^)]*\)/, '')}</span>
                //             <span className="mx-1.5">→</span>
                //             <span className="font-semibold text-[var(--tgia-color-ap-locked-text)]">
                //               {actualQuantity}
                //             </span>
                //           </div>
                //         );
                //       })}
                //     </div>
                //   </div>
                // );
              })()}
            </div>
          )}

          {/* 🆕 顯示萃取類型提示 */}
          {extractionType && (
            <div className={`mb-4 rounded-lg border-2 p-3 ${extractionType === 'DNA' || extractionType === 'RNA'
              ? infoToneClasses.success
              : infoToneClasses.caution_secondary
              }`}>
              {/* <p className="text-sm font-medium">
                {extractionType === 'DNA' && (
                  <>
                    🧬 已選擇 <span className="font-bold">DNA 萃取</span>，建庫服務將只顯示 DNA 相關選項（排除 L-RN 開頭）
                  </>
                )}
                {extractionType === 'RNA' && (
                  <>
                    🧬 已選擇 <span className="font-bold">RNA 萃取</span>（如 Q-ER03），建庫服務將只顯示 <span className="font-bold">L-RN 開頭</span>的 RNA 相關選項
                  </>
                )}
                {extractionType === 'MIXED' && (
                  <>
                    🧬 已選擇 <span className="font-bold">DNA 和 RNA 萃取</span>，建庫服務顯示全部選項
                  </>
                )}
              </p> */}
            </div>
          )}

          <p className="text-sm text-[var(--tgia-color-text-muted)] mb-6">
            📋 請填寫您在步驟1選擇的各項服務類別詳細資訊
          </p>

          {formData.serviceItems.map((item, index) => (
            <div key={index} className="mb-6 rounded-2xl border border-[var(--tgia-color-border)] bg-[var(--tgia-color-surface)] p-4">
              <div className="mb-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-lg font-semibold text-[var(--tgia-color-brand-text)]">
                    {item.category}
                  </h4>
                </div>
                <div className="h-px bg-[var(--tgia-color-border-subtle)] mb-4"></div>
              </div>

              <div className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-[var(--tgia-color-text)]">
                      服務品項與數量 <span className="text-[var(--tgia-color-accent)]">*</span>
                    </label>
                    {canAddServiceForCategory(item.category) && (
                      <button
                        type="button"
                        onClick={() => addService(index)}
                        disabled={isLocked}
                        className={`${buttonSizeClasses.sm} ${buttonToneClasses.add}`}
                      >
                        <Plus size={16} />
                        新增品項
                      </button>
                    )}
                  </div>

                  {item.services.map((serviceItem, serviceIndex) => {
                    // 🆕 動態獲取當前可用的服務選項
                    const availableOptions = item.category === '建庫服務 (L)'
                      ? getFilteredLibraryServices()
                      : getServiceOptionsForCategory(item.category) || [];  // ✅ 正確：函數調用
                    const showHiddenOptions = item.category === '萃取/QC (EQ)'
                      && availableOptions.length > 0
                      && availableOptions.every(opt => opt?.hidden === true);

                    // 🆕 檢查當前選擇的服務是否還在可用列表中
                    const isCurrentServiceAvailable = availableOptions.some(
                      opt => opt.value === serviceItem.service
                    );
                    const quantityUnitLabel = getServiceQuantityUnitLabel(serviceItem.service);
                    // 🆕 檢查是否為綁定的建庫服務


                    return (
                      <div key={serviceIndex} className="space-y-2">
                        <div className="tgia-service-input-row flex flex-wrap gap-2 items-start bg-[var(--tgia-color-surface-muted)] p-3 rounded-lg border border-[var(--tgia-color-border-subtle)] sm:flex-nowrap">
                          <div className="flex-1 basis-full sm:basis-0">
                            {/* 🆕 使用可搜尋的下拉選單 */}
                            <SearchableSelect
                              value={serviceItem.service}
                              options={[
                                { value: '', description: '請選擇服務品項' },
                                ...availableOptions
                              ]}
                              onChange={(value) => handleServiceChange(index, serviceIndex, 'service', value)}
                              placeholder="請輸入或選擇服務品項"
                              itemIndex={index}
                              serviceIndex={serviceIndex}
                              showHidden={showHiddenOptions}

                            />


                            {/* 🆕 如果之前選擇的服務現在不可用，顯示警告 */}
                            {serviceItem.service && !isCurrentServiceAvailable && (
                              <p className="text-xs text-[var(--tgia-color-accent)] mt-1">
                                ⚠️ 此服務與目前選擇的萃取類型不符，請重新選擇
                              </p>
                            )}
                          </div>
                          <div className="flex flex-1 items-center gap-2 sm:w-40 sm:flex-initial">
                            <input
                              type="number"
                              value={serviceItem.quantity}
                              onWheel={preventNumberInputScroll}
                              onChange={(e) => handleServiceChange(index, serviceIndex, 'quantity', e.target.value)}
                              className={compactInputToneClasses.text}
                              placeholder="數量"
                              min="1"
                            />
                            {quantityUnitLabel && (
                              <span className="w-10 text-xs font-semibold text-[var(--tgia-color-brand-text)]">
                                {quantityUnitLabel}
                              </span>
                            )}
                          </div>
                          {item.services.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeService(index, serviceIndex)}
                              className="rounded-lg p-2 text-[var(--tgia-color-accent)] transition hover:bg-[var(--tgia-color-error-surface)] hover:text-[var(--tgia-color-error-text)]"
                              aria-label="刪除這項服務"
                            >
                              <X size={18} />
                            </button>
                          )}
                        </div>

                        {/* 顯示選中服務的說明 */}
                        {serviceItem.service && isCurrentServiceAvailable && (
                          <div className="ml-3 mt-2">
                            {(() => {
                              const desc = availableOptions.find(opt => opt.value === serviceItem.service)?.description || '無說明';
                              const lines = desc.split('\n').filter(line => line.trim());

                              return (
                                <div className="rounded-r-xl border-l-4 border-[var(--tgia-color-brand)] bg-[var(--tgia-color-surface-muted)] px-3 py-2 text-sm">
                                  <span className="mb-2 block font-semibold text-[var(--tgia-color-brand-text)]">說明：</span>
                                  <div className="space-y-1.5 text-[var(--tgia-color-text)]">
                                    {lines.map((line, idx) => {
                                      // 檢查是否為列表項（以數字或符號開頭）
                                      const isListItem = /^[\d]+[.)]\s*/.test(line) || /^[•\-*]\s*/.test(line);

                                      if (isListItem) {
                                        return (
                                          <div key={idx} className="flex items-start gap-2 pl-2">
                                            <span className="flex-shrink-0 text-[var(--tgia-color-brand-text)]">•</span>
                                            <span className="flex-1">{line.replace(/^[\d]+[.)]\s*|^[•\-*]\s*/, '')}</span>
                                          </div>
                                        );
                                      }

                                      return <div key={idx} className={idx === 0 ? 'font-medium' : ''}>{line}</div>;
                                    })}
                                  </div>
                                </div>
                              );
                            })()}

                            {/* 顯示單項定序量 */}
                            {item.category === '定序服務 (S)' && (() => {
                              const sequencingOptions = serviceOptionsByCategory['定序服務 (S)'] || [];
                              const serviceConfig = sequencingOptions.find(opt => opt.value === serviceItem.service);
                              const amount = serviceConfig?.sequencingAmount;

                              return amount ? (
                                <div className="mt-2 px-3 py-2 bg-[var(--tgia-color-success-surface)] border-l-4 border-[var(--tgia-color-success-border)] rounded-r text-sm">
                                  <span className="font-semibold text-[var(--tgia-color-success-text)]">定序量：</span>
                                  <span className="text-[var(--tgia-color-success-text)] ml-2">
                                    <span className="font-bold ml-1">
                                      {(amount * (parseInt(serviceItem.quantity) || 0)).toLocaleString()} GB
                                    </span>
                                  </span>
                                </div>
                              ) : null;
                            })()}
                            {/* 顯示單項定序量 */}

                          </div>
                        )}


                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}

          {/* 定序量總計顯示 */}
          {totalSequencing > 0 && (
            <div className="tgia-total-sequencing mt-6 border-2 border-[var(--tgia-color-success-border)] rounded-lg p-4 bg-[var(--tgia-color-success-surface)] shadow-md">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-lg font-semibold text-[var(--tgia-color-text)] flex items-center gap-2">
                    📊 總定序量
                  </span>
                  <p className="text-xs text-[var(--tgia-color-text-muted)] mt-1">
                    根據您選擇的定序服務自動計算
                  </p>
                </div>
                <span className="text-3xl font-bold text-[var(--tgia-color-success-text)]">
                  {totalSequencing.toLocaleString()} GB
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const getSampleInfoContext = () => createSampleInfoContext({
    formData,
    apPackageInfo: getAPPackageConfig(),
    getAllowedSampleTypes,
    getSampleTypeRestrictionMessage,
    getSpeciesMode,
    serviceCatalog: serviceOptionsByCategory
  });

  const renderPreviewTextCard = (label, value, {
    colSpan = 1,
    wrapperClassName = 'bg-[var(--tgia-color-surface-muted)] p-2 rounded',
    valueClassName = ''
  } = {}) => {
    const displayValue = value === '' || value == null ? '—' : value;

    return (
      <div className={`${wrapperClassName} ${colSpan === 2 ? 'sm:col-span-2' : ''}`}>
        <span className="text-[var(--tgia-color-text-muted)] font-medium">{label}：</span>
        <span className={`text-[var(--tgia-color-text)] ml-2 ${valueClassName}`}>{displayValue}</span>
      </div>
    );
  };

  const renderAnalysisDocumentsPanel = (documents = []) => {
    if (!Array.isArray(documents) || documents.length === 0) return null;

    const averageWait = formatWaitTimeMs(analysisDocumentLatencyEstimate?.averageMs);
    const peakWait = formatWaitTimeMs(analysisDocumentLatencyEstimate?.maxMs);
    const waitHint = averageWait
      ? `預計等待約 ${averageWait}${peakWait && peakWait !== averageWait ? `，近期最慢約 ${peakWait}` : ''}。`
      : '目前尚無等待時間紀錄，首次下載可能需要稍候。';

    return (
      <div className="rounded-xl border border-[var(--tgia-color-border)] bg-[var(--tgia-color-surface-muted)] p-4">
        <div className="mb-3">
          <h3 className="text-base font-semibold text-[var(--tgia-color-text)]">分析範例結果與相關文件</h3>
          <p className="text-sm text-[var(--tgia-color-text-muted)]">此分析服務提供以下文件，可依需求下載參考</p>
          <p className="mt-1 text-xs text-[var(--tgia-color-text-muted)]">
            按下下載後瀏覽器需要準備檔案視窗，{waitHint}
          </p>
        </div>
        <div className="space-y-2">
          {documents.map((analysisDocument) => (
            <div
              key={analysisDocument.id}
              className="flex flex-col gap-3 rounded-lg border border-[var(--tgia-color-border-subtle)] bg-[var(--tgia-color-surface)] p-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0">
                <div className="font-medium text-[var(--tgia-color-text)]">{analysisDocument.label}</div>
                {analysisDocument.description && (
                  <div className="text-xs text-[var(--tgia-color-text-muted)]">{analysisDocument.description}</div>
                )}
              </div>
              <button
                onClick={() => downloadAnalysisDocument(analysisDocument)}
                disabled={isExporting}
                className={`${buttonToneClasses.download_primary} w-full shrink-0 sm:w-auto`}
              >
                <Download size={18} />
                {isExporting ? '下載中...' : '下載'}
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderAnalysisFlowBlock = (analysisFlowInfo) => {
    if (!analysisFlowInfo) return null;

    // 工具版本由分析服務品項決定，沒有設定規則的品項不顯示。
    const toolVersionText = formatScRnaToolVersionText(getScRnaPipelineToolVersions({
      analysisServiceValue: analysisFlowInfo.serviceValue
    }));

    // 平台以實驗類型為準，和送出的 platform_type 一致。
    const platformLabel = analysisFlowInfo.platformLabel
      ? resolveScRnaPlatformLabel(
        findScRNAExperimentOption('', formData.scRNAExperimentInfo?.serviceName),
        analysisFlowInfo.platformLabel
      )
      : '';

    return (
      <div className="sm:col-span-2 rounded-xl border border-[var(--tgia-color-brand-border)] bg-[var(--tgia-color-surface-soft)] p-3">
        <div className="flex items-center justify-between mb-2">
          <span className="font-medium text-[var(--tgia-color-text-muted)]">分析流程：</span>
          <div className="flex items-center gap-2">
            {platformLabel && (
              <span className={`rounded-full px-3 py-1 text-xs font-semibold ${badgeToneClasses.secondary}`}>
                {platformLabel}
              </span>
            )}
            <span className={`rounded-full px-3 py-1 text-xs font-bold ${badgeToneClasses.secondary}`}>
              {analysisFlowInfo.pipelineLabel}
            </span>
          </div>
        </div>
        <div className="space-y-2 text-sm">
          <div className="bg-[var(--tgia-color-surface)] p-3 rounded-lg">
            <span className="text-[var(--tgia-color-text-muted)] font-medium">服務品項：</span>
            <span className="font-bold ml-2 text-[var(--tgia-color-brand-text)]">{analysisFlowInfo.serviceValue}</span>
          </div>
          <div className="bg-[var(--tgia-color-surface)] p-3 rounded-lg">
            <span className="text-[var(--tgia-color-text-muted)] font-medium">摘要：</span>
            <p className="text-[var(--tgia-color-text)] mt-1 whitespace-pre-line">
              {analysisFlowInfo.description2 || '無說明'}
            </p>
            {toolVersionText && (
              <p className="mt-2 text-xs font-medium text-[var(--tgia-color-text-muted)]">
                🔧 {toolVersionText}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderSpeciesPreviewBlock = ({ speciesMode, speciesInfo = getSpeciesDisplayInfo(formData) }) => {
    if (isDetailedSpeciesMode(speciesMode)) {
      const detailedSpeciesList = getSpeciesOptionsForMode(speciesMode, formData.serviceItems, formData);
      const selectedSpeciesObj = findSpeciesOptionBySelection(
        formData.speciesSelectionKey || formData.speciesSelection,
        detailedSpeciesList
      );
      const speciesData = selectedSpeciesObj || {
        commonName: speciesInfo?.primary || '未填寫',
        scientificName: speciesInfo?.details?.[0] || formData.speciesScientific || '—',
        genome: speciesInfo?.details?.[1] || formData.speciesGenome || '—',
        TenXGenomicsRefVersion: speciesInfo?.tenx?.[0] || formData.TenXGenomicsRefVersion || '—',
        TenXGenomicsProbeSetVersion: speciesInfo?.tenx?.[1] || formData.TenXGenomicsProbeSetVersion || '—',
        TenXGenomicsVDJVersion: speciesInfo?.tenx?.[2] || formData.TenXGenomicsVDJVersion || '—'
      };

      return (
        <div className="sm:col-span-2 rounded-lg bg-[var(--tgia-color-surface-subtle)] p-2">
          <span className="font-medium text-[var(--tgia-color-text-muted)]">物種：</span>
          <div className="mt-2 overflow-hidden rounded-lg border border-[var(--tgia-color-brand-border)] shadow-sm bg-[var(--tgia-color-surface-soft)]">
            <table className="min-w-full text-sm divide-y divide-[var(--tgia-color-brand-border)]">
              <tbody className="divide-y divide-[var(--tgia-color-border-subtle)]">
                <tr>
                  <td className="w-1/3 bg-[var(--tgia-color-surface-active)] px-3 py-2 font-semibold text-[var(--tgia-color-brand-text)]">俗名</td>
                  <td className="px-3 py-2 text-[var(--tgia-color-text)] font-medium">{speciesData.commonName || '—'}</td>
                </tr>
                <tr>
                  <td className="bg-[var(--tgia-color-surface-active)] px-3 py-2 font-semibold text-[var(--tgia-color-brand-text)]">學名</td>
                  <td className="px-3 py-2 text-[var(--tgia-color-text)] italic">{speciesData.scientificName || '—'}</td>
                </tr>
                <tr>
                  <td className="bg-[var(--tgia-color-surface-active)] px-3 py-2 font-semibold text-[var(--tgia-color-brand-text)]">參考基因組</td>
                  <td className="px-3 py-2 text-[var(--tgia-color-text)]">{speciesData.genome || '—'}</td>
                </tr>
                {(speciesMode === 'scrna' || speciesMode === 'visiumhd') && (
                  <>
                    <tr>
                      <td className="bg-[var(--tgia-color-surface-active)] px-3 py-2 text-xs font-semibold text-[var(--tgia-color-brand-text)]">10X Genomics Ref Version</td>
                      <td className="px-3 py-2 text-[var(--tgia-color-text-muted)] text-xs">{speciesData.TenXGenomicsRefVersion || '—'}</td>
                    </tr>
                    <tr>
                      <td className="bg-[var(--tgia-color-surface-active)] px-3 py-2 text-xs font-semibold text-[var(--tgia-color-brand-text)]">10X Genomics Probe Set Version</td>
                      <td className="px-3 py-2 text-[var(--tgia-color-text-muted)] text-xs">{speciesData.TenXGenomicsProbeSetVersion || '—'}</td>
                    </tr>
                    {speciesMode === 'scrna' && (
                      <tr>
                        <td className="bg-[var(--tgia-color-surface-active)] px-3 py-2 text-xs font-semibold text-[var(--tgia-color-brand-text)]">10X Genomics VDJ Version</td>
                        <td className="px-3 py-2 text-[var(--tgia-color-text-muted)] text-xs">{speciesData.TenXGenomicsVDJVersion || '—'}</td>
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    return (
      <div className="rounded-lg bg-[var(--tgia-color-surface-subtle)] p-2">
        <span className="font-medium text-[var(--tgia-color-text-muted)]">物種：</span>
        <div className="mt-1 ml-2 text-[var(--tgia-color-text)]">
          <div className="font-semibold">
            {speciesInfo.primary}
          </div>
          {speciesInfo.details.length > 0 && (
            <div className="text-xs text-[var(--tgia-color-text-subtle)]">
              {speciesInfo.details.join('、')}
            </div>
          )}
          {speciesInfo.tenx.length > 0 && (
            <div className="text-xs text-[var(--tgia-color-brand-text)]">
              10X：{speciesInfo.tenx.join('、')}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSampleInfoPreviewSection = () => {
    const { speciesMode, analysisFlowInfo } = getSampleInfoContext();
    const isAnalysisOnly = isAnalysisOnlyOrder(formData);
    const showAnalysisSampleSheetLite = isAnalysisOnly && shouldUseAnalysisSampleSheetLite(formData);
    const previewScRnaRule = getSampleSheetScRnaRule(formData);
    const previewIsChIPseq = isChIPseqOrder(formData);
    const previewModel = buildSampleInfoPreviewModel({
      formData,
      resolvedSampleFormData,
      isGeneralNoSampleOrder,
      isAnalysisOnlyOrder: isAnalysisOnly,
      showAnalysisSampleSheetLite,
      // ChIP-seq 的列序有意義，留白的 input 不能被擠掉，否則序號與標示會對不起來。
      keepBlankSampleRows: previewIsChIPseq,
      speciesMode,
      analysisFlowInfo,
      analysisParameterSummary: buildAnalysisParameterSummary(
        getAnalysisParameterDefinitionsForOrder(),
        getResolvedAnalysisParameters()
      ),
      getSpeciesDisplayInfo
    });


    return (
      <div className="border-b pb-4">
        <h4 className="font-semibold text-[var(--tgia-color-text)] mb-3 text-lg">🧬 送測樣品資訊</h4>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm mb-4">
          {previewModel.items.map((item) => {
            if (item.type === 'species') {
              return (
                <div key={item.key} className="sm:col-span-2">
                  {renderSpeciesPreviewBlock({
                    speciesMode: item.speciesMode,
                    speciesInfo: item.speciesInfo
                  })}
                </div>
              );
            }

            if (item.type === 'analysisParameters') {
              return (
                <div key={item.key} className="sm:col-span-2 rounded-lg bg-[var(--tgia-color-surface-subtle)] p-2">
                  <span className="font-medium text-[var(--tgia-color-text-muted)]">分析參數：</span>
                  <div className="mt-1 ml-2 space-y-1">
                    {item.analysisParameterSummary.map(parameter => (
                      <div key={parameter.id} className="text-[var(--tgia-color-text)]">
                        {parameter.label}：
                        <span className="font-semibold ml-1">{parameter.valueLabel}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            }

            if (item.type === 'analysisFlow') {
              return (
                <div key={item.key} className="sm:col-span-2">
                  {renderAnalysisFlowBlock(item.analysisFlowInfo)}
                </div>
              );
            }

            const itemColSpan = item.colSpan || (item.key === 'sampleTypeOther' || item.key === 'preservationMethodOther' || item.key === 'shippingMethodOther' ? 2 : 1);

            return (
              <div key={item.key} className={itemColSpan === 2 ? 'sm:col-span-2' : ''}>
                {renderPreviewTextCard(item.label, item.value, { colSpan: itemColSpan })}
              </div>
            );
          })}
          {previewModel.noSampleNotice && (
            <div className={`sm:col-span-2 rounded-lg border p-3 text-sm ${infoToneClasses.caution_primary}`}>
              {previewModel.noSampleNotice}
            </div>
          )}
        </div>

        {previewModel.librarySampleSheetRows.length > 0 && (
          <div className="mt-4">
            <h5 className="mb-2 font-semibold text-[var(--tgia-color-text)]">Library 資訊</h5>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-[var(--tgia-color-surface-active)]">
                    <th className="border p-2">序號</th>
                    <th className="border p-2">Library Name</th>
                    <th className="border p-2">Tube Label</th>
                    <th className="border p-2">Conc (ng/ul)</th>
                    <th className="border p-2">Vol (uL)</th>
                    <th className="border p-2">NGS上機濃度</th>
                    <th className="border p-2">預期定序量</th>
                    <th className="border p-2">備註</th>
                  </tr>
                </thead>
                <tbody>
                  {previewModel.librarySampleSheetRows.map((row, idx) => (
                    <tr key={idx} className="bg-[var(--tgia-color-surface)]">
                      <td className="border p-2 text-center">{idx + 1}</td>
                      <td className="border p-2">{row.sampleName}</td>
                      <td className="border p-2">{row.tubeLabel}</td>
                      <td className="border p-2">{row.conc}</td>
                      <td className="border p-2">{row.vol}</td>
                      <td className="border p-2">{row.ngsConc}</td>
                      <td className="border p-2">{row.expectedSeq}</td>
                      <td className="border p-2">{row.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {previewModel.libraryDetailRows.length > 0 && (
          <div className="mt-4">
            <h5 className="mb-2 font-semibold text-[var(--tgia-color-text)]">Library Sample Sheet</h5>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-[var(--tgia-color-surface-active)]">
                    <th className="border p-2">序號</th>
                    <th className="border p-2">Sample_Name</th>
                    <th className="border p-2">Library Prep Kit</th>
                    <th className="border p-2">Index Adapter Kit</th>
                    <th className="border p-2">Set-Well Position</th>
                    <th className="border p-2">Index 1 (i7)</th>
                    <th className="border p-2">Index 2 (i5)</th>
                    <th className="border p-2">備註</th>
                    <th className="border p-2">Library</th>
                    <th className="border p-2">Tube Name</th>
                  </tr>
                </thead>
                <tbody>
                  {previewModel.libraryDetailRows.map((row, idx) => (
                    <tr key={idx} className="bg-[var(--tgia-color-surface)]">
                      <td className="border p-2 text-center">{idx + 1}</td>
                      <td className="border p-2">{row.sampleName}</td>
                      <td className="border p-2">{row.libraryPrepKit}</td>
                      <td className="border p-2">{row.indexAdapterKit}</td>
                      <td className="border p-2">{row.setWellPosition}</td>
                      <td className="border p-2">{row.index1Seq}</td>
                      <td className="border p-2">{row.index2Seq}</td>
                      <td className="border p-2">{row.note}</td>
                      <td className="border p-2">{row.library}</td>
                      <td className="border p-2 bg-[var(--tgia-color-surface-muted)]">{row.tubeName || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {isAnalysisOnly && previewModel.sampleRows.some(row => row.sampleName) && (
          <div className="mt-4">
            <h5 className="mb-2 font-semibold text-[var(--tgia-color-text)]">Sample Sheet Lite</h5>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-[var(--tgia-color-success-surface)]">
                    <th className="border p-2">序號</th>
                    {previewIsChIPseq && <th className="border p-2">類型</th>}
                    <th className="border p-2">Sample_Name</th>
                    {previewScRnaRule.requireSampleBarcode && (
                      <th className="border p-2">Sample_Barcode</th>
                    )}
                    {previewScRnaRule.showFastqName && (
                      <th className="border p-2">{previewScRnaRule.fastqNameLabel}</th>
                    )}
                    {previewScRnaRule.requireVdjLibrary && (
                      <>
                        <th className="border p-2">V(D)J 類型</th>
                        {VDJ_LIBRARY_FASTQ_FIELDS.map(({ field, label }) => (
                          <th key={field} className="border p-2">{label}</th>
                        ))}
                      </>
                    )}
                    {previewIsChIPseq && <th className="border p-2">樣本來源</th>}
                    <th className="border p-2">備註</th>
                  </tr>
                </thead>
                <tbody>
                  {previewModel.sampleRows.map((row, idx) => (
                    <tr key={idx} className="bg-[var(--tgia-color-surface)]">
                      <td className="border p-2 text-center">{idx + 1}</td>
                      {previewIsChIPseq && (
                        <td className="border p-2 whitespace-nowrap text-center text-[var(--tgia-color-text-muted)]">
                          樣本 {getChIPseqSampleGroupNo(idx)} ·{' '}
                          {CHIPSEQ_ROW_ROLE_LABELS[getChIPseqRowRole(idx)]}
                        </td>
                      )}
                      <td className="border p-2">{row.sampleName}</td>
                      {previewScRnaRule.requireSampleBarcode && (
                        <td className="border p-2">{row.sampleBarcode}</td>
                      )}
                      {previewScRnaRule.showFastqName && (
                        <td className="border p-2">{row.fastqName}</td>
                      )}
                      {previewScRnaRule.requireVdjLibrary && (
                        <>
                          <td className="border p-2">{formatVdjLibraryTypeLabel(row.vdjType)}</td>
                          {VDJ_LIBRARY_FASTQ_FIELDS.map(({ library, field }) => (
                            <td key={field} className="border p-2">
                              {vdjLibraryTypeIncludes(row.vdjType, library) ? (row[field] || '') : ''}
                            </td>
                          ))}
                        </>
                      )}
                      {previewIsChIPseq && (
                        <td className="border p-2">{row.sampleName ? sanitizeChIPseqSampleSource(row.sampleSource) : ''}</td>
                      )}
                      <td className="border p-2">{row.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!isAnalysisOnly && previewModel.sampleRows.length > 0 && (
          <div className="mt-4">
            <h5 className="font-semibold text-[var(--tgia-color-text)] mb-2">Sample Sheet</h5>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-[var(--tgia-color-success-surface)]">
                    <th className="border p-2">序號</th>
                    <th className="border p-2">Sample_Name</th>
                    <th className="border p-2">Tube Label</th>
                    <th className="border p-2">預期定序量</th>
                    <th className="border p-2">Conc (ng/ul)</th>
                    <th className="border p-2">Vol (uL)</th>
                    <th className="border p-2">260/280</th>
                    <th className="border p-2">260/230</th>
                    <th className="border p-2">DQN/RQN</th>
                    <th className="border p-2">備註</th>
                  </tr>
                </thead>
                <tbody>
                  {previewModel.sampleRows.map((row, idx) => (
                    <tr key={idx} className="bg-[var(--tgia-color-surface)]">
                      <td className="border p-2 text-center">{idx + 1}</td>
                      <td className="border p-2">{row.sampleName}</td>
                      <td className="border p-2">{row.tubeLabel}</td>
                      <td className="border p-2">{row.expectedSeq}</td>
                      <td className="border p-2">{row.conc}</td>
                      <td className="border p-2">{row.vol}</td>
                      <td className="border p-2">{row.ratio260280}</td>
                      <td className="border p-2">{row.ratio260230}</td>
                      <td className="border p-2">{row.dqnRqn}</td>
                      <td className="border p-2">{row.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {previewModel.notes && (
          <div className={`mt-4 rounded-lg border p-3 ${infoToneClasses.caution_secondary}`}>
            <span className="text-[var(--tgia-color-text-muted)] font-medium">📝 備註：</span>
            <p className="text-[var(--tgia-color-text)] mt-1">{previewModel.notes}</p>
          </div>
        )}
      </div>
    );
  };

  // 渲染步驟3：送測樣品資訊
  const renderStep3 = () => {
    const totalSequencing = calculateTotalSequencing(); // Step2 的總定序量
    const expectedSequencing = calculateExpectedSequencing(); // Step3 樣本的預期定序量
    const isOverLimit = expectedSequencing > totalSequencing; // 是否超過
    const {
      isAPPackage,
      apPackageInfo,
      speciesMode,
      analysisFlowInfo,
      allowedSampleTypes
    } = getSampleInfoContext();
    const isAnalysisOnly = isAnalysisOnlyOrder(formData);
    const showAnalysisSampleSheetLite = isAnalysisOnly && shouldUseAnalysisSampleSheetLite(formData);
    const scRnaSampleSheetRule = getSampleSheetScRnaRule(formData);
    const isChIPseq = isChIPseqOrder(formData);
    const analysisDocumentsForStep = collectAnalysisDocumentsForOrder(formData, {
      serviceCatalog: serviceOptionsByCategory,
      analysisTemplatesByServiceType
    });

    return (
      <div className="space-y-6">
        <div className="rounded-2xl border border-[var(--tgia-color-border)] bg-[var(--tgia-color-surface)] p-4 sm:p-6 shadow-sm">
          <h3 className="mb-6 text-xl font-bold text-[var(--tgia-color-text)]">送測樣品資訊</h3>

          {/* 🆕 AP 套組資訊提示 */}
          {isAPPackage && apPackageInfo && (
            <div className="mb-6 rounded-2xl border border-[var(--tgia-color-brand-border)] bg-[var(--tgia-color-surface-soft)] p-4 shadow-sm">
              <h4 className="mb-2 flex items-center gap-2 font-semibold text-[var(--tgia-color-brand-text)]">
                📦 套組產品資訊
              </h4>
              <div className="space-y-2 text-sm">
                <div className="bg-[var(--tgia-color-surface)] p-3 rounded-lg">
                  <span className="text-[var(--tgia-color-text-muted)] font-medium">套組名稱：</span>
                  <span className="font-bold ml-2 text-[var(--tgia-color-brand-text)]">{apPackageInfo.value}</span>
                </div>
                {apPackageInfo.binding && (
                  <>
                    {apPackageInfo.binding.sampleType && (
                      <div className="bg-[var(--tgia-color-surface)] p-3 rounded-lg">
                        <span className="text-[var(--tgia-color-text-muted)] font-medium">套組樣品類型：</span>
                        <span className="font-bold ml-2 text-[var(--tgia-color-caution-text)]">
                          {(() => {
                            const sampleType = apPackageInfo.binding.sampleType;
                            if (Array.isArray(sampleType)) {
                              return sampleType.join('、');
                            }
                            if (typeof sampleType === 'string' && sampleType.includes(',')) {
                              return sampleType.split(',').map(s => s.trim()).join('、');
                            }

                            return sampleType;
                          })()}
                        </span>
                      </div>
                    )}
                    {apPackageInfo.binding.seqAmountGb && (
                      <div className="bg-[var(--tgia-color-surface)] p-3 rounded-lg">
                        <span className="text-[var(--tgia-color-text-muted)] font-medium">定序量：</span>
                        <span className="font-bold ml-2 text-[var(--tgia-color-brand-text)]">
                          {apPackageInfo.binding.seqAmountGb} GB/樣本
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* 🆕 定序量比對顯示（放在最上方） */}
          {totalSequencing > 0 && !isGeneralNoSampleOrder && (
            <div className={`tgia-sequencing-check mb-6 border-2 rounded-lg p-4 ${infoToneClasses.success}`}>
              <h4 className="mb-3 flex items-center gap-2 font-semibold text-[var(--tgia-color-success-text)]">
                📊 定序量檢查
              </h4>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div className="tgia-sequencing-check__metric bg-[var(--tgia-color-surface)] p-3 rounded-lg border">
                  <div className="text-[var(--tgia-color-text-muted)] mb-1">委託定序量（Step2）</div>
                  <div className="text-2xl font-bold text-[var(--tgia-color-brand-text)]">
                    {totalSequencing.toLocaleString()} GB
                  </div>
                </div>

                <div className="tgia-sequencing-check__metric bg-[var(--tgia-color-surface)] p-3 rounded-lg border">
                  <div className="text-[var(--tgia-color-text-muted)] mb-1">樣本預期定序量</div>
                  <div className={`text-2xl font-bold ${isOverLimit ? 'text-[var(--tgia-color-accent)]' : 'text-[var(--tgia-color-success-text)]'
                    }`}>
                    {expectedSequencing.toLocaleString()} GB
                  </div>
                </div>
              </div>

              {/* 狀態提示 */}
              {expectedSequencing === 0 ? (
                <div className={`mt-3 rounded-lg border p-3 text-sm ${infoToneClasses.error}`}>
                  ❌ 尚未填寫樣本的預期定序量
                </div>
              ) : isOverLimit ? (
                <div className={`mt-3 rounded-lg border p-3 text-sm ${infoToneClasses.error}`}>
                  <div className="font-semibold mb-1">
                    ❌ 預期定序量超過委託量！
                  </div>
                  <div>
                    超出 <span className="font-bold">{(expectedSequencing - totalSequencing).toLocaleString()} GB</span>
                    ，請調整樣本預期定序量或增加 Step2 的定序服務數量。
                  </div>
                </div>
              ) : (
                <div className={`mt-3 rounded-lg border p-3 text-sm ${infoToneClasses.success}`}>
                  <div className="font-semibold mb-1">
                    ✅ 定序量配置正常
                  </div>
                  <div>
                    剩餘 <span className="font-bold">{(totalSequencing - expectedSequencing).toLocaleString()} GB</span> 可用
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 原有的樣本資訊 */}
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h5 className="font-semibold text-[var(--tgia-color-text)]">樣本資訊</h5>
          </div>

          {/* 🆕 顯示樣品類型限制提示 */}
          {/* {restrictionMessage && (
          <div className="mb-4 p-3 bg-[var(--tgia-color-brand-surface)] border-2 border-[var(--tgia-color-upload)] rounded-lg">
            <p className="text-sm text-[var(--tgia-color-upload)] font-medium">
              {restrictionMessage}
            </p>
          </div>
        )} */}

          <div className="mb-4">
            <label className="block text-sm font-medium text-[var(--tgia-color-text)] mb-1">
              樣品類型 <span className="text-[var(--tgia-color-accent)]">*</span>
            </label>
              <select
                name="sampleType"
                value={resolvedSampleFormData.sampleType}
              onChange={(e) => {
                const newValue = e.target.value;
                // 🆕 檢查是否為允許的選項
                if (!allowedSampleTypes.includes(newValue)) {
                  setMessage('❌ 此樣品類型不符合您選擇的服務類別');
                  setTimeout(() => setMessage(''), 3000);
                  return;
                }
                handleInputChange(e);
                }}
              className={inputToneClasses.select}
              >
              {/* 🆕 只顯示允許的選項 */}
              {allowedSampleTypes.map(type => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>

            {/* 🆕 當選擇的類型不被允許時顯示警告 */}
            {resolvedSampleFormData.sampleType && !allowedSampleTypes.includes(resolvedSampleFormData.sampleType) && (
              <div className={`mt-2 rounded-lg border p-2 text-sm ${infoToneClasses.error}`}>
                ⚠️ 目前選擇的樣品類型「{resolvedSampleFormData.sampleType}」與您的服務類別不符，請重新選擇
              </div>
            )}

            {/* 當選擇「其他」時顯示輸入框 */}
            {resolvedSampleFormData.sampleType === '其他' && (
              <input
                type="text"
                name="sampleTypeOther"
                value={resolvedSampleFormData.sampleTypeOther}
                onChange={handleInputChange}
                className={`${compactInputToneClasses.text} mt-2`}
                placeholder="請說明樣品類型"
              />
            )}
          </div>

          {/* Library 送件資訊 */}
          {resolvedSampleFormData.sampleType === 'Library' && (
            <div className="sm:col-span-2 mt-4 rounded-2xl border border-[var(--tgia-color-brand-border)] bg-[var(--tgia-color-surface-soft)] p-4 sm:p-6">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                <h4 className="text-lg font-bold text-[var(--tgia-color-text)]">Library 送件資訊</h4>
                <div className="flex flex-wrap gap-2">
                  <input
                    ref={excelUploadRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleExcelUpload}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={downloadTemplate}
                    className={`${buttonSizeClasses.sm} ${buttonToneClasses.excel}`}
                  >
                    <Download size={14} />
                    下載 Excel 範本
                  </button>
                  <button
                    type="button"
                    onClick={() => excelUploadRef.current?.click()}
                    className={`${buttonSizeClasses.sm} ${buttonToneClasses.upload}`}
                  >
                    <Upload size={14} />
                    上傳 Excel
                  </button>
                </div>
              </div>

              {/* 🆕 拖拉上傳區域 */}
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => excelUploadRef.current?.click()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    excelUploadRef.current?.click();
                  }
                }}
                role="button"
                tabIndex={0}
                className={`mb-4 cursor-pointer p-4 sm:p-6 border-2 border-dashed rounded-lg transition-all ${isDragging
                  ? 'border-[var(--tgia-color-brand)] bg-[var(--tgia-color-surface-active)] scale-105'
                  : 'border-[var(--tgia-color-brand-border)] bg-[var(--tgia-color-surface)] hover:border-[var(--tgia-color-brand)] hover:bg-[var(--tgia-color-surface-muted)]'
                  }`}
              >
                <div className="text-center">
                  <Upload size={48} className={`mx-auto mb-2 ${isDragging ? 'text-[var(--tgia-color-brand-text)]' : 'text-[var(--tgia-color-placeholder)]'}`} />
                  <p className="mb-1 text-sm font-medium text-[var(--tgia-color-text)]">
                    {isDragging ? '放開以上傳檔案' : '拖曳 Excel 檔案到這裡'}
                  </p>
                  <p className="text-xs text-[var(--tgia-color-text-muted)]">
                    或點擊此區塊選擇檔案
                  </p>
                  <p className="mt-2 text-xs text-[var(--tgia-color-disabled-text)]">
                    支援格式：.xlsx, .xls
                  </p>
                </div>
              </div>
              {/* 濃度測定方式 */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-[var(--tgia-color-text)] mb-2">
                  濃度測定方式 <span className="text-[var(--tgia-color-accent)]"></span>
                </label>
                <div className="flex flex-wrap gap-3">
                  <label className="flex cursor-pointer items-center gap-2 rounded-lg border px-4 py-2 transition hover:bg-[var(--tgia-color-surface-active)]">
                    <input
                      type="radio"
                      name="libraryConcMethod"
                      value="Qubit"
                      checked={formData.libraryInfo.concMethod === 'Qubit'}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        libraryInfo: { ...prev.libraryInfo, concMethod: e.target.value }
                      }))}
                      className="h-4 w-4 text-[var(--tgia-color-brand-text)]"
                    />
                    <span className="text-sm font-medium">Qubit</span>
                  </label>

                  <label className="flex cursor-pointer items-center gap-2 rounded-lg border px-4 py-2 transition hover:bg-[var(--tgia-color-surface-active)]">
                    <input
                      type="radio"
                      name="libraryConcMethod"
                      value="qPCR"
                      checked={formData.libraryInfo.concMethod === 'qPCR'}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        libraryInfo: { ...prev.libraryInfo, concMethod: e.target.value }
                      }))}
                      className="h-4 w-4 text-[var(--tgia-color-brand-text)]"
                    />
                    <span className="text-sm font-medium">qPCR</span>
                  </label>

                  <label className="flex cursor-pointer items-center gap-2 rounded-lg border px-4 py-2 transition hover:bg-[var(--tgia-color-surface-active)]">
                    <input
                      type="radio"
                      name="libraryConcMethod"
                      value="PicoGreen"
                      checked={formData.libraryInfo.concMethod === 'PicoGreen'}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        libraryInfo: { ...prev.libraryInfo, concMethod: e.target.value }
                      }))}
                      className="h-4 w-4 text-[var(--tgia-color-brand-text)]"
                    />
                    <span className="text-sm font-medium">PicoGreen</span>
                  </label>
                </div>
              </div>

              <div className="mb-4 rounded-lg border border-[var(--tgia-color-brand-border)] bg-[var(--tgia-color-surface-muted)] p-2 text-xs text-[var(--tgia-color-text)]">
                📌 使用說明：
                <br />• 點擊「下載 Excel 範本」取得包含兩個工作表的範本檔案
                <br />• 在範本中填寫 Sample Sheet 和 Library Sample Sheet 資料
                <br />• 點擊「上傳 Excel」自動匯入兩個表格的資料
                <br />• 也可以使用各表格上的「貼上資料」功能單獨匯入
              </div>

              {/* Sample Sheet */}
              <div className="mb-6">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <h5 className="font-semibold text-[var(--tgia-color-text)]">Library 資訊</h5>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={clearSampleSheet}
                      className={`${buttonSizeClasses.xs} ${buttonToneClasses.clear}`}
                    >
                      <RotateCcw size={14} />
                      清空
                    </button>
                    <button
                      type="button"
                      onClick={addLibrarySampleSheetRow}
                      className={`${buttonSizeClasses.xs} ${buttonToneClasses.add}`}
                      title={getAddRowTitle('Library')}
                    >
                      <Plus size={14} />
                      新增 Library
                    </button>
                    <button
                      type="button"
                      onClick={handleDistributeSequencing}
                      disabled={!!getAPPackageConfig()?.seqPerSample}
                      className={`${buttonSizeClasses.xs} ${buttonToneClasses.other}`}
                      title={getAPPackageConfig()?.seqPerSample ? '套組產品定序量已鎖定' : '將總定序量平均分配給所有樣本'}
                    >
                      <span className="text-sm">⚖️</span>
                      均分定序量
                    </button>
                  </div>
                </div>

                <div className={`mb-2 rounded-lg border p-2 text-xs ${infoToneClasses.caution_secondary}`}>
                  💡 提示：

                  <br />• 複製 Excel 資料前，請先確認沒有合併的儲存格
                  <br />• {getRowCountRuleHint('Library')}
                  <br />• 從 Library Name 欄位開始複製（不含序號和標題）
                  <br />• Library 欄位可輸入（建議來自 Sample Sheet 的 Library Name）
                  <br />• 複製貼上時 Library 欄位也會自動填入
                  <br />• 點擊表格任一儲存格後按 Ctrl+V 貼上，系統會自動新增行數
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-[var(--tgia-color-surface-active)]">
                        <th className="border p-2">序號</th>
                        <th className="border p-2">Library Name*</th>
                        <th className="border p-2">Tube Label*</th>
                        <th className="border p-2">Conc* (ng/ul)</th>
                        <th className="border p-2">Vol* (uL)</th>
                        <th className="border p-2">NGS上機濃度 (pM)</th>
                        <th className="border p-2">預期定序量</th>
                        <th className="border p-2">備註</th>
                        <th className="border p-2">操作</th>
                      </tr>
                    </thead>
                    {/* Sample Sheet (Library 第一個表格) */}
                    <tbody onPaste={(e) => handleTablePaste(e, 0)}>
                      {formData.libraryInfo.sampleSheet.map((row, idx) => (
                        <tr key={idx} className="bg-[var(--tgia-color-surface)]">
                          <td className="border p-2 text-center">{idx + 1}</td>

                          {/* 🆕 Sample_Name - 加入錯誤檢查 */}
                          <td className="border p-2">
                            <input
                              type="text"
                              value={row.sampleName}
                              onChange={(e) => {
                                handleLibrarySampleSheetChange(idx, 'sampleName', e.target.value);
                                // 清除錯誤
                                if (fieldErrors.sampleSheet[idx]?.sampleName) {
                                  setFieldErrors(prev => {
                                    const newErrors = { ...prev };
                                    if (newErrors.sampleSheet[idx]) {
                                      delete newErrors.sampleSheet[idx].sampleName;
                                      if (Object.keys(newErrors.sampleSheet[idx]).length === 0) {
                                        delete newErrors.sampleSheet[idx];
                                      }
                                    }
                                    return newErrors;
                                  });
                                }
                              }}
                              className={`${compactInputToneClasses.text} ${fieldErrors.sampleSheet[idx]?.sampleName ? 'tgia-field-error ' : ''}`}
                              title={fieldErrors.sampleSheet[idx]?.sampleName || ''}
                            />
                            {fieldErrors.sampleSheet[idx]?.sampleName && (
                              <p className="text-xs text-[var(--tgia-color-accent)] mt-1">
                                {fieldErrors.sampleSheet[idx].sampleName}
                              </p>
                            )}
                          </td>

                          {/* 🆕 Tube Label - 加入錯誤檢查 */}
                          <td className="border p-2">
                            <input
                              type="text"
                              value={row.tubeLabel}
                              onChange={(e) => {
                                handleLibrarySampleSheetChange(idx, 'tubeLabel', e.target.value);
                                // 清除錯誤
                                if (fieldErrors.sampleSheet[idx]?.tubeLabel) {
                                  setFieldErrors(prev => {
                                    const newErrors = { ...prev };
                                    if (newErrors.sampleSheet[idx]) {
                                      delete newErrors.sampleSheet[idx].tubeLabel;
                                      if (Object.keys(newErrors.sampleSheet[idx]).length === 0) {
                                        delete newErrors.sampleSheet[idx];
                                      }
                                    }
                                    return newErrors;
                                  });
                                }
                              }}
                              className={`${compactInputToneClasses.text} ${fieldErrors.sampleSheet[idx]?.tubeLabel ? 'tgia-field-error ' : ''}`}
                              title={fieldErrors.sampleSheet[idx]?.tubeLabel || ''}
                            />
                            {fieldErrors.sampleSheet[idx]?.tubeLabel && (
                              <p className="text-xs text-[var(--tgia-color-accent)] mt-1">
                                {fieldErrors.sampleSheet[idx].tubeLabel}
                              </p>
                            )}
                          </td>

                          <td className="border p-2">
                            <input
                              type="number"
                              value={row.conc}
                              onWheel={preventNumberInputScroll}
                              onChange={(e) => handleLibrarySampleSheetChange(idx, 'conc', e.target.value)}
                              className={compactInputToneClasses.text}
                            />
                          </td>
                          <td className="border p-2">
                            <input
                              type="number"
                              value={row.vol}
                              onWheel={preventNumberInputScroll}
                              onChange={(e) => handleLibrarySampleSheetChange(idx, 'vol', e.target.value)}
                              className={compactInputToneClasses.text}
                            />
                          </td>
                          <td className="border p-2">
                            <input
                              type="number"
                              value={row.ngsConc}
                              onWheel={preventNumberInputScroll}
                              onChange={(e) => handleLibrarySampleSheetChange(idx, 'ngsConc', e.target.value)}
                              className={compactInputToneClasses.text}
                            />
                          </td>

                          {/* 🆕 預期定序量 - 加入錯誤檢查 */}
                          <td className="border p-2">
                            <input
                              type="number"
                              value={row.expectedSeq}
                              onWheel={preventNumberInputScroll}
                              onChange={(e) => {
                                handleLibrarySampleSheetChange(idx, 'expectedSeq', e.target.value);
                                // 有錯誤時，使用者修改就清掉這個欄位的錯誤
                                if (fieldErrors.sampleSheet[idx]?.expectedSeq) {
                                  setFieldErrors(prev => {
                                    const newErrors = { ...prev };
                                    if (newErrors.sampleSheet[idx]) {
                                      delete newErrors.sampleSheet[idx].expectedSeq;
                                      if (Object.keys(newErrors.sampleSheet[idx]).length === 0) {
                                        delete newErrors.sampleSheet[idx];
                                      }
                                    }
                                    return newErrors;
                                  });
                                }
                              }}
                              readOnly={!!getAPPackageConfig()?.seqPerSample} // AP 套組時唯讀
                              className={`${compactInputToneClasses.text} ${fieldErrors.sampleSheet[idx]?.expectedSeq
                                ? 'tgia-field-error '
                                : getAPPackageConfig()?.seqPerSample
                                  ? apPackageToneClasses.lockedInput
                                  : ''
                                }`}
                              title={
                                fieldErrors.sampleSheet[idx]?.expectedSeq
                                  ? fieldErrors.sampleSheet[idx].expectedSeq
                                  : getAPPackageConfig()?.seqPerSample
                                    ? '套組產品定序量已鎖定'
                                    : ''
                              }
                            />
                            {fieldErrors.sampleSheet[idx]?.expectedSeq && (
                              <p className="text-xs text-[var(--tgia-color-accent)] mt-1">
                                {fieldErrors.sampleSheet[idx].expectedSeq}
                              </p>
                            )}
                          </td>

                          <td className="border p-2">
                            <input
                              type="text"
                              value={row.note}
                              onChange={(e) => handleLibrarySampleSheetChange(idx, 'note', e.target.value)}
                              className={compactInputToneClasses.text}
                            />
                          </td>
                          <td className="border p-2 text-center">
                            {formData.libraryInfo.sampleSheet.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeLibrarySampleSheetRow(idx)}
                                className="text-[var(--tgia-color-accent)] hover:text-[var(--tgia-color-accent)] max-sm:-m-2 max-sm:p-2"
                                aria-label="刪除這一列"
                              >
                                <X size={16} />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Library Sample Sheet */}
              <div className="mb-6">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <h5 className="font-semibold text-[var(--tgia-color-text)]">Library Sample Sheet</h5>
                  <div className="flex flex-wrap gap-2">

                    <button
                      type="button"
                      onClick={clearLibrarySheet}
                      className={`${buttonSizeClasses.xs} ${buttonToneClasses.clear}`}
                    >
                      <RotateCcw size={14} />
                      清空
                    </button>
                    <button
                      type="button"
                      onClick={addLibraryDetailRow}
                      className={`${buttonSizeClasses.xs} ${buttonToneClasses.add}`}
                    >
                      <Plus size={14} />
                      新增 Library Sample Name
                    </button>
                  </div>
                </div>

                {/* 🆕 雙向同步說明 */}
                <div className="mb-3 rounded-r-xl border-l-4 border-[var(--tgia-color-brand)] bg-[var(--tgia-color-surface-muted)] p-3 text-sm">
                  <p className="mb-1 font-semibold text-[var(--tgia-color-brand-text)]">
                    💡
                  </p>
                  <div className="space-y-1 text-xs text-[var(--tgia-color-brand-text)]">
                    <p>• 選擇 <span className="font-semibold">Library Name</span> 後，系統會自動帶入對應的 <span className="font-semibold">Tube Name</span></p>
                    <p>• Tube Name 由上方 Library 資訊管理，若需修改請回到 Library 資訊調整 Tube Label</p>
                    <p>• 手動填寫、Excel 貼上與 Excel 上傳都會依 Library Name 自動同步</p>
                  </div>
                </div>

                <div className={`mb-2 rounded-lg border p-2 text-xs ${infoToneClasses.caution_secondary}`}>
                  💡 提示：
                  <br />• Idex 填寫方式：NovaSeq 6000 v1.5 (注意 index2 (i5) 為反向序列)
                  <br />• 複製 Excel 資料前，請先確認沒有合併的儲存格
                  <br />• 從 Sample_Name 欄位開始複製（不含序號和標題）
                  <br />• 點擊表格任一儲存格後按 Ctrl+V 貼上，系統會自動新增行數
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="bg-[var(--tgia-color-surface-active)]">
                        <th className="border p-2">序號</th>
                        <th className="border p-2">Sample_Name*</th>
                        <th className="border p-2">Library Prep Kit*</th>
                        <th className="border p-2">Index Adapter Kit</th>
                        <th className="border p-2">Set-Well Position</th>
                        <th className="border p-2">Index 1 (i7)*</th>
                        <th className="border p-2">Index 2 (i5)*</th>
                        <th className="border p-2">備註</th>
                        <th className="border p-2">Library Name</th>
                        <th className="border p-2">
                          Tube Name
                          <span className="text-xs text-[var(--tgia-color-text-subtle)] block mt-1">(由 Library 資訊帶入)</span>
                        </th>
                        <th className="border p-2">操作</th>
                      </tr>
                    </thead>
                    <tbody onPaste={(e) => handleLibraryDetailTablePaste(e, 0)}>
                      {formData.libraryInfo.librarySampleSheet.map((row, idx) => (
                        <tr key={idx} className="bg-[var(--tgia-color-surface)]">
                          {/* 序號 */}
                          <td className="border p-2 text-center">{idx + 1}</td>

                          {/* Sample_Name */}
                          <td className="border p-2">
                            <input
                              type="text"
                              value={row.sampleName}
                              onChange={(e) => {
                                handleLibraryDetailChange(idx, 'sampleName', e.target.value);
                                if (fieldErrors.libraryDetailSheet[idx]?.sampleName) {
                                  setFieldErrors(prev => {
                                    const newErrors = { ...prev };
                                    if (newErrors.libraryDetailSheet[idx]) {
                                      delete newErrors.libraryDetailSheet[idx].sampleName;
                                      if (Object.keys(newErrors.libraryDetailSheet[idx]).length === 0) {
                                        delete newErrors.libraryDetailSheet[idx];
                                      }
                                    }
                                    return newErrors;
                                  });
                                }
                              }}
                              className={`${compactInputToneClasses.text} ${fieldErrors.libraryDetailSheet[idx]?.sampleName ? 'tgia-field-error ' : ''}`}
                              title={fieldErrors.libraryDetailSheet[idx]?.sampleName || ''}
                            />
                            {fieldErrors.libraryDetailSheet[idx]?.sampleName && (
                              <p className="text-xs text-[var(--tgia-color-accent)] mt-1">
                                {fieldErrors.libraryDetailSheet[idx].sampleName}
                              </p>
                            )}
                          </td>

                          {/* Library Prep Kit - 参考按钮 */}
                          <td className="border p-2">
                              <div className="flex gap-1">
                                <input
                                  type="text"
                                  value={row.libraryPrepKit}
                                  onChange={(e) => handleLibraryDetailChange(idx, 'libraryPrepKit', e.target.value)}
                                  className={compactInputToneClasses.text}
                                  placeholder="輸入 Library Prep Kit"
                                />
                                <button
                                  type="button"
                                  onClick={() => openKitModal(idx, 'libraryPrepKit', 'library')}
                                  className={`tgia-kit-reference-button flex-shrink-0 ${buttonSizeClasses.xs}`}
                                  title="選擇常用 Kit"
                                  aria-label="選擇常用 Kit"
                                >
                                  📋
                                </button>
                            </div>
                          </td>

                          {/* Index Adapter Kit - 参考按钮 */}
                          <td className="border p-2">
                            <div className="flex gap-1">
                              <input
                                type="text"
                                value={row.indexAdapterKit}
                                onChange={(e) => handleLibraryDetailChange(idx, 'indexAdapterKit', e.target.value)}
                                className={compactInputToneClasses.text}
                                placeholder="輸入 Index Adapter Kit"
                              />
                              <button
                                type="button"
                                onClick={() => openKitModal(idx, 'indexAdapterKit', 'index')}
                                className={`tgia-kit-reference-button flex-shrink-0 ${buttonSizeClasses.xs}`}
                                title="選擇常用 Index Kit"
                                aria-label="選擇常用 Index Kit"
                              >
                                🧬
                              </button>
                            </div>
                          </td>

                          {/* Set-Well Position */}
                          <td className="border p-2">
                            <input
                              type="text"
                              value={row.setWellPosition}
                              onChange={(e) => handleLibraryDetailChange(idx, 'setWellPosition', e.target.value)}
                              className={compactInputToneClasses.text}
                            />
                          </td>

                          {/* Index 1 (i7) */}
                          <td className="border p-2">
                            <input
                              type="text"
                              value={row.index1Seq}
                              onChange={(e) => {
                                handleLibraryDetailChange(idx, 'index1Seq', e.target.value);
                                if (fieldErrors.libraryDetailSheet[idx]?.index1Seq) {
                                  setFieldErrors(prev => {
                                    const newErrors = { ...prev };
                                    if (newErrors.libraryDetailSheet[idx]) {
                                      delete newErrors.libraryDetailSheet[idx].index1Seq;
                                      if (Object.keys(newErrors.libraryDetailSheet[idx]).length === 0) {
                                        delete newErrors.libraryDetailSheet[idx];
                                      }
                                    }
                                    return newErrors;
                                  });
                                }
                              }}
                              className={`${compactInputToneClasses.text} ${fieldErrors.libraryDetailSheet[idx]?.index1Seq ? 'tgia-field-error ' : ''}`}
                              title={fieldErrors.libraryDetailSheet[idx]?.index1Seq || ''}
                            />
                            {fieldErrors.libraryDetailSheet[idx]?.index1Seq && (
                              <p className="text-xs text-[var(--tgia-color-accent)] mt-1">
                                {fieldErrors.libraryDetailSheet[idx].index1Seq}
                              </p>
                            )}
                          </td>

                          {/* Index 2 (i5) */}
                          <td className="border p-2">
                            <input
                              type="text"
                              value={row.index2Seq}
                              onChange={(e) => {
                                handleLibraryDetailChange(idx, 'index2Seq', e.target.value);
                                if (fieldErrors.libraryDetailSheet[idx]?.index2Seq) {
                                  setFieldErrors(prev => {
                                    const newErrors = { ...prev };
                                    if (newErrors.libraryDetailSheet[idx]) {
                                      delete newErrors.libraryDetailSheet[idx].index2Seq;
                                      if (Object.keys(newErrors.libraryDetailSheet[idx]).length === 0) {
                                        delete newErrors.libraryDetailSheet[idx];
                                      }
                                    }
                                    return newErrors;
                                  });
                                }
                              }}
                              className={`${compactInputToneClasses.text} ${fieldErrors.libraryDetailSheet[idx]?.index2Seq ? 'tgia-field-error ' : ''}`}
                              title={fieldErrors.libraryDetailSheet[idx]?.index2Seq || ''}
                            />
                            {fieldErrors.libraryDetailSheet[idx]?.index2Seq && (
                              <p className="text-xs text-[var(--tgia-color-accent)] mt-1">
                                {fieldErrors.libraryDetailSheet[idx].index2Seq}
                              </p>
                            )}
                          </td>

                          {/* 備註 */}
                          <td className="border p-2">
                            <input
                              type="text"
                              value={row.note}
                              onChange={(e) => handleLibraryDetailChange(idx, 'note', e.target.value)}
                              className={compactInputToneClasses.text}
                            />
                          </td>

                          {/* Library */}
                          <td className="border p-2">
                            <select
                              value={findLibraryReferenceByLibraryName(formData.libraryInfo.sampleSheet, row.library) ? row.library : ''}
                              onChange={(e) => {
                                handleLibraryDetailChange(idx, 'library', e.target.value);
                                if (fieldErrors.libraryDetailSheet[idx]?.library || fieldErrors.libraryDetailSheet[idx]?.tubeName) {
                                  setFieldErrors(prev => {
                                    const newErrors = { ...prev };
                                    if (newErrors.libraryDetailSheet[idx]) {
                                      delete newErrors.libraryDetailSheet[idx].library;
                                      delete newErrors.libraryDetailSheet[idx].tubeName;
                                      if (Object.keys(newErrors.libraryDetailSheet[idx]).length === 0) {
                                        delete newErrors.libraryDetailSheet[idx];
                                      }
                                    }
                                    return newErrors;
                                  });
                                }
                              }}
                              onPaste={(e) => handleLibraryDetailTablePaste(e, idx)}
                              className={`${compactInputToneClasses.select} bg-[var(--tgia-color-surface-muted)] ${fieldErrors.libraryDetailSheet[idx]?.library ? 'tgia-field-error ' : ''}`}
                              title={fieldErrors.libraryDetailSheet[idx]?.library || ''}
                            >
                              <option value="">選擇 Library Name</option>
                              {formData.libraryInfo.sampleSheet.map((sample, sIdx) => (
                                sample.sampleName && (
                                  <option key={sIdx} value={sample.sampleName}>{sample.sampleName}</option>
                                )
                              ))}
                            </select>
                            {fieldErrors.libraryDetailSheet[idx]?.library && (
                              <p className="text-xs text-[var(--tgia-color-accent)] mt-1">
                                {fieldErrors.libraryDetailSheet[idx].library}
                              </p>
                            )}
                          </td>

                          {/* Tube Name - 由 Library 資訊自動帶入 */}
                          <td className="border p-2">
                            <input
                              type="text"
                              value={row.tubeName || ''}
                              readOnly
                              onPaste={(e) => handleLibraryDetailTablePaste(e, idx)}
                              className={`${compactInputToneClasses.text} cursor-not-allowed bg-[var(--tgia-color-surface-muted)] text-[var(--tgia-color-text-muted)] ${fieldErrors.libraryDetailSheet[idx]?.tubeName ? 'tgia-field-error ' : ''}`}
                              title={fieldErrors.libraryDetailSheet[idx]?.tubeName || 'Tube Name 由 Library 資訊自動帶入；若需修改，請調整上方 Library 資訊的 Tube Label'}
                            />
                            {fieldErrors.libraryDetailSheet[idx]?.tubeName && (
                              <p className="text-xs text-[var(--tgia-color-accent)] mt-1">
                                {fieldErrors.libraryDetailSheet[idx].tubeName}
                              </p>
                            )}
                          </td>

                          {/* 操作 */}
                          <td className="border p-2 text-center">
                            {formData.libraryInfo.librarySampleSheet.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeLibraryDetailRow(idx)}
                                className="text-[var(--tgia-color-accent)] hover:text-[var(--tgia-color-accent)] max-sm:-m-2 max-sm:p-2"
                                aria-label="刪除這一列"
                              >
                                <X size={16} />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 電泳膠圖 */}
              {/* <div>
              <h5 className="font-semibold text-[var(--tgia-color-text)] mb-3">電泳膠圖</h5>
              <textarea
                value={formData.libraryInfo.gelImage}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  libraryInfo: { ...prev.libraryInfo, gelImage: e.target.value }
                }))}
                rows={3}
                className="w-full px-3 py-2 border border-[var(--tgia-color-border)] rounded-md focus:ring-2 focus:ring-[var(--tgia-color-focus)] bg-[var(--tgia-color-surface)]"
                placeholder="請描述電泳膠圖資訊或上傳圖片連結"
              />
            </div> */}
            </div>
          )}

          {/* 🆕 scRNA-seq 實驗資訊：放在 Sample Sheet 上方，因為實驗類型決定 Sample Sheet 的欄位與填寫方式 */}
          {(() => {
            const hasScRNAseq = formData.serviceItems.some(item =>
              item.services.some(svc => isScRNAseqService(svc.service))
            );

            if (!hasScRNAseq) return null;

            const analysisServiceValue = analysisFlowInfo?.serviceValue || '';
            const scRNAExperimentOptions = getScRNAExperimentOptions(
              analysisFlowInfo?.platformLabel,
              apPackageInfo,
              analysisServiceValue
            );
            // 只認這個品項選單裡、而且可以選的項目；不支援的實驗類型會由上面的 effect 清掉，
            // 在那之前也不能拿它的說明來顯示。
            const selectedScRNAExperiment = scRNAExperimentOptions.find(item => (
              item.ServiceName === formData.scRNAExperimentInfo?.serviceName && !item.disabled
            )) || null;
            const scRNAExperimentValue = selectedScRNAExperiment?.ServiceName || '';
            const experimentNotice = getScRnaExperimentNotice(scRNAExperimentValue);

            return (
              <div className="mt-4 mb-4 rounded-lg border-2 border-[var(--tgia-color-panel-border)] bg-[var(--tgia-color-surface)] p-4 text-[var(--tgia-color-text)]">
                <h4 className="mb-3 flex items-center gap-2 text-sm font-bold text-[var(--tgia-color-text)]">
                  🧬 scRNA-seq 實驗資訊
                </h4>
                <div className="space-y-4">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-[var(--tgia-color-text-muted)]">
                      實驗類型 <span className="text-[var(--tgia-color-accent)]">*</span>
                    </label>
                    <select
                      value={scRNAExperimentValue}
                      onChange={(e) => {
                        const selectedOption = scRNAExperimentOptions.find(
                          item => item.ServiceName === e.target.value
                        );

                        setFormData(prev => normalizeSpeciesFields({
                          ...prev,
                          scRNAExperimentInfo: {
                            serviceName: selectedOption?.ServiceName || '',
                            description: selectedOption?.description || ''
                          }
                        }));
                      }}
                      className={inputToneClasses.select}
                    >
                      <option value="">-- 請選擇 --</option>
                      {scRNAExperimentOptions.map((item) => (
                        <option key={item.ServiceName} value={item.ServiceName} disabled={item.disabled}>
                          {item.disabled ? `${item.ServiceName}（此分析流程不支援）` : item.ServiceName}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-[var(--tgia-color-text-subtle)]">
                      💡 實驗類型會決定下方 Sample Sheet 需要填寫的欄位，請先選擇實驗類型再填寫樣本。
                    </p>
                  </div>

                  {selectedScRNAExperiment && (
                    <div className="rounded-lg border border-[var(--tgia-color-border-subtle)] bg-[var(--tgia-color-surface-subtle)] p-4">
                      <p className="mb-1 text-xs font-medium text-[var(--tgia-color-text-muted)]">description</p>
                      <p className="text-sm text-[var(--tgia-color-text)] whitespace-pre-line">
                        {selectedScRNAExperiment.description || '無說明'}
                      </p>
                    </div>
                  )}

                  {experimentNotice && (
                    <div className={`rounded-lg border p-3 text-sm ${infoToneClasses.caution_secondary}`}>
                      💡 {experimentNotice}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Analysis 送件資訊 Lite */}
          {showAnalysisSampleSheetLite && (
            <div className="sm:col-span-2 mt-4 rounded-2xl border border-[var(--tgia-color-brand-border)] bg-[var(--tgia-color-surface-soft)] p-4 sm:p-6">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                <h4 className="text-lg font-bold text-[var(--tgia-color-text)]">Sample Sheet</h4>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={clearSampleInfoSheet}
                    className={`${buttonSizeClasses.xs} ${buttonToneClasses.clear}`}
                  >
                    <RotateCcw size={14} />
                    清空
                  </button>
                  <button
                    type="button"
                    onClick={addSampleSheetRow}
                    className={`${buttonSizeClasses.xs} ${buttonToneClasses.add}`}
                    title={getAddRowTitle('樣本')}
                  >
                    <Plus size={14} />
                    新增樣本
                  </button>
                </div>
              </div>

              {isChIPseq && (
                <div className={`mb-4 rounded-lg border p-3 text-xs ${infoToneClasses.caution_secondary}`}>
                  <p className="mb-1 font-semibold">💡 ChIP-seq 填寫提示：</p>
                  <ul className="ml-4 list-disc space-y-1">
                    {CHIPSEQ_SAMPLE_SHEET_HINTS.map((hint, hintIdx) => (
                      <li key={hintIdx}>{hint}</li>
                    ))}
                  </ul>
                </div>
              )}

              {scRnaSampleSheetRule.hints.length > 0 && (
                <div className={`mb-4 rounded-lg border p-3 text-xs ${infoToneClasses.caution_secondary}`}>
                  <p className="mb-1 font-semibold">
                    💡 {formData.scRNAExperimentInfo?.serviceName} 填寫提示：
                  </p>
                  <ul className="ml-4 list-disc space-y-1">
                    {scRnaSampleSheetRule.hints.map((hint, hintIdx) => (
                      <li key={hintIdx}>{hint}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-[var(--tgia-color-surface-active)]">
                      <th className="border p-2">序號</th>
                      {isChIPseq && <th className="border p-2">類型</th>}
                      <th className="border p-2">{isChIPseq ? 'Sample_Name' : 'Sample_Name*'}</th>
                      {scRnaSampleSheetRule.requireSampleBarcode && (
                        <th className="border p-2">Sample_Barcode*</th>
                      )}
                      {scRnaSampleSheetRule.showFastqName && (
                        <th className="border p-2">
                          {scRnaSampleSheetRule.fastqNameLabel}
                          {scRnaSampleSheetRule.requireFastqName ? '*' : ''}
                        </th>
                      )}
                      {scRnaSampleSheetRule.requireVdjLibrary && (
                        <>
                          <th className="border p-2">V(D)J 類型*</th>
                          {VDJ_LIBRARY_FASTQ_FIELDS.map(({ field, label }) => (
                            <th key={field} className="border p-2">{label}</th>
                          ))}
                        </>
                      )}
                      {isChIPseq && <th className="border p-2">樣本來源（選填）</th>}
                      <th className="border p-2">備註</th>
                      <th className="border p-2">操作</th>
                    </tr>
                  </thead>
                  <tbody onPaste={(e) => handleSampleTablePaste(e, 0)}>
                    {formData.sampleInfo.sampleSheet.map((row, idx) => (
                      <tr key={idx} className="bg-[var(--tgia-color-surface)]">
                        <td className="border p-2 text-center">{idx + 1}</td>
                        {isChIPseq && (
                          <td className="border p-2 whitespace-nowrap text-center text-xs text-[var(--tgia-color-text-muted)]">
                            樣本 {getChIPseqSampleGroupNo(idx)} ·{' '}
                            {CHIPSEQ_ROW_ROLE_LABELS[getChIPseqRowRole(idx)]}
                            {!isChIPseqSampleNameRequired(idx) && '（選填）'}
                          </td>
                        )}
                        <td className="border p-2">
                          <input
                            type="text"
                            value={row.sampleName}
                            onChange={(e) => {
                              handleSampleSheetChange(idx, 'sampleName', e.target.value);
                              if (fieldErrors.sampleSheet[idx]?.sampleName) {
                                setFieldErrors(prev => {
                                  const newErrors = { ...prev };
                                  if (newErrors.sampleSheet[idx]) {
                                    delete newErrors.sampleSheet[idx].sampleName;
                                    if (Object.keys(newErrors.sampleSheet[idx]).length === 0) {
                                      delete newErrors.sampleSheet[idx];
                                    }
                                  }
                                  return newErrors;
                                });
                              }
                            }}
                            className={`${compactInputToneClasses.text} ${fieldErrors.sampleSheet[idx]?.sampleName ? 'tgia-field-error ' : ''}`}
                            title={fieldErrors.sampleSheet[idx]?.sampleName || ''}
                          />
                          {fieldErrors.sampleSheet[idx]?.sampleName && (
                            <p className="mt-1 text-xs text-[var(--tgia-color-accent)]">
                              {fieldErrors.sampleSheet[idx].sampleName}
                            </p>
                          )}
                        </td>
                        {scRnaSampleSheetRule.requireSampleBarcode && (
                          <td className="border p-2">
                            <input
                              type="text"
                              value={row.sampleBarcode || ''}
                              onChange={(e) => {
                                handleSampleSheetChange(idx, 'sampleBarcode', e.target.value);
                                if (fieldErrors.sampleSheet[idx]?.sampleBarcode) {
                                  setFieldErrors(prev => {
                                    const newErrors = { ...prev };
                                    if (newErrors.sampleSheet[idx]) {
                                      delete newErrors.sampleSheet[idx].sampleBarcode;
                                      if (Object.keys(newErrors.sampleSheet[idx]).length === 0) {
                                        delete newErrors.sampleSheet[idx];
                                      }
                                    }
                                    return newErrors;
                                  });
                                }
                              }}
                              className={`${compactInputToneClasses.text} ${fieldErrors.sampleSheet[idx]?.sampleBarcode ? 'tgia-field-error ' : ''}`}
                              title={fieldErrors.sampleSheet[idx]?.sampleBarcode || '多個 barcode 請以 | 分隔，例如 BC001|BC002'}
                              placeholder="BC001|BC002"
                            />
                            {fieldErrors.sampleSheet[idx]?.sampleBarcode && (
                              <p className="mt-1 text-xs text-[var(--tgia-color-accent)]">
                                {fieldErrors.sampleSheet[idx].sampleBarcode}
                              </p>
                            )}
                          </td>
                        )}
                        {scRnaSampleSheetRule.showFastqName && (
                          <td className="border p-2">
                            <input
                              type="text"
                              value={row.fastqName || ''}
                              onChange={(e) => {
                                handleSampleSheetChange(idx, 'fastqName', e.target.value);
                                clearSampleSheetFieldError(idx, 'fastqName');
                              }}
                              className={`${compactInputToneClasses.text} ${fieldErrors.sampleSheet[idx]?.fastqName ? 'tgia-field-error ' : ''}`}
                              title={fieldErrors.sampleSheet[idx]?.fastqName || scRnaSampleSheetRule.fastqNameHint}
                              placeholder={scRnaSampleSheetRule.fastqNamePlaceholder}
                            />
                            {fieldErrors.sampleSheet[idx]?.fastqName && (
                              <p className="mt-1 text-xs text-[var(--tgia-color-accent)]">
                                {fieldErrors.sampleSheet[idx].fastqName}
                              </p>
                            )}
                          </td>
                        )}
                        {scRnaSampleSheetRule.requireVdjLibrary && (
                          <>
                            <td className="border p-2">
                              <select
                                value={normalizeVdjLibraryType(row.vdjType)}
                                onChange={(e) => handleVdjTypeChange(idx, e.target.value, scRnaSampleSheetRule)}
                                className={`${compactInputToneClasses.select} ${fieldErrors.sampleSheet[idx]?.vdjType ? 'tgia-field-error ' : ''}`}
                                title={fieldErrors.sampleSheet[idx]?.vdjType || '這個 5\' GEX 樣本搭配的 V(D)J enrichment'}
                              >
                                <option value="">請選擇</option>
                                {VDJ_LIBRARY_TYPE_OPTIONS.map(option => (
                                  <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                              </select>
                              {fieldErrors.sampleSheet[idx]?.vdjType && (
                                <p className="mt-1 text-xs text-[var(--tgia-color-accent)]">
                                  {fieldErrors.sampleSheet[idx].vdjType}
                                </p>
                              )}
                            </td>
                            {VDJ_LIBRARY_FASTQ_FIELDS.map(({ library, field, label }) => {
                              const libraryApplies = vdjLibraryTypeIncludes(row.vdjType, library);

                              return (
                                <td key={field} className="border p-2">
                                  <input
                                    type="text"
                                    value={libraryApplies ? (row[field] || '') : ''}
                                    disabled={!libraryApplies}
                                    onChange={(e) => {
                                      handleSampleSheetChange(idx, field, e.target.value);
                                      clearSampleSheetFieldError(idx, field);
                                    }}
                                    className={`${compactInputToneClasses.text} ${fieldErrors.sampleSheet[idx]?.[field] ? 'tgia-field-error ' : ''} ${libraryApplies ? '' : 'opacity-50'}`}
                                    title={fieldErrors.sampleSheet[idx]?.[field]
                                      || (libraryApplies
                                        ? `${library} library 的 fastq 檔名前綴，例如 apple_${library}_R1_001.fastq.gz 只需填 apple_${library}`
                                        : (normalizeVdjLibraryType(row.vdjType) === 'NONE'
                                          ? "此樣本只做 5' GEX，沒有 V(D)J library"
                                          : `此樣本的 V(D)J 類型未包含 ${library}`))}
                                    placeholder={libraryApplies ? `apple_${library}` : '—'}
                                    aria-label={label}
                                  />
                                  {fieldErrors.sampleSheet[idx]?.[field] && (
                                    <p className="mt-1 text-xs text-[var(--tgia-color-accent)]">
                                      {fieldErrors.sampleSheet[idx][field]}
                                    </p>
                                  )}
                                </td>
                              );
                            })}
                          </>
                        )}
                        {isChIPseq && (() => {
                          // 這列沒有 Sample_Name 就沒有樣本，來源欄灰掉並清空（同 V(D)J 欄位的做法）。
                          const hasSample = Boolean(String(row.sampleName || '').trim());

                          return (
                            <td className="border p-2">
                              <input
                                type="text"
                                value={hasSample ? (row.sampleSource || '') : ''}
                                disabled={!hasSample}
                                maxLength={CHIPSEQ_SAMPLE_SOURCE_MAX_LENGTH}
                                onChange={(e) => handleSampleSheetChange(idx, 'sampleSource', e.target.value)}
                                className={`${compactInputToneClasses.text} ${hasSample ? '' : 'opacity-50'}`}
                                title={hasSample
                                  ? '沿用以前 PO 的樣本才要填，填那張 PO 的單號；這次新送的樣本留空'
                                  : '這列沒有樣本，不用填來源'}
                                placeholder={hasSample ? '以前的 PO 單號' : '—'}
                                aria-label="樣本來源"
                              />
                            </td>
                          );
                        })()}
                        <td className="border p-2">
                          <input
                            type="text"
                            value={row.note}
                            onChange={(e) => handleSampleSheetChange(idx, 'note', e.target.value)}
                            className={compactInputToneClasses.text}
                          />
                        </td>
                        <td className="border p-2 text-center">
                          {formData.sampleInfo.sampleSheet.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeSampleSheetRow(idx)}
                              className="text-[var(--tgia-color-accent)] hover:text-[var(--tgia-color-accent)] max-sm:-m-2 max-sm:p-2"
                              aria-label="刪除這一列"
                            >
                              <X size={16} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Sample 送件資訊 */}
          {!isAnalysisOnly && !isGeneralNoSampleOrder && formData.sampleType !== 'Library' && (
            <div className="sm:col-span-2 mt-4 border-2 border-[var(--tgia-color-success-border)] rounded-lg p-4 sm:p-6 bg-[var(--tgia-color-success-surface)]">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                <h4 className="text-lg font-bold text-[var(--tgia-color-text)]">
                  {formData.sampleType} 送件資訊
                </h4>

                <div className="flex flex-wrap justify-end gap-3 mt-2">

                  <button
                    type="button"
                    onClick={downloadSampleTemplate}
                    className={`${buttonSizeClasses.sm} ${buttonToneClasses.excel}`}
                  >
                    <Download size={16} />
                    下載範本
                  </button>
                  <button
                    type="button"
                    onClick={() => excelUploadRef.current?.click()}
                    className={`${buttonSizeClasses.sm} ${buttonToneClasses.upload}`}
                  >
                    <Upload size={16} />
                    上傳 Excel
                  </button>
                </div>
              </div>

              <input
                ref={excelUploadRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleExcelUpload}
                className="hidden"
              />
              {/* 🆕 拖拉上傳區域 */}
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => excelUploadRef.current?.click()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    excelUploadRef.current?.click();
                  }
                }}
                role="button"
                tabIndex={0}
                className={`mb-4 cursor-pointer p-4 sm:p-6 border-2 border-dashed rounded-lg transition-all ${isDragging
                  ? 'border-[var(--tgia-color-success-text)] bg-[var(--tgia-color-success-surface)] scale-105'
                  : 'border-[var(--tgia-color-success-border)] bg-[var(--tgia-color-surface)] hover:border-[var(--tgia-color-success-border)] hover:bg-[var(--tgia-color-success-surface)]'
                  }`}
              >
                <div className="text-center">
                  <Upload size={48} className={`mx-auto mb-2 ${isDragging ? 'text-[var(--tgia-color-success-text)]' : 'text-[var(--tgia-color-success-border)]'}`} />
                  <p className="text-sm font-medium text-[var(--tgia-color-text)] mb-1">
                    {isDragging ? '放開以上傳檔案' : '拖曳 Excel 檔案到這裡'}
                  </p>
                  <p className="text-xs text-[var(--tgia-color-text-subtle)] mb-2">
                    或使用下方按鈕選擇檔案
                  </p>
                  <div className="flex gap-2 justify-center">
                  </div>
                  <p className="text-xs text-[var(--tgia-color-placeholder)] mt-2">
                    支援格式：.xlsx, .xls
                  </p>
                </div>
              </div>
                <div className={`mb-4 rounded-lg border p-2 text-xs ${infoToneClasses.caution_secondary}`}>
                  💡 提示：
                  {/* <br />• Sample_Name 勿用數字開頭，不能空格，僅允許"-"、"_"符號 */}
                  <br />• 測定方法請選擇 Qubit 或 Nanodrop
                  <br />• 點擊表格任一儲存格後按 Ctrl+V 貼上，系統會自動新增行數
                  <br />• {getRowCountRuleHint('樣本')}
                </div>


              {/* 濃度測定方式 */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-[var(--tgia-color-text)] mb-2">
                  濃度測定方式 <span className="text-[var(--tgia-color-accent)]"></span>
                </label>
                <div className="flex flex-wrap gap-3">
                  <label className="flex items-center gap-2 px-4 py-2 border rounded-lg cursor-pointer transition hover:bg-[var(--tgia-color-success-surface)]">
                    <input
                      type="radio"
                      name="sampleConcMethod"
                      value="Qubit"
                      checked={formData.sampleInfo.concMethod === 'Qubit'}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        sampleInfo: { ...prev.sampleInfo, concMethod: e.target.value }
                      }))}
                      className="w-4 h-4 text-[var(--tgia-color-success-text)]"
                    />
                    <span className="text-sm font-medium">Qubit</span>
                  </label>

                  <label className="flex items-center gap-2 px-4 py-2 border rounded-lg cursor-pointer transition hover:bg-[var(--tgia-color-success-surface)]">
                    <input
                      type="radio"
                      name="sampleConcMethod"
                      value="Nanodrop"
                      checked={formData.sampleInfo.concMethod === 'Nanodrop'}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        sampleInfo: { ...prev.sampleInfo, concMethod: e.target.value }
                      }))}
                      className="w-4 h-4 text-[var(--tgia-color-success-text)]"
                    />
                    <span className="text-sm font-medium">Nanodrop</span>
                  </label>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {/* <button
                type="button"
                onClick={downloadSampleTemplate}
                className="text-xs text-[var(--tgia-color-success-text)] hover:text-[var(--tgia-color-success-text)] flex items-center gap-1 px-2 py-1 rounded hover:bg-[var(--tgia-color-success-surface)] border border-[var(--tgia-color-success-border)]"
              >
                <Download size={14} />
                下載範本
              </button> */}
                {/* <button
                type="button"
                onClick={() => excelUploadRef.current?.click()}
                className="text-xs text-[var(--tgia-color-upload)] hover:text-[var(--tgia-color-upload)] flex items-center gap-1 px-2 py-1 rounded hover:bg-[var(--tgia-color-brand-surface)] border border-[var(--tgia-color-upload)]"
              >
                <Upload size={14} />
                上傳 Excel
              </button> */}
                <button
                  type="button"
                  onClick={clearSampleInfoSheet}
                  className={`${buttonSizeClasses.xs} ${buttonToneClasses.clear}`}
                >
                  <RotateCcw size={14} />
                  清空
                </button>
                <button
                  type="button"
                  onClick={addSampleSheetRow}
                  className={`${buttonSizeClasses.xs} ${buttonToneClasses.add}`}
                  title={getAddRowTitle('樣本')}
                >
                  <Plus size={14} />
                  新增樣本
                </button>
                <button
                  type="button"
                  onClick={handleDistributeSequencing}
                  disabled={!!getAPPackageConfig()?.seqPerSample}
                  className={`${buttonSizeClasses.xs} ${buttonToneClasses.other}`}
                  title={getAPPackageConfig()?.seqPerSample ? '套組產品定序量已鎖定' : '將總定序量平均分配給所有樣本'}
                >
                  <span className="text-sm">⚖️</span>
                  均分定序量
                </button>
              </div>
              <br />

              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-[var(--tgia-color-surface-active)]">
                      <th className="border p-2">序號</th>
                      <th className="border p-2">Sample_Name*</th>
                      <th className="border p-2">Tube Label*</th>
                      <th className="border p-2">預期定序量</th>
                      <th className="border p-2">Conc* (ng/ul)</th>
                      <th className="border p-2">Vol* (uL)</th>
                      <th className="border p-2">260/280</th>
                      <th className="border p-2">260/230</th>
                      <th className="border p-2">DQN/RQN</th>
                      <th className="border p-2">備註</th>
                      <th className="border p-2">操作</th>
                    </tr>
                  </thead>
                  <tbody onPaste={(e) => handleSampleTablePaste(e, 0)}>
                    {formData.sampleInfo.sampleSheet.map((row, idx) => (
                      <tr key={idx} className="bg-[var(--tgia-color-surface)]">
                        <td className="border p-2 text-center">{idx + 1}</td>

                        {/* Sample_Name */}
                        <td className="border p-2">
                          <input
                            type="text"
                            value={row.sampleName}
                            onChange={(e) => {
                              handleSampleSheetChange(idx, 'sampleName', e.target.value);
                              if (fieldErrors.sampleSheet[idx]?.sampleName) {
                                setFieldErrors(prev => {
                                  const newErrors = { ...prev };
                                  if (newErrors.sampleSheet[idx]) {
                                    delete newErrors.sampleSheet[idx].sampleName;
                                    if (Object.keys(newErrors.sampleSheet[idx]).length === 0) {
                                      delete newErrors.sampleSheet[idx];
                                    }
                                  }
                                  return newErrors;
                                });
                              }
                            }}
                            className={`${compactInputToneClasses.text} ${fieldErrors.sampleSheet[idx]?.sampleName ? 'tgia-field-error ' : ''}`}
                            placeholder=""
                            title={fieldErrors.sampleSheet[idx]?.sampleName || ''}
                          />
                          {fieldErrors.sampleSheet[idx]?.sampleName && (
                            <p className="text-xs text-[var(--tgia-color-accent)] mt-1">
                              {fieldErrors.sampleSheet[idx].sampleName}
                            </p>
                          )}
                        </td>

                        {/* Tube Label */}
                        <td className="border p-2">
                          <input
                            type="text"
                            value={row.tubeLabel}
                            onChange={(e) => {
                              handleSampleSheetChange(idx, 'tubeLabel', e.target.value);
                              if (fieldErrors.sampleSheet[idx]?.tubeLabel) {
                                setFieldErrors(prev => {
                                  const newErrors = { ...prev };
                                  if (newErrors.sampleSheet[idx]) {
                                    delete newErrors.sampleSheet[idx].tubeLabel;
                                    if (Object.keys(newErrors.sampleSheet[idx]).length === 0) {
                                      delete newErrors.sampleSheet[idx];
                                    }
                                  }
                                  return newErrors;
                                });
                              }
                            }}
                            className={`${compactInputToneClasses.text} ${fieldErrors.sampleSheet[idx]?.tubeLabel ? 'tgia-field-error ' : ''}`}
                            title={fieldErrors.sampleSheet[idx]?.tubeLabel || ''}
                          />
                          {fieldErrors.sampleSheet[idx]?.tubeLabel && (
                            <p className="text-xs text-[var(--tgia-color-accent)] mt-1">
                              {fieldErrors.sampleSheet[idx].tubeLabel}
                            </p>
                          )}
                        </td>
                        <td className="border p-2">
                          <input
                            type="number"
                            value={row.expectedSeq}
                            onWheel={preventNumberInputScroll}
                            onChange={(e) => {
                              handleSampleSheetChange(idx, 'expectedSeq', e.target.value);

                              // 🆕 有錯誤時，使用者一輸入就清掉這欄的錯誤
                              if (fieldErrors.sampleSheet[idx]?.expectedSeq) {
                                setFieldErrors(prev => {
                                  const newErrors = { ...prev };
                                  if (newErrors.sampleSheet[idx]) {
                                    delete newErrors.sampleSheet[idx].expectedSeq;
                                    if (Object.keys(newErrors.sampleSheet[idx]).length === 0) {
                                      delete newErrors.sampleSheet[idx];
                                    }
                                  }
                                  return newErrors;
                                });
                              }
                            }}
                            readOnly={!!getAPPackageConfig()?.seqPerSample} // AP 套組時唯讀
                            className={`${compactInputToneClasses.text} ${fieldErrors.sampleSheet[idx]?.expectedSeq
                              ? 'tgia-field-error '
                              : getAPPackageConfig()?.seqPerSample
                                ? apPackageToneClasses.lockedInput
                                : ''
                              }`}
                            title={
                              fieldErrors.sampleSheet[idx]?.expectedSeq
                                ? fieldErrors.sampleSheet[idx].expectedSeq
                                : getAPPackageConfig()?.seqPerSample
                                  ? '套組產品定序量已鎖定'
                                  : ''
                            }
                          />
                          {fieldErrors.sampleSheet[idx]?.expectedSeq && (
                            <p className="text-xs text-[var(--tgia-color-accent)] mt-1">
                              {fieldErrors.sampleSheet[idx].expectedSeq}
                            </p>
                          )}
                        </td>

                        <td className="border p-2">
                          <input
                            type="number"
                            value={row.conc}
                            onWheel={preventNumberInputScroll}
                            onChange={(e) => handleSampleSheetChange(idx, 'conc', e.target.value)}
                            className={compactInputToneClasses.text}
                          />
                        </td>
                        <td className="border p-2">
                          <input
                            type="number"
                            value={row.vol}
                            onWheel={preventNumberInputScroll}
                            onChange={(e) => handleSampleSheetChange(idx, 'vol', e.target.value)}
                            className={compactInputToneClasses.text}
                          />
                        </td>
                        <td className="border p-2">
                          <input
                            type="number"
                            step="0.01"
                            value={row.ratio260280}
                            onWheel={preventNumberInputScroll}
                            onChange={(e) => handleSampleSheetChange(idx, 'ratio260280', e.target.value)}
                            className={compactInputToneClasses.text}
                          />
                        </td>
                        <td className="border p-2">
                          <input
                            type="number"
                            step="0.01"
                            value={row.ratio260230}
                            onWheel={preventNumberInputScroll}
                            onChange={(e) => handleSampleSheetChange(idx, 'ratio260230', e.target.value)}
                            className={compactInputToneClasses.text}
                          />
                        </td>
                        <td className="border p-2">
                          <input
                            type="number"
                            step="0.1"
                            value={row.dqnRqn}
                            onWheel={preventNumberInputScroll}
                            onChange={(e) => handleSampleSheetChange(idx, 'dqnRqn', e.target.value)}
                            className={compactInputToneClasses.text}
                          />
                        </td>
                        <td className="border p-2">
                          <input
                            type="text"
                            value={row.note}
                            onChange={(e) => handleSampleSheetChange(idx, 'note', e.target.value)}
                            className={compactInputToneClasses.text}
                          />
                        </td>
                        <td className="border p-2 text-center">
                          {formData.sampleInfo.sampleSheet.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeSampleSheetRow(idx)}
                              className="text-[var(--tgia-color-accent)] hover:text-[var(--tgia-color-accent)] max-sm:-m-2 max-sm:p-2"
                              aria-label="刪除這一列"
                            >
                              <X size={16} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* 保存方式、樣品數量等 */}
          {/* 保存方式、樣品數量等 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            {/* 保存方式 */}
            <div>
                <label className="block text-sm font-medium text-[var(--tgia-color-text)] mb-1">
                保存方式 <span className="text-[var(--tgia-color-accent)]">*</span>
                {apPackageInfo?.binding?.preservationMethod && typeof apPackageInfo.binding.preservationMethod === 'object' && (
                  <span className={`text-xs ml-2 ${apPackageToneClasses.accent}`}>📌 套組限制選項</span>
                )}
              </label>
              <select
                name="preservationMethod"
                value={resolvedSampleFormData.preservationMethod}
                onChange={handleInputChange}
                disabled={isGeneralNoSampleOrder}
                className={inputToneClasses.select}
              >
                {(() => {
                  // 🆕 判斷是否有限制選項
                  const hasRestriction = apPackageInfo?.binding?.preservationMethod &&
                    typeof apPackageInfo.binding.preservationMethod === 'object';

                  // 🆕 所有可能的選項
                  const allOptions = [
                    'Nuclease-free H2O',
                    'Tris Buffer',
                    'Trizol',
                    'EDTA Tube(Blood)',
                    'Tempus Tube(Blood)',
                    '其他'
                  ];

                  // 🆕 決定要顯示的選項
                  const optionsToShow = hasRestriction
                    ? apPackageInfo.binding.preservationMethod.allowed
                    : allOptions;

                  return optionsToShow.map(option => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ));
                })()}
              </select>
              {apPackageInfo?.binding?.preservationMethod && typeof apPackageInfo.binding.preservationMethod === 'object' && (
                <p className={`text-xs mt-1 ${apPackageToneClasses.accent}`}>
                  💡 此套組限制選項：{apPackageInfo.binding.preservationMethod.allowed.join('、')}
                </p>
              )}
              {resolvedSampleFormData.preservationMethod === '其他' && (
                <input
                  type="text"
                  name="preservationMethodOther"
                  value={resolvedSampleFormData.preservationMethodOther}
                  onChange={handleInputChange}
                  className={`${compactInputToneClasses.text} mt-2`}
                  placeholder="請說明保存方式"
                />
              )}
            </div>

            {/* 樣品數量 */}
            <div>
              <label className="block text-sm font-medium text-[var(--tgia-color-text)] mb-1">
                樣品數量 <span className="text-[var(--tgia-color-accent)]">*</span>
              </label>
              <input
                type="number"
                name="sampleCount"
                value={resolvedSampleFormData.sampleCount}
                readOnly
                onWheel={preventNumberInputScroll}
                className={`${inputToneClasses.text} cursor-not-allowed bg-[var(--tgia-color-surface-muted)]`}
                min="1"
              />
              <p className="text-xs text-[var(--tgia-color-text-subtle)] mt-1">
                💡 {isChIPseq
                  ? '由系統自動計算（依據步驟三填寫的樣本數，與 Sample Sheet 列數無關）'
                  : '由系統自動計算（依據 Sample Sheet）'}
              </p>
            </div>

            {/* 物種 */}
            <div>
              <label className="block text-sm font-medium text-[var(--tgia-color-text)] mb-1">
                物種
                {apPackageInfo?.binding?.speciesCommon && (
                  <span className={`text-xs ml-2 ${apPackageToneClasses.accent}`}>🔒 套組鎖定</span>
                )}
              </label>
              {(() => {
                let detailedSpeciesList = getSpeciesOptionsForMode(speciesMode, formData.serviceItems, formData);
                const lockedSpeciesCommon = apPackageInfo?.binding?.speciesCommon || '';
                const isLockedScrnaSpecies = speciesMode === 'scrna' && Boolean(lockedSpeciesCommon);
                const allowOtherSpecies = canSelectOtherSpecies(speciesMode, formData.serviceItems, formData);

                if (isLockedScrnaSpecies) {
                  detailedSpeciesList = detailedSpeciesList.filter(s => s.commonName === lockedSpeciesCommon);
                }

                if (isDetailedSpeciesMode(speciesMode)) {
                  // 🆕 情況 A: 特殊分析服務，顯示簡潔下拉選單 + 詳細資訊表格
                  const selectedSpeciesObj = findSpeciesOptionBySelection(
                    formData.speciesSelectionKey || lockedSpeciesCommon || formData.speciesSelection,
                    detailedSpeciesList
                  ) || (isLockedScrnaSpecies ? detailedSpeciesList[0] : null);
                  const speciesValue = isLockedScrnaSpecies
                    ? (formData.speciesSelectionKey || getSpeciesSelectionKey(selectedSpeciesObj) || lockedSpeciesCommon)
                    : (formData.speciesSelectionKey || formData.speciesSelection);

                  return (
                    <>
                      <select
                        name="speciesSelection"
                        value={speciesValue}
                        onChange={handleInputChange}
                        disabled={isLockedScrnaSpecies}
                        className={`tgia-species-select ${inputToneClasses.select} mb-2`}
                      >
                        <option value="物種請選擇">物種請選擇</option>
                        {detailedSpeciesList.map((s, idx) => (
                          <option key={`${s.commonName}-${s.scientificName}-${s.genome}-${idx}`} value={getSpeciesSelectionKey(s)}>
                            {getSpeciesOptionLabel(s)}
                          </option>
                        ))}
                        {!isLockedScrnaSpecies && allowOtherSpecies && <option value="其他">其他</option>}
                      </select>

                      {/* 🆕 顯示詳細資訊表格 */}
                      {selectedSpeciesObj && (
                        <div className="mb-2 overflow-hidden rounded-lg border border-[var(--tgia-color-brand-border)] bg-[var(--tgia-color-surface-soft)] shadow-sm">
                          <table className="min-w-full text-sm divide-y divide-[var(--tgia-color-brand-border)]">
                            <tbody className="divide-y divide-[var(--tgia-color-border-subtle)]">
                              <tr>
                                <td className="w-1/3 bg-[var(--tgia-color-surface-active)] px-3 py-2 font-semibold text-[var(--tgia-color-brand-text)]">俗名</td>
                                <td className="px-3 py-2 text-[var(--tgia-color-text)] font-medium">{selectedSpeciesObj.commonName}</td>
                              </tr>
                              <tr>
                                <td className="bg-[var(--tgia-color-surface-active)] px-3 py-2 font-semibold text-[var(--tgia-color-brand-text)]">學名</td>
                                <td className="px-3 py-2 text-[var(--tgia-color-text)] italic">{selectedSpeciesObj.scientificName}</td>
                              </tr>
                              <tr>
                                <td className="bg-[var(--tgia-color-surface-active)] px-3 py-2 font-semibold text-[var(--tgia-color-brand-text)]">參考基因組</td>
                                <td className="px-3 py-2 text-[var(--tgia-color-text)]">{selectedSpeciesObj.genome}</td>
                              </tr>
                              {(speciesMode === 'scrna' || speciesMode === 'visiumhd') && (
                                <>
                                  <tr>
                                    <td className="bg-[var(--tgia-color-surface-active)] px-3 py-2 text-xs font-semibold text-[var(--tgia-color-brand-text)]">10X Genomics Ref Version</td>
                                    <td className="px-3 py-2 text-[var(--tgia-color-text-muted)] text-xs">{selectedSpeciesObj['TenXGenomicsRefVersion'] || ''}</td>
                                  </tr>
                                  <tr>
                                    <td className="bg-[var(--tgia-color-surface-active)] px-3 py-2 text-xs font-semibold text-[var(--tgia-color-brand-text)]">10X Genomics Probe Set Version</td>
                                    <td className="px-3 py-2 text-[var(--tgia-color-text-muted)] text-xs">{selectedSpeciesObj['TenXGenomicsProbeSetVersion'] || ''}</td>
                                  </tr>
                                  {speciesMode === 'scrna' && (
                                    <tr>
                                      <td className="bg-[var(--tgia-color-surface-active)] px-3 py-2 text-xs font-semibold text-[var(--tgia-color-brand-text)]">10X Genomics VDJ Version</td>
                                      <td className="px-3 py-2 text-[var(--tgia-color-text-muted)] text-xs">{selectedSpeciesObj['TenXGenomicsVDJVersion'] || ''}</td>
                                    </tr>
                                  )}
                                </>
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  );
                }

                const isSingleron = formData.serviceItems?.some(item =>
                  item.services?.some(svc => isSingleronService(svc.service))
                );

                return (
                  <select
                    name="speciesSelection"
                    value={formData.speciesSelection}
                    onChange={handleInputChange}
                    disabled={!!apPackageInfo?.binding?.speciesCommon}
                    className={`tgia-species-select w-full min-h-[50px] rounded-xl border border-[var(--tgia-color-control-border)] px-4 py-3 text-base leading-6 focus:outline-none focus:ring-2 focus:ring-[var(--tgia-color-focus)] ${apPackageInfo?.binding?.speciesCommon
                      ? 'cursor-not-allowed bg-[var(--tgia-color-surface-muted)] font-semibold text-[var(--tgia-color-disabled-text)]'
                      : ''
                      }`}
                  >
                    <option>物種請選擇</option>
                    <option>Human</option>
                    <option>Mouse</option>
                    {(!isSingleron || speciesMode !== 'scrna') && <option>Rat</option>}
                    <option>其他</option>
                  </select>
                );
              })()}
              {apPackageInfo?.binding?.speciesCommon && (
                <p className="mt-1 text-xs text-[var(--tgia-color-caution-text-muted)]">
                  💡 此套組指定物種為：{apPackageInfo.binding.speciesCommon}
                </p>
              )}
              {speciesMode === 'chipseq' && (
                <p className="mt-1 text-xs text-[var(--tgia-color-caution-text-muted)]">
                  💡 {CHIPSEQ_SPECIES_NOTICE}
                </p>
              )}
              {analysisFlowInfo && renderAnalysisFlowBlock(analysisFlowInfo)}
              {formData.speciesSelection === '其他' && !apPackageInfo?.binding?.speciesCommon && canSelectOtherSpecies(speciesMode, formData.serviceItems, formData) && (() => {
                if (isDetailedSpeciesMode(speciesMode)) {
                  return (
                    <div className="mt-3 space-y-3 rounded-lg border border-[var(--tgia-color-border)] bg-[var(--tgia-color-surface-subtle)] p-4">
                      <p className="text-sm font-semibold text-[var(--tgia-color-brand-text)]">🎨 自定義物種詳細資訊</p>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-[var(--tgia-color-text-subtle)] mb-1">俗名 (Common Name) <span className="text-[var(--tgia-color-accent)]">*</span></label>
                          <input
                            type="text"
                            name="speciesCommon"
                            value={formData.speciesCommon}
                            onChange={handleInputChange}
                            className={compactInputToneClasses.text}
                            placeholder="如：Zebrafish"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-[var(--tgia-color-text-subtle)] mb-1">學名 (Scientific Name) <span className="text-[var(--tgia-color-accent)]">*</span></label>
                          <input
                            type="text"
                            name="speciesScientific"
                            value={formData.speciesScientific}
                            onChange={handleInputChange}
                            className={`${compactInputToneClasses.text} italic`}
                            placeholder="如：Danio rerio"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-[var(--tgia-color-text-subtle)] mb-1">參考基因組 (Genome) <span className="text-[var(--tgia-color-placeholder)] font-normal ml-1">(Optional)</span></label>
                        <input
                          type="text"
                          name="speciesGenome"
                          value={formData.speciesGenome}
                          onChange={handleInputChange}
                          className={compactInputToneClasses.text}
                          placeholder="如：GRCz11"
                        />
                      </div>

                      {(speciesMode === 'scrna' || speciesMode === 'visiumhd') && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-[var(--tgia-color-text-subtle)] mb-1">10X Genomics Ref Version <span className="text-[var(--tgia-color-placeholder)] font-normal ml-1">(Optional)</span></label>
                            <input
                              type="text"
                              name="TenXGenomicsRefVersion"
                              value={formData.TenXGenomicsRefVersion}
                              onChange={handleInputChange}
                              className={compactInputToneClasses.text}
                              placeholder="如：2024-A"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-[var(--tgia-color-text-subtle)] mb-1">10X Genomics Probe Set Version <span className="text-[var(--tgia-color-placeholder)] font-normal ml-1">(Optional)</span></label>
                            <input
                              type="text"
                              name="TenXGenomicsProbeSetVersion"
                              value={formData.TenXGenomicsProbeSetVersion}
                              onChange={handleInputChange}
                              className={compactInputToneClasses.text}
                              placeholder="如：v1.1"
                            />
                          </div>
                          {speciesMode === 'scrna' && (
                            <div>
                              <label className="block text-xs font-medium text-[var(--tgia-color-text-subtle)] mb-1">10X Genomics VDJ Version <span className="text-[var(--tgia-color-placeholder)] font-normal ml-1">(Optional)</span></label>
                              <input
                                type="text"
                                name="TenXGenomicsVDJVersion"
                                value={formData.TenXGenomicsVDJVersion}
                                onChange={handleInputChange}
                                className={compactInputToneClasses.text}
                                placeholder="如：v7.1.0"
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                }

                // 🔸 非 RNA-seq 服務，維持原本單一輸入框
                return (
                  <input
                    type="text"
                    name="speciesOther"
                    value={formData.speciesOther}
                    onChange={handleInputChange}
                    className={`${compactInputToneClasses.text} mt-2`}
                    placeholder="請輸入物種名稱（例：Zebrafish、Pig）"
                  />
                );
              })()}
            </div>

            {/* 🆕 分析參數：品項決定的額外分析設定，版面比照物種，一個參數一個欄位，直接接在物種下面；
                數字閾值大多不用改，收在預設收合的「進階閾值」裡，免得業務被一堆參數嚇到。 */}
            {(() => {
              const parameterDefinitions = getAnalysisParameterDefinitionsForOrder();
              if (parameterDefinitions.length === 0) return null;

              const parameterValues = getResolvedAnalysisParameters();
              const parameterErrors = fieldErrors.analysisParameters || {};
              const suggestionWarnings = getAnalysisParameterSuggestionWarnings(parameterDefinitions, parameterValues);
              const basicDefinitions = parameterDefinitions.filter(definition => !definition.advanced);
              const advancedDefinitions = parameterDefinitions.filter(definition => definition.advanced);
              const thresholdsOpen = isAnalysisThresholdsOpen
                || advancedDefinitions.some(definition => parameterErrors[definition.id]);

              // 使用者親手改值才會套用建議（例如換抗體 → 自動帶 peak calling mode）；
              // 還原舊單 / 草稿不會走這裡，所以不會蓋掉當初存的設定。
              const updateParameter = (id, value, options) => {
                setFormData(prev => ({
                  ...prev,
                  analysisParameters: applyAnalysisParameterChange(
                    getAnalysisParameterDefinitionsForOrder(prev),
                    getResolvedAnalysisParameters(prev),
                    id,
                    value,
                    options
                  )
                }));
                setFieldErrors(prev => {
                  if (!prev.analysisParameters?.[id]) return prev;
                  const nextParameterErrors = { ...prev.analysisParameters };
                  delete nextParameterErrors[id];
                  return { ...prev, analysisParameters: nextParameterErrors };
                });
              };

              const renderParameterError = (definition) => (
                parameterErrors[definition.id] && (
                  <p className="mt-1 text-xs text-[var(--tgia-color-accent)]">{parameterErrors[definition.id]}</p>
                )
              );

              const renderSelectParameter = (definition) => {
                const value = parameterValues[definition.id];
                const selectValue = getAnalysisParameterSelectValue(definition, value, {
                  customMode: Boolean(analysisParameterCustomModes[definition.id])
                });
                const isCustom = selectValue === ANALYSIS_PARAMETER_CUSTOM_OPTION_VALUE;
                const selectedOption = definition.options.find(option => option.value === selectValue);
                const optionGroups = definition.options.reduce((groups, option) => {
                  const groupLabel = option.group || '';
                  const group = groups.find(item => item.label === groupLabel);
                  if (group) {
                    group.options.push(option);
                  } else {
                    groups.push({ label: groupLabel, options: [option] });
                  }
                  return groups;
                }, []);
                const renderOption = option => (
                  <option key={option.value} value={option.value}>
                    {definition.describeInOption && option.description
                      ? `${option.label} — ${option.description}`
                      : option.label}
                  </option>
                );

                return (
                  <div key={definition.id}>
                    <label className="block text-sm font-medium text-[var(--tgia-color-text)] mb-1">
                      {definition.label} <span className="text-[var(--tgia-color-accent)]">*</span>
                    </label>
                    <select
                      value={selectValue}
                      onChange={(e) => {
                        const nextValue = e.target.value;
                        if (definition.allowCustom) {
                          setAnalysisParameterCustomModes(prev => ({
                            ...prev,
                            [definition.id]: nextValue === ANALYSIS_PARAMETER_CUSTOM_OPTION_VALUE
                          }));
                        }
                        updateParameter(
                          definition.id,
                          nextValue === ANALYSIS_PARAMETER_CUSTOM_OPTION_VALUE ? '' : nextValue
                        );
                      }}
                      className={`${inputToneClasses.select} ${parameterErrors[definition.id] && !isCustom ? 'tgia-field-error ' : ''}`}
                    >
                      {definition.required && <option value="">請選擇</option>}
                      {optionGroups.map(group => (
                        group.label
                          ? <optgroup key={group.label} label={group.label}>{group.options.map(renderOption)}</optgroup>
                          : group.options.map(renderOption)
                      ))}
                      {definition.allowCustom && (
                        <option value={ANALYSIS_PARAMETER_CUSTOM_OPTION_VALUE}>{definition.customOptionLabel}</option>
                      )}
                    </select>
                    {isCustom && (
                      <input
                        type="text"
                        value={value || ''}
                        maxLength={definition.maxLength}
                        onChange={(e) => updateParameter(definition.id, e.target.value, { applySuggestion: false })}
                        className={`${compactInputToneClasses.text} mt-2 ${parameterErrors[definition.id] ? 'tgia-field-error ' : ''}`}
                        placeholder={definition.customPlaceholder}
                        aria-label={`${definition.label}（自填）`}
                      />
                    )}
                    {renderParameterError(definition)}
                    {definition.hint && (
                      <p className="text-xs text-[var(--tgia-color-text-subtle)] mt-1">
                        💡 {definition.hint}
                      </p>
                    )}
                    {isCustom && definition.customHint && (
                      <p className="text-xs text-[var(--tgia-color-text-subtle)] mt-1">
                        💡 {definition.customHint}
                      </p>
                    )}
                    {!definition.describeInOption && selectedOption?.description && (
                      <p className="text-xs text-[var(--tgia-color-text-subtle)] mt-1">
                        💡 {selectedOption.label}：{selectedOption.description}
                      </p>
                    )}
                    {suggestionWarnings[definition.id] && (
                      <p className="text-xs font-medium text-[var(--tgia-color-caution-text)] mt-1">
                        ⚠️ {suggestionWarnings[definition.id]}
                      </p>
                    )}
                  </div>
                );
              };

              const renderNumberParameter = (definition) => (
                <div key={definition.id}>
                  <label className="block text-xs font-medium text-[var(--tgia-color-text-subtle)] mb-1">
                    {definition.label} <span className="text-[var(--tgia-color-accent)]">*</span>
                    <span className="ml-2 font-normal text-[var(--tgia-color-placeholder)]">預設 {definition.defaultValue}</span>
                  </label>
                  <input
                    type="number"
                    inputMode="decimal"
                    step={definition.step || 'any'}
                    min={definition.min}
                    max={definition.max}
                    value={parameterValues[definition.id]}
                    onChange={(e) => updateParameter(definition.id, e.target.value)}
                    onWheel={preventNumberInputScroll}
                    className={`${compactInputToneClasses.text} ${parameterErrors[definition.id] ? 'tgia-field-error ' : ''}`}
                  />
                  {renderParameterError(definition)}
                  {definition.hint && (
                    <p className="text-xs text-[var(--tgia-color-text-subtle)] mt-1">
                      💡 {definition.hint}
                    </p>
                  )}
                </div>
              );

              const renderParameter = definition => (
                definition.type === 'number' ? renderNumberParameter(definition) : renderSelectParameter(definition)
              );

              return (
                <>
                  {basicDefinitions.map(renderParameter)}
                  {advancedDefinitions.length > 0 && (
                    <div className="rounded-lg border border-[var(--tgia-color-border)] bg-[var(--tgia-color-surface-subtle)]">
                      <button
                        type="button"
                        onClick={() => setIsAnalysisThresholdsOpen(!thresholdsOpen)}
                        className="flex w-full items-center justify-between px-3 py-2 text-left text-sm font-medium text-[var(--tgia-color-text)]"
                        aria-expanded={thresholdsOpen}
                      >
                        <span>
                          {thresholdsOpen ? '▾' : '▸'} 進階閾值（通常不用改）
                          {!thresholdsOpen && (
                            <span className="ml-2 text-xs font-normal text-[var(--tgia-color-text-subtle)]">
                              {advancedDefinitions
                                .map(definition => `${definition.label} ${parameterValues[definition.id]}`)
                                .join('、')}
                            </span>
                          )}
                        </span>
                      </button>
                      {thresholdsOpen && (
                        <div className="space-y-3 border-t border-[var(--tgia-color-border)] px-3 py-3">
                          {advancedDefinitions.map(renderParameter)}
                        </div>
                      )}
                    </div>
                  )}
                </>
              );
            })()}

            {/* 寄送方式 */}
            <div>
              <label className="block text-sm font-medium text-[var(--tgia-color-text)] mb-1">
                寄送方式 <span className="text-[var(--tgia-color-accent)]">*</span>
                {apPackageInfo?.binding?.shippingMethod && typeof apPackageInfo.binding.shippingMethod === 'object' && (
                  <span className={`text-xs ml-2 ${apPackageToneClasses.accent}`}>📌 套組限制選項</span>
                )}
              </label>
              <select
                name="shippingMethod"
                value={resolvedSampleFormData.shippingMethod}
                onChange={handleInputChange}
                disabled={isGeneralNoSampleOrder}
                className={inputToneClasses.select}
              >
                {(() => {
                  // 🆕 判斷是否有限制選項
                  const hasRestriction = apPackageInfo?.binding?.shippingMethod &&
                    typeof apPackageInfo.binding.shippingMethod === 'object';

                  // 🆕 所有可能的選項
                  const allOptions = [
                    '冷凍(乾冰)',
                    '冷藏',
                    '常溫',
                    '其他'
                  ];

                  // 🆕 決定要顯示的選項
                  const optionsToShow = hasRestriction
                    ? apPackageInfo.binding.shippingMethod.allowed
                    : allOptions;

                  return optionsToShow.map(option => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ));
                })()}
              </select>
              {apPackageInfo?.binding?.shippingMethod && typeof apPackageInfo.binding.shippingMethod === 'object' && (
                <p className={`text-xs mt-1 ${apPackageToneClasses.accent}`}>
                  💡 此套組限制選項：{apPackageInfo.binding.shippingMethod.allowed.join('、')}
                </p>
              )}
              {resolvedSampleFormData.shippingMethod === '其他' && (
                <input
                  type="text"
                  name="shippingMethodOther"
                  value={resolvedSampleFormData.shippingMethodOther}
                  onChange={handleInputChange}
                  className={`${compactInputToneClasses.text} mt-2`}
                  placeholder="請說明寄送方式"
                />
              )}
            </div>
          </div>

          {/* 備註 */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-[var(--tgia-color-text)] mb-1">
              備註
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={3}
              className={inputToneClasses.textarea}
            />
          </div>

          {analysisDocumentsForStep.length > 0 && (
            <div className="mt-6">
              {renderAnalysisDocumentsPanel(analysisDocumentsForStep)}
            </div>
          )}
        </div>
      </div>
    );
  };

  // 渲染步驟4：簽名確認
  // const renderStep4 = () => (
  //   <div className="space-y-6">
  //     <div className="border-2 border-[var(--tgia-color-ap-locked-border)] rounded-lg p-4 sm:p-6 bg-[var(--tgia-color-ap-locked-surface)]">
  //       <h3 className="text-xl font-bold text-[var(--tgia-color-text)] mb-6">委託人簽名確認</h3>
  //       <div className="bg-[var(--tgia-color-surface)] rounded-lg p-4 sm:p-6 border-2 border-[var(--tgia-color-border-subtle)]">
  //         {!formData.signature ? (
  //           <div className="text-center py-8">
  //             <div className="mb-4">
  //               <Edit3 size={48} className="mx-auto text-[var(--tgia-color-upload)]" />
  //             </div>
  //             <p className="text-[var(--tgia-color-text)] mb-2 font-medium">請簽名確認訂單內容無誤</p>
  //             <p className="text-sm text-[var(--tgia-color-text-subtle)] mb-6">
  //               支援手寫簽名 ✍️ 或上傳圖片 📤
  //             </p>
  //             <button
  //               type="button"
  //               // onClick={() => setShowSignaturePad(true)}
  //               className="inline-flex items-center gap-2 px-6 py-3 bg-[var(--tgia-color-upload)] text-[var(--tgia-color-on-filled)] rounded-lg hover:bg-[var(--tgia-color-brand-hover)] transition"
  //             >
  //               <Edit3 size={20} />
  //               開始簽名
  //             </button>
  //           </div>
  //         ) : (
  //           <div className="space-y-4">
  //             <div className="flex items-center justify-between">
  //               <p className="text-[var(--tgia-color-success-text)] font-semibold flex items-center gap-2">
  //                 <Check size={24} />
  //                 已完成簽名
  //               </p>
  //               <div className="flex gap-2">
  //                 <button
  //                   type="button"
  //                   // onClick={() => setShowSignaturePad(true)}
  //                   className="px-4 py-2 text-sm border-2 border-[var(--tgia-color-upload)] text-[var(--tgia-color-upload)] rounded-lg hover:bg-[var(--tgia-color-brand-surface)]"
  //                 >
  //                   重新簽名
  //                 </button>
  //                 <button
  //                   type="button"
  //                   onClick={clearSignature}
  //                   className="px-4 py-2 text-sm text-[var(--tgia-color-accent)] border-2 border-[var(--tgia-color-error-border)] rounded-lg hover:bg-[var(--tgia-color-error-surface)]"
  //                 >
  //                   清除簽名
  //                 </button>
  //               </div>
  //             </div>
  //             <div className="border-2 border-[var(--tgia-color-border)] rounded-lg p-4">
  //               <img 
  //                 src={formData.signature} 
  //                 alt="委託人簽名" 
  //                 className="max-w-full h-auto mx-auto"
  //                 style={{ maxHeight: '150px' }}
  //               />
  //             </div>
  //           </div>
  //         )}
  //       </div>
  //     </div>
  //   </div>
  // );

  // 渲染步驟5：預覽與提交
  // 渲染步驟5：預覽與提交
  const renderStep4 = () => (
    <div className="space-y-6">
      <div className="rounded-2xl border border-[var(--tgia-color-border)] bg-[var(--tgia-color-surface)] p-4 sm:p-6 shadow-sm">
        <h3 className="mb-6 text-xl font-bold text-[var(--tgia-color-text)]">訂單預覽</h3>

        <div className="space-y-6 rounded-2xl bg-[var(--tgia-color-surface)] p-4 sm:p-6">

          {/* 1. 基本資訊 */}
          <div className="border-b border-[var(--tgia-color-border-subtle)] pb-4">
            <h4 className="mb-3 text-lg font-semibold text-[var(--tgia-color-text)]">📋 基本資訊</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div className="rounded-lg bg-[var(--tgia-color-surface-subtle)] p-2">
                <span className="text-[var(--tgia-color-text-muted)] font-medium">業務人員：</span>
                <span className="text-[var(--tgia-color-text)]">{formData.salesPerson}</span>
              </div>
              <div className="rounded-lg bg-[var(--tgia-color-surface-subtle)] p-2">
                <span className="text-[var(--tgia-color-text-muted)] font-medium">單位：</span>
                <span className="text-[var(--tgia-color-text)]">{formData.organization}</span>
              </div>
              <div className="rounded-lg bg-[var(--tgia-color-surface-subtle)] p-2">
                <span className="text-[var(--tgia-color-text-muted)] font-medium">負責人/主持人：</span>
                <span className="text-[var(--tgia-color-text)]">{formData.principalInvestigator}</span>
              </div>
              <div className="rounded-lg bg-[var(--tgia-color-surface-subtle)] p-2">
                <span className="text-[var(--tgia-color-text-muted)] font-medium">聯絡人：</span>
                <span className="text-[var(--tgia-color-text)]">{formData.contactPerson}</span>
              </div>
              <div className="rounded-lg bg-[var(--tgia-color-surface-subtle)] p-2">
                <span className="text-[var(--tgia-color-text-muted)] font-medium">聯絡電話：</span>
                <span className="text-[var(--tgia-color-text)]">{formData.contactPhone || '未填寫'}</span>
              </div>
              <div className="rounded-lg bg-[var(--tgia-color-surface-subtle)] p-2">
                <span className="text-[var(--tgia-color-text-muted)] font-medium">Email：</span>
                <span className="text-[var(--tgia-color-text)]">{formData.email}</span>
              </div>
              {formData.address && (
                <div className="sm:col-span-2 rounded-lg bg-[var(--tgia-color-surface-subtle)] p-2">
                  <span className="text-[var(--tgia-color-text-muted)] font-medium">地址：</span>
                  <span className="text-[var(--tgia-color-text)]">{formData.address}</span>
                </div>
              )}
            </div>
          </div>

          {/* 2. 發票資訊 */}
          <div className="border-b border-[var(--tgia-color-border-subtle)] pb-4">
            <h4 className="mb-3 text-lg font-semibold text-[var(--tgia-color-text)]">🧾 發票資訊</h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
              <div className="rounded-lg bg-[var(--tgia-color-surface-subtle)] p-2">
                <span className="text-[var(--tgia-color-text-muted)] font-medium">抬頭：</span>
                <span className="text-[var(--tgia-color-text)]">{formData.invoiceTitle || '未填寫'}</span>
              </div>
              <div className="rounded-lg bg-[var(--tgia-color-surface-subtle)] p-2">
                <span className="text-[var(--tgia-color-text-muted)] font-medium">統編：</span>
                <span className="text-[var(--tgia-color-text)]">{formData.taxId || '未填寫'}</span>
              </div>
              <div className="rounded-lg bg-[var(--tgia-color-surface-subtle)] p-2">
                <span className="text-[var(--tgia-color-text-muted)] font-medium">發票聯數：</span>
                <span className="text-[var(--tgia-color-text)]">{formData.invoiceCopies}</span>
              </div>
            </div>
          </div>

          {/* 3. 數據交付資訊 */}
          <div className="border-b border-[var(--tgia-color-border-subtle)] pb-4">
            <h4 className="mb-3 text-lg font-semibold text-[var(--tgia-color-text)]">💾 數據交付資訊</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div className="rounded-lg bg-[var(--tgia-color-surface-subtle)] p-2">
                <span className="text-[var(--tgia-color-text-muted)] font-medium">提供方式：</span>
                <span className="text-[var(--tgia-color-text)]">{formData.dataDeliveryMethod}</span>
              </div>
              {formData.dataDeliveryMethod === '國網中心下載' && formData.nchcAccount && (
                <div className="rounded-lg bg-[var(--tgia-color-surface-subtle)] p-2">
                  <span className="text-[var(--tgia-color-text-muted)] font-medium">國網帳號：</span>
                  <span className="text-[var(--tgia-color-text)]">{formData.nchcAccount}</span>
                </div>
              )}
              {formData.dataDeliveryMethod === 'HDD由專人遞送' && (
                <>
                  {formData.deliveryAddress && (
                    <div className="sm:col-span-2 rounded-lg bg-[var(--tgia-color-surface-subtle)] p-2">
                      <span className="text-[var(--tgia-color-text-muted)] font-medium">交貨地址：</span>
                      <span className="text-[var(--tgia-color-text)]">{formData.deliveryAddress}</span>
                    </div>
                  )}
                  <div className="rounded-lg bg-[var(--tgia-color-surface-subtle)] p-2">
                    <span className="text-[var(--tgia-color-text-muted)] font-medium">收件人：</span>
                    <span className="text-[var(--tgia-color-text)]">{formData.recipient || '未填寫'}</span>
                  </div>
                  <div className="rounded-lg bg-[var(--tgia-color-surface-subtle)] p-2">
                    <span className="text-[var(--tgia-color-text-muted)] font-medium">收件電話：</span>
                    <span className="text-[var(--tgia-color-text)]">{formData.recipientPhone || '未填寫'}</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* 4. 急件與樣品返還 */}
          <div className="border-b border-[var(--tgia-color-border-subtle)] pb-4">
            <h4 className="mb-3 text-lg font-semibold text-[var(--tgia-color-text)]">⚡ 急件與樣品返還</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div className="bg-[var(--tgia-color-surface-muted)] p-3 rounded-lg border border-[var(--tgia-color-border-subtle)]">
                <span className="text-[var(--tgia-color-text-muted)] font-medium">急件狀態：</span>
                <span className="text-[var(--tgia-color-success-text)] font-bold ml-2">正常件（急件暫停）</span>
              </div>
              <div className="bg-[var(--tgia-color-surface-muted)] p-3 rounded-lg border border-[var(--tgia-color-border-subtle)]">
                <span className="text-[var(--tgia-color-text-muted)] font-medium">樣品返還：</span>
                <span className="text-[var(--tgia-color-text)] ml-2">{formData.sampleReturn}</span>
              </div>
            </div>
          </div>

          {/* 5. 委託內容 */}
          <div className="border-b border-[var(--tgia-color-border-subtle)] pb-4">
            <h4 className="mb-3 text-lg font-semibold text-[var(--tgia-color-text)]">📦 委託內容</h4>
            <div className="space-y-3">
              {formData.serviceItems.map((item, idx) => (
                <div key={idx} className="rounded-xl border border-[var(--tgia-color-brand-border)] bg-[var(--tgia-color-surface-muted)] p-4">
                  <h5 className="mb-2 font-semibold text-[var(--tgia-color-brand-text)]">{item.category}</h5>
                  <div className="space-y-2">
                    {item.services.map((service, sIdx) => {
                      const quantityUnitLabel = getServiceQuantityUnitLabel(service.service);

                      return service.service && (
                        <div key={sIdx} className="flex items-center justify-between rounded-lg bg-[var(--tgia-color-surface)] p-2 text-sm max-sm:gap-3">
                          <span className="text-[var(--tgia-color-text)]">{service.service}</span>
                          <span className="font-semibold text-[var(--tgia-color-brand-text)] max-sm:shrink-0 max-sm:whitespace-nowrap">
                            數量：{service.quantity}{quantityUnitLabel ? ` ${quantityUnitLabel}` : ''}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {renderSampleInfoPreviewSection()}

        </div>

        <div className={`mt-6 rounded-xl border-2 p-4 ${infoToneClasses.caution_primary}`}>
          <p className="flex items-center gap-2 text-sm">
            <AlertCircle size={18} />
            請確認所有資訊無誤後再提交，提交後可下載訂購需求單。
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`${pageToneClasses.page} tgia-theme-root`} data-theme={isDarkMode ? 'dark' : 'light'}>
      {/* 🆕 全屏載入遮罩 */}
      {isSubmitting && (
        <div className="fixed inset-0 tgia-modal-backdrop flex items-center justify-center z-50">
          <div className="bg-[var(--tgia-color-surface)] rounded-2xl p-8 shadow-2xl flex flex-col items-center gap-4">
            <div className="relative">
              {/* 外圈旋轉動畫 */}
              <div className="w-20 h-20 border-4 border-[var(--tgia-color-border)] border-t-[var(--tgia-color-brand)] rounded-full animate-spin"></div>
              {/* 內圈脈動動畫 */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-12 h-12 rounded-full border-4 border-[var(--tgia-submit-spinner-core-ring)] bg-[var(--tgia-submit-spinner-core)] animate-pulse"></div>
              </div>
            </div>
            <div className="text-center">
              <p className="mb-1 text-xl font-semibold text-[var(--tgia-color-text)]">提交中...</p>
              <p className="text-sm text-[var(--tgia-color-text-muted)]">請稍候，正在處理您的需求單</p>
            </div>
          </div>
        </div>
      )}

      <div className={pageToneClasses.shell}>
        <button
          type="button"
          className="tgia-theme-toggle tgia-shell-theme-toggle"
          aria-pressed={isDarkMode}
          onClick={() => setIsDarkMode(prev => !prev)}
          title="切換深色模式"
        >
          <span className="tgia-theme-toggle__label">{isDarkMode ? '深色模式' : '淺色模式'}</span>
          <span className="tgia-theme-toggle__track" aria-hidden="true">
            <span className="tgia-theme-toggle__thumb" />
          </span>
        </button>

        {/* 標題 */}
        <div className="mb-6 border-b border-[var(--tgia-color-border-subtle)] pb-5 sm:mb-8 sm:pb-6">
          <div className="grid items-center gap-3 grid-cols-1 sm:gap-4 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
            <div className="flex items-center justify-self-center sm:justify-self-start">
              <button
                type="button"
                onClick={handleCreateNewOrder}
                className="rounded-md transition hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--tgia-color-focus)] focus:ring-offset-2"
                title="重新建立訂單，回到登入頁面"
                aria-label="重新建立訂單，回到登入頁面"
              >
                <img
                  src={isDarkMode ? tgiaLogo_dark : tgiaLogo}
                  alt="TGIA logo"
                  className="h-auto w-44 object-contain sm:w-60"
                />
              </button>
            </div>
            <div className="justify-self-center text-center">
              <h1 className={pageToneClasses.title + ' mb-2'}>
                {APP_TITLE}
              </h1>
              <h2 className={pageToneClasses.subtitle + ' mb-2'}>
                委託服務同意書/訂購確認單
              </h2>
              <p className={pageToneClasses.requiredNote}>標 * 為必填之欄位</p>
            </div>
            <div className="hidden h-8 w-[80px] justify-self-end sm:block sm:h-12 sm:w-[116px]" aria-hidden="true" />
          </div>
        </div>

        {/* 步驟進度條 */}
        <StepIndicator currentStep={currentStep} steps={steps} isLocked={isLocked} />

        {/* 表單內容 */}
        <div className="min-h-[500px]">
          {currentStep === 0 && renderStep0()}
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
          {/* {currentStep === 5 && renderStep5()} */}
        </div>

        {/* 導航按鈕 */}
        {currentStep > 0 && (
          <ActionBar
            className="mt-8 pt-6"
            left={(
              <button
                onClick={() => {
                  if (isLocked) {
                    setMessage('⚠️ 此訂單已提交並鎖定，無法再切換步驟');
                    return;
                  }
                  prevStep();
                }}
                disabled={currentStep === 0 || isLocked || isSavingDraft || isSubmitting}
                className={buttonToneClasses.primary}
              >
                <ChevronLeft size={20} />
                上一步
              </button>
            )}
            center={(
              !isLocked ? (
                <button
                  onClick={handleSaveDraft}
                  disabled={isSavingDraft || isSubmitting}
                  className={`${buttonToneClasses.neutral} whitespace-nowrap`}
                >
                  <Save size={18} />
                  {isSavingDraft ? '儲存中...' : '儲存草稿'}
                </button>
              ) : null
            )}
            right={(
              currentStep < steps.length - 1 ? (
                <button
                  onClick={nextStep}
                  disabled={isSavingDraft || isSubmitting}
                  className={buttonToneClasses.primary}
                >
                  下一步
                  <ChevronRight size={20} />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={isLocked || isSavingDraft || isSubmitting}
                  className={buttonToneClasses.primary}
                >
                  <Send size={20} />
                  {(editingOrderSource === 'draft' || draftOrderCode) ? '提交為正式訂單' : '提交需求'}
                </button>
              )
            )}
          />
        )}

        {/* 訊息提示 */}
        {message && (
          <div className={`mt-4 flex items-center gap-2 rounded-xl border px-4 py-4 ${isWarningMessage(message)
            ? infoToneClasses.error
            : infoToneClasses.success
            }`}>
            <AlertCircle size={20} />
            <span>{message}</span>
          </div>
        )}

        {/* 下載按鈕與重新建立訂單按鈕（提交後顯示） */}
        {exportReady && currentStep === steps.length - 1 && (
          <div className="tgia-completion-download-panel mt-8">
            <SectionCard
              title="完成與下載"
              subtitle="訂單已完成，請立即下載訂購單或重新建立下一筆訂單。"
              tone="brand"
              bodyClassName="space-y-4"
            >
              <div className="flex flex-col gap-3 sm:flex-row">
                <button
                  onClick={exportToExcel}
                  disabled={isExporting}
                  className={`${buttonToneClasses.primary} w-full sm:flex-1`}
                >
                  <Download size={20} />
                  {isExporting ? '下載中...' : '下載訂購單'}
                </button>

                <button
                  onClick={handleCreateNewOrder}
                  className={`${buttonToneClasses.neutral} w-full sm:w-auto`}
                >
                  <Plus size={20} />
                  重新建立訂單
                </button>

                <button
                  onClick={handleCreateSimilarOrder}
                  className={`${buttonToneClasses.neutral} w-full sm:w-auto`}
                >
                  <CopyPlus size={20} />
                  保留內容建立新訂單
                </button>
              </div>

              {/* 🆕 分析需求單下載按鈕（只在需要時顯示） */}
              {needsAnalysisRequest && (
                <div className="rounded-xl border border-[var(--tgia-color-border)] bg-[var(--tgia-color-surface-muted)] p-4">
                  <div className="mb-3">
                    <h3 className="text-base font-semibold text-[var(--tgia-color-text)]">分析需求單</h3>
                    <p className="text-sm text-[var(--tgia-color-text-muted)]">您的訂單包含分析服務，請下載並填寫分析需求單。</p>
                  </div>
                  <button
                    onClick={downloadAnalysisRequest}
                    disabled={isExporting}
                    className={`${buttonToneClasses.download_primary} w-full`}
                  >
                    <Download size={20} />
                    {isExporting ? '下載中...' : '下載分析需求單'}
                  </button>
                  <p className="text-center text-xs text-[var(--tgia-color-caution-text-muted)]">
                    填寫完成後請回傳給您的業務代表
                  </p>
                </div>
              )}

            </SectionCard>
          </div>
        )}
      </div>

      <footer className="mt-10 text-center text-[var(--tgia-color-footer-text)] text-base py-6 border-t">
        <p>Email: salessupport@tgiainc.com｜TEL: 02-2651-2495 | FAX: 02-2651-7187</p>
        <p>地址: 10656 台北市大安區仁愛路三段 125 號 12 樓 115 | 30261 新竹縣竹北市生醫五路 66 號 8 樓之 6</p>
        <p>Address: 10656 11F., No. 125, Sec. 3, Ren'ai Rd., Da’an Dist., Taipei | 30261 8F.-6, No. 66, Shengyi 5th Rd., Zhubei City, Hsinchu</p>
        <p className="text-xs text-[var(--tgia-color-footer-text)] mt-2">© 2026 TGIA Inc. All rights reserved. v3.15.3</p>
        <div className="mt-6 flex justify-center">
          <img
            src={isDarkMode ? tgiaLogoFull_dark : tgiaLogoFull}
            alt="TGIA logo full"
            className="h-12 w-auto object-contain"
          />
        </div>
      </footer>
      {/* Kit 選擇弹窗 */}
      <KitReferenceModal
        isOpen={kitModalOpen}
        onClose={() => setKitModalOpen(false)}
        onSelect={handleKitSelect}
        onApplyToAll={handleKitApplyToAll}
        type={kitModalConfig.type}
      />
    </div>
  );
};

export default TGIAOrderForm;
